import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { adminAPI, bookingAPI, uploadAPI, tutoringRequestAPI, notificationsAPI, teamAPI, cmsAPI, api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import AvailabilitySelector from '../components/AvailabilitySelector';
import CMSContentManager from '../components/CMSContentManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to resolve team profile image URLs (handles relative /api/... paths)
const getTeamImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http')) return imageUrl;
  if (imageUrl.startsWith('/')) return `${API_URL}${imageUrl}`;
  return imageUrl;
};

// ===================== NOTIFICATION SOUND UTILITY =====================
// Creates a simple notification beep using Web Audio API (no external file needed)
const createNotificationSound = () => {
  let audioContext = null;
  let isEnabled = false;
  
  const enable = () => {
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    isEnabled = true;
  };
  
  const play = (type = 'message') => {
    if (!isEnabled || !audioContext) return;
    
    try {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Different tones for different notification types
      if (type === 'message') {
        oscillator.frequency.value = 880; // A5 note for messages
        oscillator.type = 'sine';
      } else if (type === 'payment') {
        oscillator.frequency.value = 660; // E5 note for payments
        oscillator.type = 'triangle';
      }
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Could not play notification sound:', e);
    }
  };
  
  return { enable, play };
};

const notificationSound = createNotificationSound();
import { 
  GraduationCap, 
  Users,
  DollarSign,
  Calendar,
  Loader2,
  LogOut,
  Shield,
  CheckCircle,
  XCircle,
  TrendingUp,
  Mail,
  Lock,
  UserCheck,
  Clock,
  AlertTriangle,
  MessageCircle,
  Send,
  Eye,
  EyeOff,
  School,
  BookOpen,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  User,
  UserRound,
  MapPin,
  ExternalLink,
  Copy,
  Upload,
  CreditCard,
  Bell,
  FileText,
  Target,
  UserPlus,
  Search,
  UsersRound,
  GripVertical,
  Image,
  Link as LinkIcon,
  RefreshCw,
  Edit2,
  Settings,
  X,
  AlertCircle,
  History,
  MessageSquare,
  Archive,
  ArchiveRestore,
  Wrench,
  ShieldAlert,
  Database,
  Receipt,
  Undo2
} from 'lucide-react';

// Admin Login Component
const AdminLogin = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.role !== 'admin') {
        toast.error('Access denied. Admin credentials required.');
        return;
      }
      toast.success('Welcome, Administrator!');
      onLoginSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <GraduationCap className="h-10 w-10 text-indigo-400" />
            <span className="font-heading text-2xl font-semibold text-white">
              Gifted Academy Tutors
            </span>
          </Link>
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="h-8 w-8 text-red-400" />
          </div>
          <h1 className="font-heading text-3xl font-semibold text-white">Admin Portal</h1>
          <p className="text-slate-400 mt-2">Sign in to access the admin dashboard</p>
        </div>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Admin Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@giftedacademytutors.ca"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                    required
                    data-testid="admin-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                    required
                    data-testid="admin-password-input"
                  />
                </div>
                <div className="text-right">
                  <Link 
                    to="/forgot-password" 
                    className="text-xs text-indigo-400 hover:text-indigo-300"
                    data-testid="admin-forgot-password-link"
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <Shield className="mr-2 h-4 w-4" />
                    Access Admin Dashboard
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-700 text-center">
              <Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors">
                ← Back to Homepage
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          Protected area. Unauthorized access is prohibited.
        </p>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [businessMetrics, setBusinessMetrics] = useState(null);
  const [pendingTutors, setPendingTutors] = useState([]);
  const [pendingSessions, setPendingSessions] = useState([]);
  const [approvingSessionId, setApprovingSessionId] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDashboard, setShowDashboard] = useState(false);
  
  // Live Chat state - now using conversations
  const [chatConversations, setChatConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  
  // Student expanded view state
  const [expandedStudents, setExpandedStudents] = useState({});
  const [studentBookings, setStudentBookings] = useState({});
  
  // Lesson History state (per student)
  const [lessonHistoryExpanded, setLessonHistoryExpanded] = useState({});
  const [lessonHistoryData, setLessonHistoryData] = useState({});
  const [loadingLessonHistory, setLoadingLessonHistory] = useState({});

  // Student Hours Management state
  const [hoursModalOpen, setHoursModalOpen] = useState(false);
  const [selectedStudentForHours, setSelectedStudentForHours] = useState(null);
  const [studentHoursData, setStudentHoursData] = useState(null);
  const [loadingHoursData, setLoadingHoursData] = useState(false);
  const [editingPurchasedHours, setEditingPurchasedHours] = useState(false);
  const [newPurchasedHours, setNewPurchasedHours] = useState('');
  const [editingSessionLog, setEditingSessionLog] = useState(null);
  const [sessionLogForm, setSessionLogForm] = useState({ 
    tutor_name: '', 
    session_date: '', 
    session_time: '',
    hours_used: '', 
    notes: '' 
  });

  // Tutor management state
  const [tutors, setTutors] = useState([]);
  const [tutorStats, setTutorStats] = useState({ total: 0, pending: 0, approved: 0 });
  const [tutorFilter, setTutorFilter] = useState('all');
  const [showTutorDialog, setShowTutorDialog] = useState(false);
  const [editingTutor, setEditingTutor] = useState(null);
  
  // Tutor Hours Overview state
  const [tutorHoursOverview, setTutorHoursOverview] = useState([]);
  const [loadingTutorHours, setLoadingTutorHours] = useState(false);
  const [expandedTutors, setExpandedTutors] = useState({});
  const [expandedTutorStudents, setExpandedTutorStudents] = useState({});
  
  // Tutor Payroll state
  const [tutorPayroll, setTutorPayroll] = useState({ tutors: [], summary: {} });
  const [loadingPayroll, setLoadingPayroll] = useState(false);
  const [payrollPeriod, setPayrollPeriod] = useState('all');
  const [payrollCustomStart, setPayrollCustomStart] = useState('');
  const [payrollCustomEnd, setPayrollCustomEnd] = useState('');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [expandedPayrollTutors, setExpandedPayrollTutors] = useState({});
  const [showSessionEditModal, setShowSessionEditModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [sessionEditForm, setSessionEditForm] = useState({ tutor_rate_used: '', payment_notes: '' });
  const [savingSessionPayroll, setSavingSessionPayroll] = useState(false);
  const [runningBackfill, setRunningBackfill] = useState(false);
  
  // Tutor Invoice state
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [previousPeriod, setPreviousPeriod] = useState(null);
  const [showGenerateInvoiceModal, setShowGenerateInvoiceModal] = useState(false);
  const [generateInvoiceForm, setGenerateInvoiceForm] = useState({ tutor_id: '', period_start: '', period_end: '' });
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [sendingInvoiceEmail, setSendingInvoiceEmail] = useState(null);
  const [runningInvoiceBatch, setRunningInvoiceBatch] = useState(false);
  const [schedulerRuns, setSchedulerRuns] = useState([]);
  const [showSchedulerHistory, setShowSchedulerHistory] = useState(false);
  const [eligibleTutors, setEligibleTutors] = useState([]);
  const [ineligibleReasons, setIneligibleReasons] = useState([]);
  const [loadingEligibleTutors, setLoadingEligibleTutors] = useState(false);
  const [batchMonth, setBatchMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [batchYear, setBatchYear] = useState(new Date().getFullYear());
  
  // Tutor Assignment state
  const [approvedTutors, setApprovedTutors] = useState([]);
  const [showAssignTutorModal, setShowAssignTutorModal] = useState(false);
  const [studentForTutorAssignment, setStudentForTutorAssignment] = useState(null);
  const [selectedTutorForAssignment, setSelectedTutorForAssignment] = useState('');
  const [assigningTutor, setAssigningTutor] = useState(false);
  
  // Tutor Unassignment confirmation state
  const [showUnassignConfirmModal, setShowUnassignConfirmModal] = useState(false);
  const [studentForUnassignment, setStudentForUnassignment] = useState(null);
  const [unassigningTutor, setUnassigningTutor] = useState(false);
  
  // User Archive state
  const [showArchivedUsers, setShowArchivedUsers] = useState(false);
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [showArchiveConfirmModal, setShowArchiveConfirmModal] = useState(false);
  const [userToArchive, setUserToArchive] = useState(null);
  const [archivingUser, setArchivingUser] = useState(false);
  const [restoringUser, setRestoringUser] = useState(false);
  
  // Tutor Review Modal state
  const [showTutorReviewModal, setShowTutorReviewModal] = useState(false);
  const [selectedTutorForReview, setSelectedTutorForReview] = useState(null);
  const [tutorReviewData, setTutorReviewData] = useState(null);
  const [loadingTutorReview, setLoadingTutorReview] = useState(false);
  const [newAdminNote, setNewAdminNote] = useState('');
  const [adminNoteType, setAdminNoteType] = useState('general');
  const [reviewingDocument, setReviewingDocument] = useState(null);
  const [documentReviewStatus, setDocumentReviewStatus] = useState('');
  const [documentReviewNotes, setDocumentReviewNotes] = useState('');
  
  const [tutorForm, setTutorForm] = useState({
    name: '',
    email: '',
    headline: '',
    subjects: [],
    grade_levels: [],
    university_courses: [],
    bio: '',
    qualifications: '',
    experience_years: 0,
    tutoring_mode: 'online',
    city: '',
    availability_summary: '',
    availability: {},
    languages: ['English'],
    profile_photo_url: '',
    is_approved: true,
    is_visible: true
  });
  const [newSubject, setNewSubject] = useState('');
  const [savingTutor, setSavingTutor] = useState(false);
  const [uploadingTutorPhoto, setUploadingTutorPhoto] = useState(false);
  const [tutorPhotoPreview, setTutorPhotoPreview] = useState('');
  const [viewingTutor, setViewingTutor] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);

  // Tutor Approved Rate Management state
  const [tutorRates, setTutorRates] = useState(null);
  const [editingRates, setEditingRates] = useState(false);
  const [rateFormData, setRateFormData] = useState({
    approved_rate_high_school: '',
    approved_rate_university: '',
    effective_date: ''
  });
  const [savingRates, setSavingRates] = useState(false);

  // Payment management state
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState({ total_paid: 0, total_pending: 0, total_revenue: 0, under_18_payments: 0 });
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [viewingPayment, setViewingPayment] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  // E-Transfer Orders state
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState({ total_paid: 0, total_pending: 0, total_awaiting_verification: 0, total_revenue: 0 });
  const [orderFilter, setOrderFilter] = useState('all');
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Tutoring Requests state
  const [tutoringRequests, setTutoringRequests] = useState([]);
  const [requestStats, setRequestStats] = useState({ pending: 0, matched: 0, in_progress: 0 });
  const [requestFilter, setRequestFilter] = useState('all');
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [showRequestDialog, setShowRequestDialog] = useState(false);

  // Admin Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  // Team Members state
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [teamForm, setTeamForm] = useState({
    full_name: '',
    title: '',
    bio: '',
    profile_image: '',
    display_order: 0,
    is_visible: true
  });
  const [savingTeamMember, setSavingTeamMember] = useState(false);
  const [uploadingTeamPhoto, setUploadingTeamPhoto] = useState(false);

  // Email Center state
  const [emailCenterForm, setEmailCenterForm] = useState({
    audience: '',
    emailType: '',
    recipientScope: 'all_active',
    selectedRecipients: [],
    subject: '',
    body: ''
  });
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loadingEmailHistory, setLoadingEmailHistory] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  // Search states for each tab
  const [searchTutors, setSearchTutors] = useState('');
  const [searchChat, setSearchChat] = useState('');
  const [searchStudents, setSearchStudents] = useState('');
  const [searchUsers, setSearchUsers] = useState('');
  const [searchBookings, setSearchBookings] = useState('');
  const [searchOrders, setSearchOrders] = useState('');
  const [searchRequests, setSearchRequests] = useState('');

  // ===================== MAINTENANCE / DATA RESET STATE =====================
  const [maintenanceStudents, setMaintenanceStudents] = useState([]);
  const [loadingMaintenanceStudents, setLoadingMaintenanceStudents] = useState(false);
  const [selectedResetIds, setSelectedResetIds] = useState([]);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [executingReset, setExecutingReset] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [searchMaintenance, setSearchMaintenance] = useState('');

  // ===================== FINANCIAL RECONCILIATION STATE =====================
  const [financialAudit, setFinancialAudit] = useState(null);
  const [loadingFinancialAudit, setLoadingFinancialAudit] = useState(false);
  const [financialDryRun, setFinancialDryRun] = useState(null);
  const [financialResetResult, setFinancialResetResult] = useState(null);
  const [executingFinancialReset, setExecutingFinancialReset] = useState(false);
  const [selectedFinancialResetIds, setSelectedFinancialResetIds] = useState([]);
  const [maintenanceTab, setMaintenanceTab] = useState('operational'); // 'operational' or 'financial'

  // ===================== REAL-TIME POLLING STATE =====================
  // Track seen items to prevent duplicate sound notifications
  const seenMessageIds = useRef(new Set());
  const seenNotificationIds = useRef(new Set());
  const seenOrderIds = useRef(new Set());
  const initialLoadComplete = useRef(false);
  const pollingActive = useRef(false);
  const soundEnabled = useRef(false);
  
  // Auto-refresh refs to prevent overlapping requests
  const isRefreshingStats = useRef(false);
  const isRefreshingLists = useRef(false);
  const lastStatsRefresh = useRef(Date.now());
  const lastListsRefresh = useRef(Date.now());

  // Enable sound on first user interaction (browser autoplay policy)
  const enableNotificationSound = useCallback(() => {
    if (!soundEnabled.current) {
      notificationSound.enable();
      soundEnabled.current = true;
    }
  }, []);
  // Grade options for display
  const gradeOptions = {
    'grade-9': 'Grade 9',
    'grade-10': 'Grade 10',
    'grade-11': 'Grade 11',
    'grade-12': 'Grade 12',
    'university-1': 'University - Year 1',
    'university-2': 'University - Year 2',
    'university-3': 'University - Year 3',
    'university-4': 'University - Year 4',
    'graduate': 'Graduate Student',
    'other': 'Other'
  };

  const getGradeLabel = (value) => gradeOptions[value] || value || 'Not set';

  const toggleStudentExpanded = (studentId) => {
    setExpandedStudents(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  // Get upcoming bookings for a specific student
  const getStudentUpcomingBookings = (studentId) => {
    return bookings.filter(b => 
      b.student_id === studentId && 
      ['pending', 'confirmed'].includes(b.status) && 
      new Date(b.date) >= new Date()
    ).slice(0, 3);
  };

  useEffect(() => {
    // Check if user is authenticated and is admin
    if (!authLoading) {
      if (isAuthenticated && user?.role === 'admin') {
        setShowDashboard(true);
        loadData();
      } else if (isAuthenticated && user?.role !== 'admin') {
        // Logged in but not admin - redirect to appropriate dashboard
        toast.error('Access denied. Admin credentials required.');
        if (user?.role === 'tutor') {
          navigate('/tutor-dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        // Not authenticated - show login form
        setShowDashboard(false);
        setLoading(false);
      }
    }
  }, [isAuthenticated, user, authLoading, navigate]);

  const handleLoginSuccess = () => {
    setShowDashboard(true);
    loadData();
  };

  const loadData = async () => {
    try {
      const [statsRes, pendingRes, usersRes, bookingsRes, chatRes, tutorsRes, paymentsRes, ordersRes, requestsRes, notifRes, teamRes, businessRes, pendingSessionsRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getPendingTutors(),
        adminAPI.listUsers({ limit: 50 }),
        adminAPI.listBookings({ limit: 50 }),
        adminAPI.getChatMessages({ limit: 100 }),
        adminAPI.listTutors({ status: tutorFilter, limit: 50 }),
        adminAPI.listPayments({ limit: 50 }),
        adminAPI.listOrders({ limit: 50 }),
        tutoringRequestAPI.adminList({ limit: 50 }),
        notificationsAPI.list(false, 50),
        teamAPI.adminList(),
        adminAPI.getBusinessMetrics().catch(() => ({ data: null })),
        adminAPI.listSessions({ status: 'pending', limit: 50 }).catch(() => ({ data: { sessions: [] } }))
      ]);
      setStats(statsRes.data);
      setBusinessMetrics(businessRes.data);
      setPendingTutors(pendingRes.data.tutors || []);
      setPendingSessions(pendingSessionsRes.data?.sessions || []);
      setUsers(usersRes.data.users || []);
      setBookings(bookingsRes.data.bookings || []);
      setChatConversations(chatRes.data.conversations || []);
      setUnreadChatCount(chatRes.data.unread_count || 0);
      setTutors(tutorsRes.data.tutors || []);
      setTutorStats(tutorsRes.data.stats || { total: 0, pending: 0, approved: 0 });
      setPayments(paymentsRes.data.payments || []);
      setPaymentStats(paymentsRes.data.stats || { total_paid: 0, total_pending: 0, total_revenue: 0, under_18_payments: 0 });
      setOrders(ordersRes.data.orders || []);
      setOrderStats(ordersRes.data.stats || { total_paid: 0, total_pending: 0, total_revenue: 0 });
      setTutoringRequests(requestsRes.data.requests || []);
      setRequestStats(requestsRes.data.stats || { pending: 0, matched: 0, in_progress: 0 });
      setNotifications(notifRes.data.notifications || []);
      setUnreadNotificationCount(notifRes.data.unread_count || 0);
      setTeamMembers(teamRes.data.team_members || []);
      
      // On initial load, mark all existing items as seen (don't play sounds for them)
      if (!initialLoadComplete.current) {
        const conversations = chatRes.data.conversations || [];
        conversations.forEach(conv => {
          (conv.messages || []).forEach(msg => {
            seenMessageIds.current.add(msg.message_id);
          });
        });
        (notifRes.data.notifications || []).forEach(n => {
          seenNotificationIds.current.add(n.notification_id);
        });
        (ordersRes.data.orders || []).forEach(o => {
          seenOrderIds.current.add(o.order_id);
        });
        initialLoadComplete.current = true;
        pollingActive.current = true;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // ===================== REAL-TIME POLLING FUNCTIONS =====================
  
  // Poll for new chat messages (every 5 seconds)
  const pollChatMessages = useCallback(async () => {
    if (!pollingActive.current || !showDashboard) return;
    
    try {
      const chatRes = await adminAPI.getChatMessages({ limit: 100 });
      const conversations = chatRes.data.conversations || [];
      
      // Check for new messages
      let hasNewMessages = false;
      conversations.forEach(conv => {
        (conv.messages || []).forEach(msg => {
          if (!seenMessageIds.current.has(msg.message_id)) {
            seenMessageIds.current.add(msg.message_id);
            // Only play sound for messages from non-admin (user messages)
            if (msg.sender_type !== 'admin' && soundEnabled.current) {
              hasNewMessages = true;
            }
          }
        });
      });
      
      if (hasNewMessages) {
        notificationSound.play('message');
      }
      
      setChatConversations(conversations);
      setUnreadChatCount(chatRes.data.unread_count || 0);
    } catch (error) {
      console.error('Error polling chat:', error);
    }
  }, [showDashboard]);

  // Poll for notifications and orders (every 10 seconds)  
  const pollNotificationsAndOrders = useCallback(async () => {
    if (!pollingActive.current || !showDashboard) return;
    
    try {
      const [notifRes, ordersRes] = await Promise.all([
        notificationsAPI.list(false, 50),
        adminAPI.listOrders({ limit: 50 })
      ]);
      
      // Check for new notifications
      let hasNewNotifications = false;
      (notifRes.data.notifications || []).forEach(n => {
        if (!seenNotificationIds.current.has(n.notification_id)) {
          seenNotificationIds.current.add(n.notification_id);
          if (soundEnabled.current) {
            hasNewNotifications = true;
          }
        }
      });
      
      // Check for new orders or status changes
      let hasNewOrders = false;
      (ordersRes.data.orders || []).forEach(o => {
        if (!seenOrderIds.current.has(o.order_id)) {
          seenOrderIds.current.add(o.order_id);
          if (soundEnabled.current) {
            hasNewOrders = true;
          }
        }
      });
      
      if (hasNewNotifications || hasNewOrders) {
        notificationSound.play('payment');
      }
      
      setNotifications(notifRes.data.notifications || []);
      setUnreadNotificationCount(notifRes.data.unread_count || 0);
      setOrders(ordersRes.data.orders || []);
      setOrderStats(ordersRes.data.stats || { total_paid: 0, total_pending: 0, total_awaiting_verification: 0, total_revenue: 0 });
    } catch (error) {
      console.error('Error polling notifications/orders:', error);
    }
  }, [showDashboard]);

  // Set up polling intervals when dashboard is shown
  useEffect(() => {
    if (!showDashboard) return;
    
    // Chat polling: every 5 seconds
    const chatInterval = setInterval(pollChatMessages, 5000);
    
    // Notifications & orders polling: every 10 seconds
    const notifInterval = setInterval(pollNotificationsAndOrders, 10000);
    
    return () => {
      clearInterval(chatInterval);
      clearInterval(notifInterval);
    };
  }, [showDashboard, pollChatMessages, pollNotificationsAndOrders]);

  // ===================== AUTO-REFRESH FUNCTIONS =====================
  
  // Refresh summary stats (lightweight - every 15 seconds)
  const refreshStats = useCallback(async () => {
    if (!pollingActive.current || !showDashboard || isRefreshingStats.current) return;
    
    isRefreshingStats.current = true;
    try {
      const [statsRes, businessRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getBusinessMetrics().catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setBusinessMetrics(businessRes.data);
      lastStatsRefresh.current = Date.now();
    } catch (error) {
      console.error('Error refreshing stats:', error);
    } finally {
      isRefreshingStats.current = false;
    }
  }, [showDashboard]);

  // Refresh list/table data (heavier - every 30 seconds)
  const refreshLists = useCallback(async () => {
    if (!pollingActive.current || !showDashboard || isRefreshingLists.current) return;
    
    isRefreshingLists.current = true;
    try {
      const [usersRes, tutorsRes, ordersRes, requestsRes, pendingSessionsRes] = await Promise.all([
        adminAPI.listUsers({ limit: 50 }),
        adminAPI.listTutors({ status: tutorFilter, limit: 50 }),
        adminAPI.listOrders({ limit: 50 }),
        tutoringRequestAPI.adminList({ limit: 50 }),
        adminAPI.listSessions({ status: 'pending', limit: 50 }).catch(() => ({ data: { sessions: [] } }))
      ]);
      setUsers(usersRes.data.users || []);
      setTutors(tutorsRes.data.tutors || []);
      setTutorStats(tutorsRes.data.stats || { total: 0, pending: 0, approved: 0 });
      setOrders(ordersRes.data.orders || []);
      setOrderStats(ordersRes.data.stats || { total_paid: 0, total_pending: 0, total_revenue: 0 });
      setTutoringRequests(requestsRes.data.requests || []);
      setRequestStats(requestsRes.data.stats || { pending: 0, matched: 0, in_progress: 0 });
      setPendingSessions(pendingSessionsRes.data?.sessions || []);
      lastListsRefresh.current = Date.now();
    } catch (error) {
      console.error('Error refreshing lists:', error);
    } finally {
      isRefreshingLists.current = false;
    }
  }, [showDashboard, tutorFilter]);

  // Set up auto-refresh intervals
  useEffect(() => {
    if (!showDashboard) return;
    
    // Stats refresh: every 15 seconds
    const statsInterval = setInterval(refreshStats, 15000);
    
    // Lists refresh: every 30 seconds
    const listsInterval = setInterval(refreshLists, 30000);
    
    return () => {
      clearInterval(statsInterval);
      clearInterval(listsInterval);
    };
  }, [showDashboard, refreshStats, refreshLists]);

  // Load Tutor Hours Overview data
  const loadTutorHoursOverview = useCallback(async () => {
    setLoadingTutorHours(true);
    try {
      const response = await adminAPI.getTutorHoursOverview();
      setTutorHoursOverview(response.data?.tutors || []);
    } catch (error) {
      console.error('Error loading tutor hours overview:', error);
      toast.error('Failed to load tutor hours data');
    } finally {
      setLoadingTutorHours(false);
    }
  }, []);

  // Toggle tutor expansion
  const toggleTutorExpand = (tutorName) => {
    setExpandedTutors(prev => ({
      ...prev,
      [tutorName]: !prev[tutorName]
    }));
  };

  // Toggle student expansion within a tutor
  const toggleStudentExpand = (tutorName, studentName) => {
    const key = `${tutorName}-${studentName}`;
    setExpandedTutorStudents(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Load Tutor Payroll data
  const loadTutorPayroll = useCallback(async () => {
    setLoadingPayroll(true);
    try {
      const params = { period: payrollPeriod };
      if (payrollPeriod === 'custom' && payrollCustomStart && payrollCustomEnd) {
        params.start_date = payrollCustomStart;
        params.end_date = payrollCustomEnd;
      }
      const response = await adminAPI.getTutorPayroll(params);
      setTutorPayroll(response.data || { tutors: [], summary: {} });
    } catch (error) {
      console.error('Error loading tutor payroll:', error);
      toast.error('Failed to load tutor payroll data');
    } finally {
      setLoadingPayroll(false);
    }
  }, [payrollPeriod, payrollCustomStart, payrollCustomEnd]);

  // Load payment history
  const loadPaymentHistory = useCallback(async (tutorName = null) => {
    try {
      const params = tutorName ? { tutor_name: tutorName } : {};
      const response = await adminAPI.getTutorPaymentHistory(params);
      setPaymentHistory(response.data?.payments || []);
      setShowPaymentHistory(true);
    } catch (error) {
      console.error('Error loading payment history:', error);
      toast.error('Failed to load payment history');
    }
  }, []);

  // Mark tutor sessions as paid
  const handleMarkAsPaid = useCallback(async (tutor) => {
    if (!tutor.unpaid_session_ids?.length) {
      toast.error('No unpaid sessions to mark');
      return;
    }
    
    // Check for rate missing
    if (tutor.has_rate_missing) {
      toast.error('Cannot process payment: Some sessions are missing tutor rate. Please set rates first.');
      return;
    }
    
    const sessionIds = tutor.unpaid_session_ids.map(s => s.log_id);
    const confirmMsg = `Mark ${tutor.unpaid_hours}h as paid for ${tutor.tutor_name}?\n\nAmount: $${tutor.amount_owed.toFixed(2)}\nSessions: ${sessionIds.length}`;
    
    if (!window.confirm(confirmMsg)) return;
    
    setProcessingPayment(tutor.tutor_name);
    try {
      await adminAPI.createTutorPayment({
        tutor_id: tutor.tutor_id,
        tutor_name: tutor.tutor_name,
        session_ids: sessionIds,
        amount_paid: tutor.amount_owed
      });
      toast.success(`Marked ${tutor.unpaid_hours}h as paid for ${tutor.tutor_name}`);
      loadTutorPayroll();
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setProcessingPayment(null);
    }
  }, [loadTutorPayroll]);

  // Reverse a payment
  const handleReversePayment = useCallback(async (paymentId, tutorName) => {
    if (!window.confirm(`Reverse payment for ${tutorName}? This will mark sessions as unpaid again.`)) {
      return;
    }
    try {
      await adminAPI.reverseTutorPayment(paymentId);
      toast.success('Payment reversed successfully');
      loadPaymentHistory();
      loadTutorPayroll();
    } catch (error) {
      console.error('Error reversing payment:', error);
      toast.error('Failed to reverse payment');
    }
  }, [loadPaymentHistory, loadTutorPayroll]);

  // Toggle payroll tutor expansion
  const togglePayrollTutorExpand = (tutorName) => {
    setExpandedPayrollTutors(prev => ({
      ...prev,
      [tutorName]: !prev[tutorName]
    }));
  };

  // Open session edit modal
  const openSessionEditModal = useCallback(async (session) => {
    try {
      const response = await adminAPI.getSessionPayrollDetails(session.log_id);
      setEditingSession(response.data);
      setSessionEditForm({
        tutor_rate_used: response.data.payroll_fields.tutor_rate_used || '',
        payment_notes: response.data.payroll_fields.payment_notes || ''
      });
      setShowSessionEditModal(true);
    } catch (error) {
      console.error('Error loading session details:', error);
      toast.error('Failed to load session details');
    }
  }, []);

  // Save session payroll fields
  const saveSessionPayroll = useCallback(async () => {
    if (!editingSession) return;
    
    setSavingSessionPayroll(true);
    try {
      const data = {};
      if (sessionEditForm.tutor_rate_used !== '' && sessionEditForm.tutor_rate_used !== null) {
        data.tutor_rate_used = parseFloat(sessionEditForm.tutor_rate_used);
      }
      if (sessionEditForm.payment_notes !== editingSession.payroll_fields.payment_notes) {
        data.payment_notes = sessionEditForm.payment_notes;
      }
      
      await adminAPI.updateSessionPayroll(editingSession.session.log_id, data);
      toast.success('Session payroll updated');
      setShowSessionEditModal(false);
      setEditingSession(null);
      loadTutorPayroll();
    } catch (error) {
      console.error('Error saving session payroll:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingSessionPayroll(false);
    }
  }, [editingSession, sessionEditForm, loadTutorPayroll]);

  // Run backfill for existing sessions
  const runPayrollBackfill = useCallback(async () => {
    const confirmMsg = `This will safely add missing payroll fields to existing approved sessions:\n\n` +
      `• Add payment_status = "unpaid" if missing\n` +
      `• Add tutor_rate_used from tutor profile if available\n` +
      `• Does NOT overwrite existing values\n` +
      `• Does NOT delete any data\n\n` +
      `Continue?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    setRunningBackfill(true);
    try {
      const response = await adminAPI.backfillPayrollFields();
      const stats = response.data.stats;
      toast.success(
        `Backfill complete:\n` +
        `• ${stats.sessions_checked} sessions checked\n` +
        `• ${stats.payment_status_added} payment_status added\n` +
        `• ${stats.tutor_rate_added} tutor rates added\n` +
        `• ${stats.rate_missing_count} still missing rate`
      );
      loadTutorPayroll();
    } catch (error) {
      console.error('Error running backfill:', error);
      toast.error('Failed to run backfill');
    } finally {
      setRunningBackfill(false);
    }
  }, [loadTutorPayroll]);

  // ===================== TUTOR INVOICE FUNCTIONS =====================
  
  // Load invoices list
  const loadInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      const params = invoiceStatusFilter !== 'all' ? { status: invoiceStatusFilter } : {};
      const response = await adminAPI.getTutorInvoices({ ...params, limit: 100 });
      setInvoices(response.data?.invoices || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoadingInvoices(false);
    }
  }, [invoiceStatusFilter]);

  // Load invoice periods
  const loadInvoicePeriods = useCallback(async () => {
    try {
      const [currentRes, prevRes] = await Promise.all([
        adminAPI.getInvoicePeriods(),
        adminAPI.getPreviousInvoicePeriod()
      ]);
      setCurrentPeriod(currentRes.data);
      setPreviousPeriod(prevRes.data);
    } catch (error) {
      console.error('Error loading invoice periods:', error);
    }
  }, []);

  // Load eligible tutors for a period
  const loadEligibleTutors = useCallback(async (periodStart, periodEnd) => {
    if (!periodStart || !periodEnd) return;
    
    setLoadingEligibleTutors(true);
    try {
      const response = await adminAPI.getInvoiceEligibleTutors({
        period_start: periodStart,
        period_end: periodEnd
      });
      setEligibleTutors(response.data?.eligible_tutors || []);
      setIneligibleReasons(response.data?.ineligible_reasons || []);
    } catch (error) {
      console.error('Error loading eligible tutors:', error);
      setEligibleTutors([]);
      setIneligibleReasons([]);
    } finally {
      setLoadingEligibleTutors(false);
    }
  }, []);

  // Generate single invoice
  const handleGenerateInvoice = useCallback(async () => {
    if (!generateInvoiceForm.tutor_id || !generateInvoiceForm.period_start || !generateInvoiceForm.period_end) {
      toast.error('Please fill all fields');
      return;
    }
    
    setGeneratingInvoice(true);
    try {
      const response = await adminAPI.generateInvoice(generateInvoiceForm);
      if (response.data?.success) {
        toast.success(`Invoice generated: ${response.data.invoice?.invoice_number || 'Success'}`);
        setShowGenerateInvoiceModal(false);
        setGenerateInvoiceForm({ tutor_id: '', period_start: '', period_end: '' });
        loadInvoices();
      } else {
        toast.error(response.data?.message || 'No sessions found for this period');
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGeneratingInvoice(false);
    }
  }, [generateInvoiceForm, loadInvoices]);

  // Send invoice email
  const handleSendInvoiceEmail = useCallback(async (invoiceId, recipientType = 'both') => {
    setSendingInvoiceEmail(invoiceId);
    try {
      const response = await adminAPI.sendInvoiceEmail(invoiceId, recipientType);
      if (response.data?.status === 'success') {
        toast.success('Invoice email sent successfully');
        loadInvoices();
      } else if (response.data?.status === 'partial_failure') {
        toast.warning('Some emails failed to send');
      } else {
        toast.error('Failed to send invoice email');
      }
    } catch (error) {
      console.error('Error sending invoice email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingInvoiceEmail(null);
    }
  }, [loadInvoices]);

  // Download invoice PDF
  const handleDownloadInvoicePDF = useCallback(async (invoiceId, invoiceNumber) => {
    try {
      const response = await api.get(`/admin/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  }, []);

  // Load scheduler run history
  const loadSchedulerRuns = useCallback(async () => {
    try {
      const response = await adminAPI.getSchedulerRuns(10);
      setSchedulerRuns(response.data?.runs || []);
    } catch (error) {
      console.error('Error loading scheduler runs:', error);
    }
  }, []);

  // Run invoice batch with selected month/year
  const handleRunInvoiceBatch = useCallback(async (type) => {
    // Calculate period dates based on selected month/year
    const year = batchYear;
    const month = batchMonth;
    const lastDay = new Date(year, month, 0).getDate(); // Last day of selected month
    
    let periodStart, periodEnd;
    if (type === '15th') {
      periodStart = `${year}-${String(month).padStart(2, '0')}-01`;
      periodEnd = `${year}-${String(month).padStart(2, '0')}-15`;
    } else {
      periodStart = `${year}-${String(month).padStart(2, '0')}-16`;
      periodEnd = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
    }
    
    const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'long' });
    const confirmMsg = type === '15th'
      ? `Run invoice batch for ${monthName} 1-15, ${year}?\n\nThis will generate and email invoices to all tutors with approved sessions in this period.`
      : `Run invoice batch for ${monthName} 16-${lastDay}, ${year}?\n\nThis will generate and email invoices to all tutors with approved sessions in this period.`;
    
    if (!window.confirm(confirmMsg)) return;
    
    setRunningInvoiceBatch(true);
    try {
      const response = await adminAPI.runInvoiceBatch({
        period_start: periodStart,
        period_end: periodEnd
      });
      
      const result = response.data;
      if (result.status === 'completed' || result.status === 'partial_failure') {
        const invoicesCreated = result.invoices_created || 0;
        if (invoicesCreated > 0) {
          toast.success(
            `Invoice batch complete:\n` +
            `• ${invoicesCreated} invoices created\n` +
            `• ${result.emails_sent || 0} emails sent\n` +
            `• ${result.invoices_skipped_zero_hours || 0} tutors skipped (no hours)`
          );
        } else {
          toast.warning(
            `Batch completed but no invoices created.\n` +
            `• ${result.invoices_skipped_zero_hours || 0} tutors skipped (no eligible hours)\n` +
            `• ${result.invoices_skipped_duplicate || 0} already invoiced\n` +
            `Check Batch History for details.`
          );
        }
        loadInvoices();
        loadSchedulerRuns();
      } else if (result.status === 'skipped_duplicate') {
        toast.warning(
          `Invoice batch already ran for this period and created invoices.\n` +
          `View Batch History for details or use Generate Invoice for individual tutors.`
        );
        loadSchedulerRuns();
      } else {
        toast.error(result.error || 'Invoice batch failed');
      }
    } catch (error) {
      console.error('Error running invoice batch:', error);
      toast.error(error.response?.data?.detail || 'Failed to run invoice batch');
    } finally {
      setRunningInvoiceBatch(false);
    }
  }, [loadInvoices, batchMonth, batchYear, loadSchedulerRuns]);

  // Refresh on tab/window visibility change
  useEffect(() => {
    if (!showDashboard) return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && pollingActive.current) {
        // Only refresh if it's been at least 5 seconds since last refresh
        const now = Date.now();
        if (now - lastStatsRefresh.current > 5000) {
          refreshStats();
        }
        if (now - lastListsRefresh.current > 10000) {
          refreshLists();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [showDashboard, refreshStats, refreshLists]);

  // Immediate refresh after admin actions (used by action handlers)
  const triggerImmediateRefresh = useCallback(() => {
    // Small delay to allow backend to process
    setTimeout(() => {
      refreshStats();
      refreshLists();
    }, 500);
  }, [refreshStats, refreshLists]);

  // Enable sound on first click anywhere on the dashboard
  useEffect(() => {
    if (!showDashboard) return;
    
    const handleFirstInteraction = () => {
      enableNotificationSound();
      document.removeEventListener('click', handleFirstInteraction);
    };
    
    document.addEventListener('click', handleFirstInteraction);
    
    return () => {
      document.removeEventListener('click', handleFirstInteraction);
    };
  }, [showDashboard, enableNotificationSound]);

  // Load Email Center data when dashboard loads
  useEffect(() => {
    if (showDashboard) {
      loadEmailTemplates();
      loadEmailHistory();
    }
  }, [showDashboard]);

  const loadTeamMembers = async () => {
    setLoadingTeam(true);
    try {
      const res = await teamAPI.adminList();
      setTeamMembers(res.data.team_members || []);
    } catch (error) {
      console.error('Error loading team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoadingTeam(false);
    }
  };

  const handleOpenTeamDialog = (member = null) => {
    if (member) {
      setEditingTeamMember(member);
      setTeamForm({
        full_name: member.full_name || '',
        title: member.title || '',
        bio: member.bio || '',
        profile_image: member.profile_image || '',
        display_order: member.display_order || 0,
        is_visible: member.is_visible !== false
      });
    } else {
      setEditingTeamMember(null);
      setTeamForm({
        full_name: '',
        title: '',
        bio: '',
        profile_image: '',
        display_order: teamMembers.length,
        is_visible: true
      });
    }
    setShowTeamDialog(true);
  };

  const handleSaveTeamMember = async () => {
    if (!teamForm.full_name.trim() || !teamForm.title.trim()) {
      toast.error('Name and title are required');
      return;
    }

    setSavingTeamMember(true);
    try {
      if (editingTeamMember) {
        await teamAPI.update(editingTeamMember.member_id, teamForm);
        toast.success('Team member updated');
      } else {
        await teamAPI.create(teamForm);
        toast.success('Team member added');
      }
      setShowTeamDialog(false);
      loadTeamMembers();
    } catch (error) {
      console.error('Error saving team member:', error);
      toast.error('Failed to save team member');
    } finally {
      setSavingTeamMember(false);
    }
  };

  const handleDeleteTeamMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) {
      return;
    }
    try {
      await teamAPI.delete(memberId);
      toast.success('Team member deleted');
      loadTeamMembers();
    } catch (error) {
      console.error('Error deleting team member:', error);
      toast.error('Failed to delete team member');
    }
  };

  const handleToggleTeamVisibility = async (member) => {
    try {
      await teamAPI.update(member.member_id, { is_visible: !member.is_visible });
      toast.success(member.is_visible ? 'Team member hidden' : 'Team member visible');
      loadTeamMembers();
    } catch (error) {
      console.error('Error toggling visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  const handleTeamPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!editingTeamMember) {
      toast.error('Please save the team member first, then upload the image');
      return;
    }

    setUploadingTeamPhoto(true);
    try {
      const res = await teamAPI.uploadImage(editingTeamMember.member_id, file);
      setTeamForm(prev => ({ ...prev, profile_image: res.data.image_url }));
      toast.success('Image uploaded');
      loadTeamMembers();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingTeamPhoto(false);
    }
  };

  const moveTeamMember = async (index, direction) => {
    const newOrder = [...teamMembers];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    
    const orderIds = newOrder.map(m => m.member_id);
    try {
      await teamAPI.reorder(orderIds);
      setTeamMembers(newOrder);
      toast.success('Order updated');
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error('Failed to reorder');
    }
  };

  // ===================== EMAIL CENTER FUNCTIONS =====================
  
  const emailTypeOptions = {
    tutors: [
      { value: 'invoice_reminder', label: 'Invoice Reminder' },
      { value: 'late_invoice_reminder', label: 'Late Invoice Reminder' },
      { value: 'missing_document_reminder', label: 'Missing Document Reminder' },
      { value: 'availability_reminder', label: 'Availability Reminder' },
      { value: 'policy_update', label: 'Policy Update' },
      { value: 'custom', label: 'Custom Message' }
    ],
    parents: [
      { value: 'package_reminder', label: 'Package Reminder' },
      { value: 'follow_up', label: 'Follow-up' },
      { value: 'inactive_student_reminder', label: 'Inactive Student Reminder' },
      { value: 'new_feature_update', label: 'New Feature Update' },
      { value: 'general_support', label: 'General Support Message' },
      { value: 'custom', label: 'Custom Message' }
    ],
    students: [
      { value: 'package_reminder', label: 'Package Reminder' },
      { value: 'follow_up', label: 'Follow-up' },
      { value: 'inactive_student_reminder', label: 'Inactive Student Reminder' },
      { value: 'new_feature_update', label: 'New Feature Update' },
      { value: 'general_support', label: 'General Support Message' },
      { value: 'custom', label: 'Custom Message' }
    ]
  };

  const loadEmailTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/email-center/templates`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmailTemplates(data.templates || {});
      }
    } catch (error) {
      console.error('Error loading email templates:', error);
    }
  };

  const loadEmailRecipients = async (audience, scope) => {
    if (!audience) return;
    setLoadingRecipients(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/email-center/recipients?audience=${audience}&scope=${scope}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }}
      );
      if (res.ok) {
        const data = await res.json();
        setEmailRecipients(data.recipients || []);
      }
    } catch (error) {
      console.error('Error loading recipients:', error);
      toast.error('Failed to load recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  const loadEmailHistory = async () => {
    setLoadingEmailHistory(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/email-center/history`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmailHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error loading email history:', error);
    } finally {
      setLoadingEmailHistory(false);
    }
  };

  const handleAudienceChange = (value) => {
    setEmailCenterForm(prev => ({
      ...prev,
      audience: value,
      emailType: '',
      subject: '',
      body: '',
      selectedRecipients: []
    }));
    loadEmailRecipients(value, emailCenterForm.recipientScope);
  };

  const handleEmailTypeChange = (value) => {
    const template = emailTemplates[emailCenterForm.audience]?.[value];
    setEmailCenterForm(prev => ({
      ...prev,
      emailType: value,
      subject: template?.subject || '',
      body: template?.body || ''
    }));
  };

  const handleRecipientScopeChange = (value) => {
    setEmailCenterForm(prev => ({
      ...prev,
      recipientScope: value,
      selectedRecipients: []
    }));
    loadEmailRecipients(emailCenterForm.audience, value);
  };

  const handleSendTestEmail = async () => {
    if (!emailCenterForm.subject || !emailCenterForm.body) {
      toast.error('Please fill in subject and message body');
      return;
    }
    
    setSendingTestEmail(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/email-center/test?subject=${encodeURIComponent(emailCenterForm.subject)}&body=${encodeURIComponent(emailCenterForm.body)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Test email sent to admin');
      } else {
        throw new Error(data.detail || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.message || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSendEmails = async () => {
    if (!emailCenterForm.audience || !emailCenterForm.emailType) {
      toast.error('Please select audience and email type');
      return;
    }
    if (!emailCenterForm.subject || !emailCenterForm.body) {
      toast.error('Please fill in subject and message body');
      return;
    }
    if (emailCenterForm.recipientScope === 'selected' && emailCenterForm.selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    setSendingEmail(true);
    try {
      const payload = {
        audience: emailCenterForm.audience,
        email_type: emailCenterForm.emailType,
        recipient_scope: emailCenterForm.recipientScope,
        selected_recipient_ids: emailCenterForm.selectedRecipients,
        subject: emailCenterForm.subject,
        body: emailCenterForm.body,
        schedule_later: false,
        scheduled_datetime: null
      };

      const res = await fetch(`${API_URL}/api/admin/email-center/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Emails sent successfully');
        // Reset form
        setEmailCenterForm({
          audience: '',
          emailType: '',
          recipientScope: 'all_active',
          selectedRecipients: [],
          subject: '',
          body: ''
        });
        setEmailRecipients([]);
        loadEmailHistory();
      } else {
        throw new Error(data.detail || 'Failed to send emails');
      }
    } catch (error) {
      console.error('Error sending emails:', error);
      toast.error(error.message || 'Failed to send emails');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCancelScheduledEmail = async (logId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled email?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/email-center/scheduled/${logId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        toast.success('Scheduled email cancelled');
        loadEmailHistory();
      } else {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to cancel');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to cancel scheduled email');
    }
  };

  const getRecipientCount = () => {
    if (emailCenterForm.recipientScope === 'selected') {
      return emailCenterForm.selectedRecipients.length;
    }
    return emailRecipients.length;
  };

  // ===================== MAINTENANCE / DATA RESET FUNCTIONS =====================
  
  const loadMaintenanceStudents = async () => {
    setLoadingMaintenanceStudents(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users?role=student&limit=500`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMaintenanceStudents(data.users || []);
      }
    } catch (error) {
      console.error('Error loading students for maintenance:', error);
      toast.error('Failed to load student accounts');
    } finally {
      setLoadingMaintenanceStudents(false);
    }
  };

  const handleResetSelectionToggle = (userId) => {
    setSelectedResetIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
    // Clear any previous results when selection changes
    setDryRunResult(null);
    setResetResult(null);
  };

  const handleSelectAllFiltered = () => {
    const filteredStudents = getFilteredMaintenanceStudents();
    const allFilteredIds = filteredStudents.map(s => s.user_id);
    const allSelected = allFilteredIds.every(id => selectedResetIds.includes(id));
    
    if (allSelected) {
      setSelectedResetIds(prev => prev.filter(id => !allFilteredIds.includes(id)));
    } else {
      setSelectedResetIds(prev => [...new Set([...prev, ...allFilteredIds])]);
    }
    setDryRunResult(null);
    setResetResult(null);
  };

  const handleClearSelection = () => {
    setSelectedResetIds([]);
    setDryRunResult(null);
    setResetResult(null);
  };

  const getFilteredMaintenanceStudents = () => {
    if (!searchMaintenance.trim()) return maintenanceStudents;
    const search = searchMaintenance.toLowerCase();
    return maintenanceStudents.filter(s => 
      s.name?.toLowerCase().includes(search) ||
      s.email?.toLowerCase().includes(search) ||
      s.user_id?.toLowerCase().includes(search)
    );
  };

  const handleDryRunReset = async () => {
    if (selectedResetIds.length === 0) {
      toast.error('Please select at least one student account');
      return;
    }
    
    setExecutingReset(true);
    setDryRunResult(null);
    setResetResult(null);
    
    console.log('[Maintenance] Dry run requested for:', selectedResetIds);
    
    try {
      let response;
      try {
        response = await fetch(`${API_URL}/api/admin/maintenance/reset-student-operational-data`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            user_ids: selectedResetIds,
            dry_run: true
          })
        });
      } catch (networkError) {
        console.error('[Maintenance] Dry run network error:', networkError);
        toast.error('Network error: Unable to connect to server');
        return;
      }
      
      console.log('[Maintenance] Dry run response status:', response.status);
      
      let data;
      try {
        data = await response.json();
        console.log('[Maintenance] Dry run response data:', data);
      } catch (parseError) {
        console.error('[Maintenance] Dry run JSON parse error:', parseError);
        toast.error('Server returned an invalid response');
        return;
      }
      
      setDryRunResult(data);
      
      if (!response.ok) {
        toast.error(data.detail || data.message || `Server error (${response.status})`);
        return;
      }
      
      if (data.status === 'error') {
        toast.error(data.message || 'Dry run failed');
      } else if (data.status === 'dry_run_complete') {
        toast.success(`Dry run complete: ${data.total_records_to_delete} records found for ${data.valid_student_ids?.length || 0} accounts`);
      } else {
        toast.info('Dry run complete - review results below');
      }
    } catch (error) {
      console.error('[Maintenance] Dry run unexpected error:', error);
      toast.error(`Dry run error: ${error.message || 'Unknown error'}`);
    } finally {
      setExecutingReset(false);
    }
  };

  const handleExecuteReset = async () => {
    if (!dryRunResult || dryRunResult.status !== 'dry_run_complete') {
      toast.error('Please run a dry run first');
      return;
    }
    
    if (!window.confirm(`⚠️ FINAL WARNING: This will permanently delete ${dryRunResult.total_records_to_delete} records for ${dryRunResult.valid_student_ids?.length || 0} student accounts. This action CANNOT be undone. Are you absolutely sure?`)) {
      return;
    }
    
    setExecutingReset(true);
    setResetResult(null);
    
    console.log('[Maintenance] Execute reset requested for:', selectedResetIds);
    
    try {
      let response;
      try {
        response = await fetch(`${API_URL}/api/admin/maintenance/reset-student-operational-data`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            user_ids: selectedResetIds,
            dry_run: false
          })
        });
      } catch (networkError) {
        console.error('[Maintenance] Network error:', networkError);
        toast.error('Network error: Unable to connect to server. Please check your connection.');
        return;
      }
      
      console.log('[Maintenance] Response status:', response.status);
      
      let data;
      try {
        data = await response.json();
        console.log('[Maintenance] Response data:', data);
      } catch (parseError) {
        console.error('[Maintenance] JSON parse error:', parseError);
        toast.error('Server returned an invalid response. Please try again.');
        return;
      }
      
      setResetResult(data);
      
      if (!response.ok) {
        // HTTP error (4xx, 5xx)
        toast.error(data.detail || data.message || `Server error (${response.status})`);
        return;
      }
      
      // HTTP 200 but check status field
      if (data.status === 'execution_complete') {
        toast.success(`Successfully deleted ${data.total_records_deleted} records for ${data.valid_student_ids?.length || 0} accounts`);
        triggerImmediateRefresh();
        setSelectedResetIds([]);
        setDryRunResult(null);
      } else if (data.status === 'error') {
        // Backend returned an error in the response body
        toast.error(data.message || 'Reset operation failed');
      } else {
        // Unexpected status
        toast.error(data.message || 'Unexpected response from server');
      }
    } catch (error) {
      console.error('[Maintenance] Unexpected error:', error);
      toast.error(`Unexpected error: ${error.message || 'Unknown error'}`);
    } finally {
      setExecutingReset(false);
    }
  };

  // ===================== FINANCIAL RECONCILIATION FUNCTIONS =====================

  const loadFinancialAudit = async () => {
    setLoadingFinancialAudit(true);
    setFinancialAudit(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/maintenance/financial-audit`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFinancialAudit(data);
        toast.success('Financial audit loaded');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to load financial audit');
      }
    } catch (error) {
      console.error('Financial audit error:', error);
      toast.error('Failed to load financial audit');
    } finally {
      setLoadingFinancialAudit(false);
    }
  };

  const handleFinancialResetSelectionToggle = (userId) => {
    setSelectedFinancialResetIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
    setFinancialDryRun(null);
    setFinancialResetResult(null);
  };

  const handleClearFinancialSelection = () => {
    setSelectedFinancialResetIds([]);
    setFinancialDryRun(null);
    setFinancialResetResult(null);
  };

  const handleFinancialDryRun = async () => {
    if (selectedFinancialResetIds.length === 0) {
      toast.error('Please select at least one student account');
      return;
    }
    
    setExecutingFinancialReset(true);
    setFinancialDryRun(null);
    setFinancialResetResult(null);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/maintenance/financial-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          user_ids: selectedFinancialResetIds,
          dry_run: true,
          reset_hour_tracking: true
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setFinancialDryRun(data);
        if (data.status === 'error') {
          toast.error(data.message);
        } else {
          toast.success('Financial dry run complete - review impact below');
        }
      } else {
        toast.error(data.detail || 'Financial dry run failed');
      }
    } catch (error) {
      console.error('Financial dry run error:', error);
      toast.error('Failed to perform financial dry run');
    } finally {
      setExecutingFinancialReset(false);
    }
  };

  const handleFinancialResetExecute = async () => {
    if (!financialDryRun || financialDryRun.status !== 'dry_run_complete') {
      toast.error('Please run a dry run first');
      return;
    }
    
    const impact = financialDryRun.financial_impact || {};
    if (!window.confirm(`⚠️ FINAL WARNING: This will permanently:\n\n• Delete ${financialDryRun.total_records_to_delete} records\n• Remove $${impact.revenue_to_remove} from Revenue\n• Remove $${impact.gross_revenue_to_remove} from Gross Revenue\n• Remove $${impact.tutor_cost_to_remove} from Tutor Cost\n• Remove ${impact.hours_owed_to_remove} from Hours Owed\n\nThis action CANNOT be undone. Are you absolutely sure?`)) {
      return;
    }
    
    setExecutingFinancialReset(true);
    setFinancialResetResult(null);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/maintenance/financial-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          user_ids: selectedFinancialResetIds,
          dry_run: false,
          reset_hour_tracking: true
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setFinancialResetResult(data);
        if (data.status === 'execution_complete') {
          toast.success(`Financial reset complete. Audit ID: ${data.audit_id}`);
          // Refresh all dashboard data
          triggerImmediateRefresh();
          // Reload audit
          loadFinancialAudit();
          // Clear selection
          setSelectedFinancialResetIds([]);
          setFinancialDryRun(null);
        } else {
          toast.error(data.message || 'Financial reset failed');
        }
      } else {
        toast.error(data.detail || 'Financial reset execution failed');
      }
    } catch (error) {
      console.error('Financial reset execution error:', error);
      toast.error('Failed to execute financial reset');
    } finally {
      setExecutingFinancialReset(false);
    }
  };

  const loadOrders = async () => {
    setLoadingOrders(true);
    try {
      const params = orderFilter !== 'all' ? { status: orderFilter } : {};
      const res = await adminAPI.listOrders({ ...params, limit: 50 });
      setOrders(res.data.orders || []);
      setOrderStats(res.data.stats || { total_paid: 0, total_pending: 0, total_revenue: 0 });
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleMarkOrderPaid = async (orderId) => {
    try {
      await adminAPI.updateOrder(orderId, { payment_status: 'paid' });
      toast.success('Order marked as paid');
      loadOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
  };

  const loadPayments = async () => {
    setLoadingPayments(true);
    try {
      const params = paymentFilter !== 'all' ? { status: paymentFilter } : {};
      const res = await adminAPI.listPayments({ ...params, limit: 50 });
      setPayments(res.data.payments || []);
      setPaymentStats(res.data.stats || { total_paid: 0, total_pending: 0, total_revenue: 0, under_18_payments: 0 });
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleViewPayment = async (payment) => {
    try {
      const res = await adminAPI.getPaymentDetails(payment.payment_id);
      setViewingPayment(res.data);
      setShowPaymentDialog(true);
    } catch (error) {
      console.error('Error loading payment details:', error);
      toast.error('Failed to load payment details');
    }
  };

  // Tutoring Requests functions
  const loadTutoringRequests = async () => {
    setLoadingRequests(true);
    try {
      const params = requestFilter !== 'all' ? { status: requestFilter } : {};
      const res = await tutoringRequestAPI.adminList({ ...params, limit: 50 });
      setTutoringRequests(res.data.requests || []);
      setRequestStats(res.data.stats || { pending: 0, matched: 0, in_progress: 0 });
    } catch (error) {
      console.error('Error loading tutoring requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleUpdateRequest = async (requestId, updates) => {
    try {
      await tutoringRequestAPI.adminUpdate(requestId, updates);
      toast.success('Request updated successfully');
      loadTutoringRequests();
    } catch (error) {
      console.error('Error updating request:', error);
      toast.error('Failed to update request');
    }
  };

  // Notifications functions
  const loadNotifications = async () => {
    try {
      const res = await notificationsAPI.list(false, 50);
      setNotifications(res.data.notifications || []);
      setUnreadNotificationCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await notificationsAPI.markRead(notificationId);
      loadNotifications();
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      loadNotifications();
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications read:', error);
    }
  };

  const loadTutors = async () => {
    try {
      const res = await adminAPI.listTutors({ status: tutorFilter, limit: 50 });
      setTutors(res.data.tutors || []);
      setTutorStats(res.data.stats || { total: 0, pending: 0, approved: 0 });
    } catch (error) {
      console.error('Error loading tutors:', error);
    }
  };

  // Tutor management handlers
  const resetTutorForm = () => {
    setTutorForm({
      name: '',
      email: '',
      headline: '',
      subjects: [],
      grade_levels: [],
      university_courses: [],
      bio: '',
      qualifications: '',
      experience_years: 0,
      tutoring_mode: 'online',
      city: '',
      availability_summary: '',
      availability: {},
      languages: ['English'],
      profile_photo_url: '',
      is_approved: true,
      is_visible: true
    });
    setEditingTutor(null);
  };

  const handleOpenTutorDialog = (tutor = null) => {
    if (tutor) {
      setEditingTutor(tutor);
      setTutorForm({
        name: tutor.name || '',
        email: tutor.email || '',
        headline: tutor.headline || '',
        subjects: tutor.subjects || [],
        grade_levels: tutor.grade_levels || [],
        university_courses: tutor.university_courses || [],
        bio: tutor.bio || '',
        qualifications: tutor.qualifications || '',
        experience_years: tutor.experience_years || 0,
        tutoring_mode: tutor.tutoring_mode || 'online',
        city: tutor.city || '',
        availability_summary: tutor.availability_summary || '',
        availability: tutor.availability || {},
        languages: tutor.languages || ['English'],
        profile_photo_url: tutor.profile_photo_url || '',
        is_approved: tutor.is_approved !== false,
        is_visible: tutor.is_visible !== false
      });
      setTutorPhotoPreview(tutor.profile_photo_url || '');
    } else {
      resetTutorForm();
      setTutorPhotoPreview('');
    }
    setShowTutorDialog(true);
  };

  const handleSaveTutor = async () => {
    if (!tutorForm.name.trim()) {
      toast.error('Tutor name is required');
      return;
    }
    if (tutorForm.subjects.length === 0) {
      toast.error('At least one subject is required');
      return;
    }

    setSavingTutor(true);
    try {
      if (editingTutor) {
        await adminAPI.updateTutor(editingTutor.profile_id, tutorForm);
        toast.success('Tutor updated successfully');
      } else {
        await adminAPI.createTutor(tutorForm);
        toast.success('Tutor created successfully');
      }
      setShowTutorDialog(false);
      resetTutorForm();
      loadTutors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tutor');
    } finally {
      setSavingTutor(false);
    }
  };

  const handleDeleteTutor = async (profileId) => {
    if (!window.confirm('Are you sure you want to delete this tutor?')) return;
    
    try {
      await adminAPI.deleteTutor(profileId);
      toast.success('Tutor deleted');
      loadTutors();
    } catch (error) {
      toast.error('Failed to delete tutor');
    }
  };

  const handleApproveTutorProfile = async (profileId) => {
    try {
      await adminAPI.approveTutorProfile(profileId);
      toast.success('Tutor approved and visible');
      loadTutors();
    } catch (error) {
      toast.error('Failed to approve tutor');
    }
  };

  const handleUnapproveTutorProfile = async (profileId) => {
    try {
      await adminAPI.unapproveTutorProfile(profileId);
      toast.success('Tutor unapproved and hidden');
      loadTutors();
    } catch (error) {
      toast.error('Failed to unapprove tutor');
    }
  };

  const handleToggleVisibility = async (profileId) => {
    try {
      const res = await adminAPI.toggleTutorVisibility(profileId);
      toast.success(res.data.message);
      loadTutors();
    } catch (error) {
      toast.error('Failed to toggle visibility');
    }
  };

  // Tutor Approved Rate Management handlers
  const loadTutorRates = async (tutorId) => {
    try {
      const res = await adminAPI.getTutorApprovedRates(tutorId);
      setTutorRates(res.data);
      setRateFormData({
        approved_rate_high_school: res.data.approved_rate_high_school || '',
        approved_rate_university: res.data.approved_rate_university || '',
        effective_date: res.data.rate_effective_date || new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      console.error('Failed to load tutor rates:', error);
      setTutorRates(null);
    }
  };

  const handleSaveTutorRates = async () => {
    if (!viewingTutor) return;
    
    const tutorId = viewingTutor.profile_id || viewingTutor.user_id;
    if (!tutorId) {
      toast.error('Invalid tutor ID');
      return;
    }

    if (!rateFormData.effective_date) {
      toast.error('Please set an effective date');
      return;
    }

    setSavingRates(true);
    try {
      const payload = {
        effective_date: rateFormData.effective_date
      };
      
      if (rateFormData.approved_rate_high_school !== '') {
        payload.approved_rate_high_school = parseFloat(rateFormData.approved_rate_high_school);
      }
      if (rateFormData.approved_rate_university !== '') {
        payload.approved_rate_university = parseFloat(rateFormData.approved_rate_university);
      }

      await adminAPI.updateTutorApprovedRates(tutorId, payload);
      toast.success('Tutor rates updated successfully. New rates apply to future completed sessions only.');
      setEditingRates(false);
      loadTutorRates(tutorId);
    } catch (error) {
      console.error('Failed to save tutor rates:', error);
      toast.error(error.response?.data?.detail || 'Failed to save tutor rates');
    } finally {
      setSavingRates(false);
    }
  };

  // Handle session approval for Platform Income tracking
  const handleApproveSession = async (logId, gradeLevel = 'high_school') => {
    setApprovingSessionId(logId);
    try {
      const response = await adminAPI.approveSession(logId, { 
        status: 'approved', 
        grade_level: gradeLevel 
      });
      toast.success(`Session approved! Platform income: $${response.data.platform_income?.toFixed(2) || '0.00'}`);
      // Refresh pending sessions and business metrics
      const [sessionsRes, metricsRes] = await Promise.all([
        adminAPI.listSessions({ status: 'pending', limit: 50 }).catch(() => ({ data: { sessions: [] } })),
        adminAPI.getBusinessMetrics().catch(() => ({ data: null }))
      ]);
      setPendingSessions(sessionsRes.data?.sessions || []);
      setBusinessMetrics(metricsRes.data);
    } catch (error) {
      console.error('Failed to approve session:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve session');
    } finally {
      setApprovingSessionId(null);
    }
  };

  const handleTutorPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => setTutorPhotoPreview(e.target.result);
    reader.readAsDataURL(file);

    setUploadingTutorPhoto(true);
    try {
      const response = await uploadAPI.uploadPhoto(file);
      setTutorForm(prev => ({ ...prev, profile_photo_url: response.data.url }));
      toast.success('Photo uploaded!');
    } catch (error) {
      toast.error('Failed to upload photo');
      setTutorPhotoPreview('');
    } finally {
      setUploadingTutorPhoto(false);
    }
  };

  const addSubjectToForm = () => {
    if (newSubject.trim() && !tutorForm.subjects.includes(newSubject.trim())) {
      setTutorForm(prev => ({ ...prev, subjects: [...prev.subjects, newSubject.trim()] }));
      setNewSubject('');
    }
  };

  const copySubmissionLink = () => {
    const link = `${window.location.origin}/become-a-tutor`;
    navigator.clipboard.writeText(link);
    toast.success('Submission link copied to clipboard!');
  };

  const handleApproveTutor = async (userId) => {
    try {
      await adminAPI.approveTutor(userId);
      toast.success('Tutor approved!');
      loadData();
    } catch (error) {
      toast.error('Failed to approve tutor');
    }
  };

  // Live Chat handlers - conversation-based
  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    // Mark conversation as read when opened
    if (conversation.unread_count > 0) {
      try {
        await adminAPI.markConversationRead(conversation.conversation_id);
        setChatConversations(prev => prev.map(c => 
          c.conversation_id === conversation.conversation_id 
            ? { ...c, unread_count: 0, messages: c.messages.map(m => ({ ...m, is_read: true })) }
            : c
        ));
        setUnreadChatCount(prev => Math.max(0, prev - conversation.unread_count));
      } catch (error) {
        console.error('Failed to mark conversation as read:', error);
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await adminAPI.markAllChatRead();
      setChatConversations(prev => prev.map(c => ({
        ...c,
        unread_count: 0,
        messages: c.messages.map(m => ({ ...m, is_read: true }))
      })));
      setUnreadChatCount(0);
      toast.success('All messages marked as read');
    } catch (error) {
      toast.error('Failed to mark all messages as read');
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedConversation) return;
    
    setSendingReply(true);
    try {
      await adminAPI.replyChatMessage({
        message: replyMessage,
        conversation_id: selectedConversation.conversation_id
      });
      toast.success('Reply sent!');
      setReplyMessage('');
      // Refresh to get updated conversation
      const chatRes = await adminAPI.getChatMessages({ limit: 100 });
      setChatConversations(chatRes.data.conversations || []);
      // Update selected conversation with new message
      const updatedConv = chatRes.data.conversations?.find(c => c.conversation_id === selectedConversation.conversation_id);
      if (updatedConv) {
        setSelectedConversation(updatedConv);
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  // Student Hours Management handlers
  const handleOpenHoursModal = async (student) => {
    setSelectedStudentForHours(student);
    setHoursModalOpen(true);
    setLoadingHoursData(true);
    try {
      const response = await adminAPI.getStudentHours(student.user_id);
      setStudentHoursData(response.data);
      setNewPurchasedHours(response.data.total_hours_purchased?.toString() || '0');
    } catch (error) {
      toast.error('Failed to load hours data');
    } finally {
      setLoadingHoursData(false);
    }
  };

  // Lesson History handlers
  const toggleLessonHistory = async (studentId) => {
    const isCurrentlyExpanded = lessonHistoryExpanded[studentId];
    
    if (isCurrentlyExpanded) {
      // Collapse
      setLessonHistoryExpanded(prev => ({ ...prev, [studentId]: false }));
    } else {
      // Expand and load if not already loaded
      setLessonHistoryExpanded(prev => ({ ...prev, [studentId]: true }));
      
      if (!lessonHistoryData[studentId]) {
        setLoadingLessonHistory(prev => ({ ...prev, [studentId]: true }));
        try {
          const response = await adminAPI.getStudentHours(studentId);
          setLessonHistoryData(prev => ({ 
            ...prev, 
            [studentId]: response.data.session_logs || [] 
          }));
        } catch (error) {
          toast.error('Failed to load lesson history');
          setLessonHistoryExpanded(prev => ({ ...prev, [studentId]: false }));
        } finally {
          setLoadingLessonHistory(prev => ({ ...prev, [studentId]: false }));
        }
      }
    }
  };

  // Helper to get lesson notes file status
  const getLessonNotesStatus = (lesson) => {
    // Check if file was removed by retention policy
    if (lesson.lesson_notes_expired === true) {
      return { 
        available: false, 
        expired: true,
        label: 'Expired (9-month retention)' 
      };
    }
    
    const lessonNotesUrl = lesson.lesson_notes_url;
    if (!lessonNotesUrl || lessonNotesUrl.trim() === '') {
      return { available: false, expired: false, label: 'Missing/Unavailable' };
    }
    return { available: true, expired: false, label: 'Available' };
  };

  // Helper to get full lesson notes URL
  const getLessonNotesUrl = (lessonNotesUrl) => {
    if (!lessonNotesUrl) return null;
    if (lessonNotesUrl.startsWith('http')) return lessonNotesUrl;
    if (lessonNotesUrl.startsWith('/')) return `${API_URL}${lessonNotesUrl}`;
    return lessonNotesUrl;
  };

  const handleGenerateTutorLink = async (userId) => {
    try {
      const response = await adminAPI.generateTutorLink(userId);
      toast.success('Tutor link generated!');
      // Update the users list with the new token
      setUsers(prev => prev.map(u => 
        u.user_id === userId 
          ? { ...u, tutor_link_token: response.data.tutor_link_token, tutor_link_active: true }
          : u
      ));
    } catch (error) {
      toast.error('Failed to generate tutor link');
    }
  };

  const handleRegenerateTutorLink = async () => {
    if (!selectedStudentForHours) return;
    try {
      const response = await adminAPI.regenerateTutorLink(selectedStudentForHours.user_id);
      toast.success('Tutor link regenerated!');
      setStudentHoursData(prev => ({ ...prev, tutor_link_token: response.data.tutor_link_token }));
      setUsers(prev => prev.map(u => 
        u.user_id === selectedStudentForHours.user_id 
          ? { ...u, tutor_link_token: response.data.tutor_link_token }
          : u
      ));
    } catch (error) {
      toast.error('Failed to regenerate link');
    }
  };

  const handleToggleTutorLink = async () => {
    if (!selectedStudentForHours) return;
    try {
      const response = await adminAPI.toggleTutorLink(selectedStudentForHours.user_id);
      toast.success(response.data.message);
      setStudentHoursData(prev => ({ ...prev, tutor_link_active: response.data.tutor_link_active }));
      setUsers(prev => prev.map(u => 
        u.user_id === selectedStudentForHours.user_id 
          ? { ...u, tutor_link_active: response.data.tutor_link_active }
          : u
      ));
    } catch (error) {
      toast.error('Failed to toggle link');
    }
  };

  const handleUpdatePurchasedHours = async () => {
    if (!selectedStudentForHours) return;
    const hours = parseFloat(newPurchasedHours);
    if (isNaN(hours) || hours < 0) {
      toast.error('Please enter a valid number');
      return;
    }
    try {
      const response = await adminAPI.updateStudentHours(selectedStudentForHours.user_id, { total_hours_purchased: hours });
      toast.success('Hours updated!');
      setStudentHoursData(prev => ({
        ...prev,
        total_hours_purchased: response.data.total_hours_purchased,
        total_hours_remaining: response.data.total_hours_remaining
      }));
      setUsers(prev => prev.map(u => 
        u.user_id === selectedStudentForHours.user_id 
          ? { ...u, total_hours_purchased: response.data.total_hours_purchased, total_hours_used: response.data.total_hours_used }
          : u
      ));
      setEditingPurchasedHours(false);
    } catch (error) {
      toast.error('Failed to update hours');
    }
  };

  const handleEditSessionLog = (log) => {
    setEditingSessionLog(log);
    setSessionLogForm({
      tutor_name: log.tutor_name || '',
      session_date: log.session_date || '',
      session_time: log.session_time || '',
      hours_used: log.hours_used?.toString() || '',
      notes: log.notes || ''
    });
  };

  const handleSaveSessionLog = async () => {
    if (!editingSessionLog) return;
    const hours = parseFloat(sessionLogForm.hours_used);
    if (isNaN(hours) || hours <= 0) {
      toast.error('Hours must be greater than 0');
      return;
    }
    try {
      await adminAPI.editSessionLog(editingSessionLog.log_id, {
        tutor_name: sessionLogForm.tutor_name,
        session_date: sessionLogForm.session_date,
        session_time: sessionLogForm.session_time,
        hours_used: hours,
        notes: sessionLogForm.notes
      });
      toast.success('Session log updated!');
      // Refresh hours data
      const response = await adminAPI.getStudentHours(selectedStudentForHours.user_id);
      setStudentHoursData(response.data);
      setUsers(prev => prev.map(u => 
        u.user_id === selectedStudentForHours.user_id 
          ? { ...u, total_hours_used: response.data.total_hours_used }
          : u
      ));
      setEditingSessionLog(null);
    } catch (error) {
      toast.error('Failed to update session log');
    }
  };

  const handleDeleteSessionLog = async (logId) => {
    if (!confirm('Are you sure you want to delete this session log?')) return;
    try {
      const response = await adminAPI.deleteSessionLog(logId);
      toast.success('Session log deleted!');
      // Refresh hours data
      const hoursResponse = await adminAPI.getStudentHours(selectedStudentForHours.user_id);
      setStudentHoursData(hoursResponse.data);
      setUsers(prev => prev.map(u => 
        u.user_id === selectedStudentForHours.user_id 
          ? { ...u, total_hours_used: hoursResponse.data.total_hours_used }
          : u
      ));
    } catch (error) {
      toast.error('Failed to delete session log');
    }
  };

  // ===================== TUTOR ASSIGNMENT HANDLERS =====================
  
  const loadApprovedTutors = async () => {
    try {
      const response = await adminAPI.getApprovedTutors();
      setApprovedTutors(response.data.tutors || []);
    } catch (error) {
      console.error('Failed to load approved tutors:', error);
    }
  };

  const handleOpenAssignTutorModal = async (student) => {
    setStudentForTutorAssignment(student);
    setSelectedTutorForAssignment(student.assigned_tutor_id || '');
    setShowAssignTutorModal(true);
    await loadApprovedTutors();
  };

  const handleAssignTutor = async () => {
    if (!selectedTutorForAssignment || !studentForTutorAssignment) {
      toast.error('Please select a tutor');
      return;
    }
    
    setAssigningTutor(true);
    try {
      const response = await adminAPI.assignTutor(studentForTutorAssignment.user_id, selectedTutorForAssignment);
      toast.success(`Tutor assigned! Notifications sent.`);
      
      // Update the users list with the assignment
      setUsers(prev => prev.map(u => 
        u.user_id === studentForTutorAssignment.user_id 
          ? { 
              ...u, 
              assigned_tutor_id: response.data.tutor_id,
              assigned_tutor_name: response.data.tutor_name,
              tutor_assigned_at: response.data.assigned_at
            }
          : u
      ));
      
      setShowAssignTutorModal(false);
      setStudentForTutorAssignment(null);
      setSelectedTutorForAssignment('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign tutor');
    } finally {
      setAssigningTutor(false);
    }
  };

  const handleUnassignTutor = async (student) => {
    // Open confirmation modal instead of window.confirm
    setStudentForUnassignment(student);
    setShowUnassignConfirmModal(true);
  };
  
  const confirmUnassignTutor = async () => {
    if (!studentForUnassignment) return;
    
    setUnassigningTutor(true);
    try {
      const response = await adminAPI.unassignTutor(studentForUnassignment.user_id);
      const emailsSent = response.data?.emails_sent || {};
      
      // Build notification message
      let message = 'Tutor unassigned successfully.';
      const notified = [];
      if (emailsSent.student_notified) notified.push('student');
      if (emailsSent.tutor_notified) notified.push('tutor');
      if (emailsSent.parent_notified) notified.push('parent');
      if (notified.length > 0) {
        message += ` Notification emails sent to: ${notified.join(', ')}.`;
      }
      
      toast.success(message);
      
      // Update the users list
      setUsers(prev => prev.map(u => 
        u.user_id === studentForUnassignment.user_id 
          ? { ...u, assigned_tutor_id: null, assigned_tutor_name: null, tutor_assigned_at: null }
          : u
      ));
      
      setShowUnassignConfirmModal(false);
      setStudentForUnassignment(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unassign tutor');
    } finally {
      setUnassigningTutor(false);
    }
  };

  // ===================== USER ARCHIVE HANDLERS =====================
  
  const handleArchiveUser = (user) => {
    setUserToArchive(user);
    setShowArchiveConfirmModal(true);
  };

  const confirmArchiveUser = async () => {
    if (!userToArchive) return;
    
    setArchivingUser(true);
    try {
      await adminAPI.archiveUser(userToArchive.user_id);
      toast.success(`${userToArchive.name || userToArchive.email} has been archived`);
      
      // Remove from active users list
      setUsers(prev => prev.filter(u => u.user_id !== userToArchive.user_id));
      
      // Add to archived users list if showing
      if (showArchivedUsers) {
        setArchivedUsers(prev => [...prev, { ...userToArchive, is_archived: true, archived_at: new Date().toISOString() }]);
      }
      
      setShowArchiveConfirmModal(false);
      setUserToArchive(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive user');
    } finally {
      setArchivingUser(false);
    }
  };

  const handleUnarchiveUser = async (user) => {
    setRestoringUser(true);
    try {
      await adminAPI.unarchiveUser(user.user_id);
      toast.success(`${user.name || user.email} has been restored`);
      
      // Remove from archived list
      setArchivedUsers(prev => prev.filter(u => u.user_id !== user.user_id));
      
      // Add back to active users list
      setUsers(prev => [...prev, { ...user, is_archived: false }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore user');
    } finally {
      setRestoringUser(false);
    }
  };

  const loadArchivedUsers = async () => {
    try {
      const response = await adminAPI.listUsers({ archived_only: true, limit: 100 });
      setArchivedUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load archived users');
    }
  };

  // Load archived users when toggling to show them
  useEffect(() => {
    if (showArchivedUsers) {
      loadArchivedUsers();
    }
  }, [showArchivedUsers]);

  // ===================== TUTOR REVIEW HANDLERS =====================
  
  const handleOpenTutorReview = async (tutor) => {
    const tutorId = tutor.user_id || tutor.profile_id;
    setSelectedTutorForReview(tutor);
    setShowTutorReviewModal(true);
    setLoadingTutorReview(true);
    setNewAdminNote('');
    setAdminNoteType('general');
    
    try {
      const response = await adminAPI.getTutorFullReview(tutorId);
      setTutorReviewData(response.data);
    } catch (error) {
      toast.error('Failed to load tutor review data');
      console.error(error);
    } finally {
      setLoadingTutorReview(false);
    }
  };

  const handleAddAdminNote = async () => {
    if (!newAdminNote.trim() || !tutorReviewData) return;
    const tutorId = selectedTutorForReview?.user_id || selectedTutorForReview?.profile_id;
    
    try {
      await adminAPI.addTutorNote(tutorId, { note: newAdminNote, type: adminNoteType });
      toast.success('Note added');
      setNewAdminNote('');
      // Refresh data
      const response = await adminAPI.getTutorFullReview(tutorId);
      setTutorReviewData(response.data);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteAdminNote = async (noteId) => {
    const tutorId = selectedTutorForReview?.user_id || selectedTutorForReview?.profile_id;
    try {
      await adminAPI.deleteTutorNote(tutorId, noteId);
      toast.success('Note deleted');
      const response = await adminAPI.getTutorFullReview(tutorId);
      setTutorReviewData(response.data);
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleReviewDocument = async (document) => {
    setReviewingDocument(document);
    setDocumentReviewStatus(document.status || 'pending');
    setDocumentReviewNotes(document.admin_notes || '');
  };

  const handleSubmitDocumentReview = async () => {
    if (!reviewingDocument || !tutorReviewData) return;
    const tutorId = selectedTutorForReview?.user_id || selectedTutorForReview?.profile_id;
    
    try {
      await adminAPI.reviewTutorDocument(tutorId, reviewingDocument.document_id, {
        status: documentReviewStatus,
        admin_notes: documentReviewNotes
      });
      toast.success('Document reviewed');
      setReviewingDocument(null);
      // Refresh data
      const response = await adminAPI.getTutorFullReview(tutorId);
      setTutorReviewData(response.data);
      // Refresh tutors list
      loadData();
    } catch (error) {
      toast.error('Failed to review document');
    }
  };

  const handleUpdateTutorStatus = async (newStatus) => {
    const tutorId = selectedTutorForReview?.user_id || selectedTutorForReview?.profile_id;
    try {
      await adminAPI.updateTutorStatus(tutorId, { status: newStatus });
      toast.success(`Tutor status updated to: ${newStatus}`);
      // Refresh data
      const response = await adminAPI.getTutorFullReview(tutorId);
      setTutorReviewData(response.data);
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    confirmed: 'bg-green-100 text-green-700',
    completed: 'bg-slate-100 text-slate-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  const roleColors = {
    student: 'bg-blue-100 text-blue-700',
    tutor: 'bg-purple-100 text-purple-700',
    admin: 'bg-red-100 text-red-700'
  };

  // Show loading spinner while checking auth
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-400" />
      </div>
    );
  }

  // Show admin login form if not authenticated or not showing dashboard
  if (!showDashboard) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 text-white p-6 hidden lg:block">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <GraduationCap className="h-8 w-8 text-indigo-400" />
          <span className="font-heading text-lg font-semibold">Admin Panel</span>
        </Link>

        <nav className="space-y-2">
          <div className="px-4 py-2 bg-slate-800 rounded-lg text-white flex items-center gap-3">
            <Shield className="h-5 w-5" />
            <span>Dashboard</span>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-red-600">{user?.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400">Administrator</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-stone-100 p-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <GraduationCap className="h-8 w-8 text-indigo-700" />
              <span className="font-heading text-lg font-semibold">Admin Panel</span>
            </Link>
            <Button variant="ghost" size="icon" onClick={logout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </header>

        <div className="p-6 lg:p-8">
          {/* Welcome */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-semibold text-slate-900">
              Admin Dashboard
            </h1>
            <p className="text-slate-600 mt-1">
              Monitor and manage the Gifted Academy platform.
            </p>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="border-stone-100">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Users className="h-6 w-6 text-blue-700" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats.total_users}</p>
                      <p className="text-sm text-slate-500">Total Users</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-stone-100">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <GraduationCap className="h-6 w-6 text-purple-700" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats.total_tutors}</p>
                      <p className="text-sm text-slate-500">Tutors</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-stone-100">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                      <Clock className="h-6 w-6 text-indigo-700" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats.total_hours_owed || 0}</p>
                      <p className="text-sm text-slate-500">Hours Owed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-stone-100">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <DollarSign className="h-6 w-6 text-emerald-700" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">${stats.total_revenue?.toFixed(0) || 0}</p>
                      <p className="text-sm text-slate-500">Revenue</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Student Consent Stats */}
          {stats?.consent_stats && (
            <div className="grid sm:grid-cols-4 gap-4 mb-8">
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <UserCheck className="h-8 w-8 text-green-600" />
                    <div>
                      <p className="text-xl font-bold text-green-900">{stats.consent_stats.students_18_plus || 0}</p>
                      <p className="text-xs text-green-700">Students 18+</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <Clock className="h-8 w-8 text-amber-600" />
                    <div>
                      <p className="text-xl font-bold text-amber-900">{stats.consent_stats.pending_consent || 0}</p>
                      <p className="text-xs text-amber-700">Pending Consent</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-8 w-8 text-blue-600" />
                    <div>
                      <p className="text-xl font-bold text-blue-900">{stats.consent_stats.approved_consent || 0}</p>
                      <p className="text-xs text-blue-700">Parent Approved</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <XCircle className="h-8 w-8 text-red-600" />
                    <div>
                      <p className="text-xl font-bold text-red-900">{stats.consent_stats.denied_consent || 0}</p>
                      <p className="text-xs text-red-700">Parent Denied</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Business Metrics - Platform Income Tracking */}
          {businessMetrics && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-indigo-600" />
                Platform Income (from completed lessons)
              </h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <DollarSign className="h-8 w-8 text-blue-600" />
                      <div>
                        <p className="text-xl font-bold text-blue-900">${businessMetrics.gross_revenue?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-blue-700">Gross Revenue</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <Users className="h-8 w-8 text-orange-600" />
                      <div>
                        <p className="text-xl font-bold text-orange-900">${businessMetrics.tutor_cost?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-orange-700">Tutor Cost</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-emerald-200 bg-emerald-50">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="h-8 w-8 text-emerald-600" />
                      <div>
                        <p className="text-xl font-bold text-emerald-900">${businessMetrics.platform_income?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-emerald-700">Platform Income</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Based on {businessMetrics.total_approved_sessions || 0} approved sessions ({businessMetrics.total_approved_hours?.toFixed(1) || 0} hours)
                {businessMetrics.pending_sessions > 0 && <span className="text-amber-600 ml-2">• {businessMetrics.pending_sessions} sessions pending approval</span>}
              </p>
            </div>
          )}

          {/* Sessions Pending Approval */}
          {pendingSessions.length > 0 && (
            <Card className="border-amber-200 bg-amber-50 mb-8" data-testid="pending-sessions-section">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-amber-900 mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  {pendingSessions.length} Session{pendingSessions.length > 1 ? 's' : ''} Pending Approval
                </h3>
                <p className="text-sm text-amber-700 mb-4">
                  Approve these sessions to update Platform Income metrics. Financial data is calculated on approval.
                </p>
                <div className="space-y-3">
                  {pendingSessions.map((session) => (
                    <div 
                      key={session.log_id} 
                      className="bg-white rounded-lg p-4 border border-amber-200"
                      data-testid={`pending-session-${session.log_id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <p className="font-medium text-slate-900">
                            {session.student_name || 'Unknown Student'} - {session.tutor_name || 'Unknown Tutor'}
                          </p>
                          <p className="text-sm text-slate-600">
                            {session.hours_used} hour{session.hours_used !== 1 ? 's' : ''} on {session.session_date}
                          </p>
                          {session.lesson_topic && (
                            <p className="text-xs text-slate-500">Topic: {session.lesson_topic}</p>
                          )}
                          {session.lesson_notes_url && (
                            <a 
                              href={getLessonNotesUrl(session.lesson_notes_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
                              data-testid={`lesson-notes-${session.log_id}`}
                            >
                              <FileText className="h-3 w-3" />
                              View Lesson Notes
                            </a>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApproveSession(session.log_id, 'high_school')}
                            disabled={approvingSessionId === session.log_id}
                            data-testid={`approve-hs-${session.log_id}`}
                          >
                            {approvingSessionId === session.log_id ? 'Approving...' : 'Approve (HS)'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApproveSession(session.log_id, 'university')}
                            disabled={approvingSessionId === session.log_id}
                            data-testid={`approve-uni-${session.log_id}`}
                          >
                            {approvingSessionId === session.log_id ? 'Approving...' : 'Approve (Uni)'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pending Tutors Alert */}
          {pendingTutors.length > 0 && (
            <Card className="border-amber-200 bg-amber-50 mb-8">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900">
                      {pendingTutors.length} Tutor{pendingTutors.length > 1 ? 's' : ''} Pending Approval
                    </h3>
                    <p className="text-sm text-amber-700">
                      Review and approve new tutors to let them start teaching.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Tabs */}
          <Card className="border-stone-100">
            <CardContent className="pt-6">
              <Tabs defaultValue="tutors">
                <TabsList className="mb-4 flex-wrap">
                  <TabsTrigger value="tutors" className="relative">
                    Tutors
                    {tutorStats.pending > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                        {tutorStats.pending} pending
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="chat" className="relative">
                    Live Chat
                    {unreadChatCount > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                        {unreadChatCount}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="students">
                    Students
                  </TabsTrigger>
                  <TabsTrigger value="users">
                    All Users ({users.length})
                  </TabsTrigger>
                  <TabsTrigger value="payments" className="relative">
                    Orders
                    {(orderStats.total_awaiting_verification > 0) && (
                      <span className="ml-2 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                        {orderStats.total_awaiting_verification} to verify
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="requests" className="relative">
                    Requests
                    {requestStats.pending > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                        {requestStats.pending} new
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="notifications" className="relative">
                    <Bell className="h-4 w-4 mr-1" />
                    {unreadNotificationCount > 0 && (
                      <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                        {unreadNotificationCount}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="team">
                    <UsersRound className="h-4 w-4 mr-1" />
                    Team
                  </TabsTrigger>
                  <TabsTrigger value="tutor-hours">
                    <Clock className="h-4 w-4 mr-1" />
                    Tutor Hours
                  </TabsTrigger>
                  <TabsTrigger value="tutor-payroll">
                    <DollarSign className="h-4 w-4 mr-1" />
                    Tutor Payroll
                  </TabsTrigger>
                  <TabsTrigger value="email-center">
                    <Mail className="h-4 w-4 mr-1" />
                    Email Center
                  </TabsTrigger>
                  <TabsTrigger value="content-cms">
                    <FileText className="h-4 w-4 mr-1" />
                    Website Content
                  </TabsTrigger>
                  <TabsTrigger value="invoices">
                    <Receipt className="h-4 w-4 mr-1" />
                    Invoices
                  </TabsTrigger>
                  <TabsTrigger value="maintenance" className="text-amber-600">
                    <Wrench className="h-4 w-4 mr-1" />
                    Maintenance
                  </TabsTrigger>
                </TabsList>

                {/* Tutors Management Tab */}
                <TabsContent value="tutors">
                  <div className="space-y-4">
                    {/* Tutor Actions Bar */}
                    <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-indigo-50 rounded-xl">
                      <div className="flex items-center gap-4">
                        <Button
                          onClick={() => handleOpenTutorDialog()}
                          className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                          data-testid="add-tutor-btn"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Tutor
                        </Button>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            placeholder="Search tutors..."
                            value={searchTutors}
                            onChange={(e) => setSearchTutors(e.target.value)}
                            className="pl-9 w-[200px] rounded-full"
                            data-testid="search-tutors"
                          />
                        </div>
                        <Select value={tutorFilter} onValueChange={(v) => { setTutorFilter(v); }}>
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Filter tutors" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Tutors ({tutorStats.total})</SelectItem>
                            <SelectItem value="pending">Pending ({tutorStats.pending})</SelectItem>
                            <SelectItem value="approved">Approved ({tutorStats.approved})</SelectItem>
                            <SelectItem value="visible">Visible</SelectItem>
                            <SelectItem value="hidden">Hidden</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="outline" onClick={loadTutors} className="rounded-full">
                          Refresh
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-indigo-700">Tutor Submission Link:</span>
                        <Button variant="outline" size="sm" onClick={copySubmissionLink} className="gap-2">
                          <Copy className="h-4 w-4" />
                          Copy Link
                        </Button>
                        <a href="/become-a-tutor" target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </a>
                      </div>
                    </div>

                    {/* Tutor List */}
                    {tutors.filter(t => 
                      !searchTutors || 
                      t.name?.toLowerCase().includes(searchTutors.toLowerCase()) ||
                      t.email?.toLowerCase().includes(searchTutors.toLowerCase()) ||
                      t.subjects?.some(s => s.toLowerCase().includes(searchTutors.toLowerCase()))
                    ).length === 0 ? (
                      <div className="text-center py-12">
                        <GraduationCap className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="font-heading text-lg text-slate-900">{searchTutors ? 'No matching tutors' : 'No tutors found'}</h3>
                        <p className="text-slate-500 mb-4">{searchTutors ? 'Try a different search term.' : 'Add tutors or share the submission link.'}</p>
                        {!searchTutors && (
                          <Button onClick={() => handleOpenTutorDialog()} className="rounded-full">
                            <Plus className="h-4 w-4 mr-2" />
                            Add First Tutor
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {tutors.filter(t => 
                          !searchTutors || 
                          t.name?.toLowerCase().includes(searchTutors.toLowerCase()) ||
                          t.email?.toLowerCase().includes(searchTutors.toLowerCase()) ||
                          t.subjects?.some(s => s.toLowerCase().includes(searchTutors.toLowerCase()))
                        ).map((tutor) => (
                          <div
                            key={tutor.profile_id}
                            className={`p-4 rounded-xl border ${
                              !tutor.is_approved 
                                ? 'bg-amber-50 border-amber-200' 
                                : tutor.is_visible 
                                  ? 'bg-white border-stone-200' 
                                  : 'bg-slate-50 border-slate-200'
                            }`}
                            data-testid={`tutor-card-${tutor.profile_id}`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-4">
                                <Avatar className="h-14 w-14">
                                  <AvatarImage src={tutor.profile_photo_url} />
                                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-lg">
                                    {tutor.name?.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-semibold text-slate-900">{tutor.name}</h4>
                                    {!tutor.is_approved && (
                                      <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                                    )}
                                    {tutor.is_approved && !tutor.is_visible && (
                                      <Badge className="bg-slate-100 text-slate-600">Hidden</Badge>
                                    )}
                                    {tutor.is_approved && tutor.is_visible && (
                                      <Badge className="bg-green-100 text-green-700">Live</Badge>
                                    )}
                                    {tutor.submission_type === 'self-submitted' && (
                                      <Badge variant="outline" className="text-xs">Self-submitted</Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-600">{tutor.headline || tutor.email}</p>
                                  
                                  {/* Agreement & Document Status */}
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {tutor.agreement_signed ? (
                                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                        <FileText className="h-3 w-3 mr-1" />
                                        Agreement v{tutor.agreement_version}
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-orange-100 text-orange-700 text-xs">
                                        <AlertCircle className="h-3 w-3 mr-1" />
                                        No Agreement
                                      </Badge>
                                    )}
                                    {tutor.all_required_approved ? (
                                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                        <Shield className="h-3 w-3 mr-1" />
                                        Docs Verified
                                      </Badge>
                                    ) : tutor.all_required_uploaded ? (
                                      <Badge className="bg-blue-100 text-blue-700 text-xs">
                                        <Clock className="h-3 w-3 mr-1" />
                                        Docs Pending Review
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-orange-100 text-orange-700 text-xs">
                                        <Upload className="h-3 w-3 mr-1" />
                                        Docs Missing ({tutor.documents_count || 0}/3)
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {tutor.subjects?.slice(0, 4).map(s => (
                                      <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                                    ))}
                                    {tutor.subjects?.length > 4 && (
                                      <Badge variant="outline" className="text-xs">+{tutor.subjects.length - 4}</Badge>
                                    )}
                                  </div>
                                  {tutor.city && (
                                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                                      <MapPin className="h-3 w-3" /> {tutor.city}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {!tutor.is_approved ? (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setViewingTutor(tutor);
                                        setShowViewDialog(true);
                                        loadTutorRates(tutor.profile_id || tutor.user_id);
                                        setEditingRates(false);
                                      }}
                                      title="View full profile"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700"
                                      onClick={() => handleOpenTutorReview(tutor)}
                                      title="Full Review (Documents & Agreement)"
                                    >
                                      <FileText className="h-4 w-4 mr-1" />
                                      Review
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => handleApproveTutorProfile(tutor.profile_id)}
                                      className="rounded-full bg-green-600 hover:bg-green-700"
                                    >
                                      <CheckCircle className="h-4 w-4 mr-1" />
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleDeleteTutor(tutor.profile_id)}
                                      className="rounded-full"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setViewingTutor(tutor);
                                        setShowViewDialog(true);
                                        loadTutorRates(tutor.profile_id || tutor.user_id);
                                        setEditingRates(false);
                                      }}
                                      title="View full profile"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleToggleVisibility(tutor.profile_id)}
                                      title={tutor.is_visible ? 'Hide from public' : 'Show on public page'}
                                    >
                                      {tutor.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4 text-green-600" />}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleOpenTutorDialog(tutor)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleDeleteTutor(tutor.profile_id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="pending">
                  {pendingTutors.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
                      <h3 className="font-heading text-lg text-slate-900">All caught up!</h3>
                      <p className="text-slate-500">No tutors pending approval.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingTutors.map((tutor) => (
                        <div
                          key={tutor.user_id}
                          className="flex items-center justify-between p-4 bg-stone-50 rounded-xl"
                        >
                          <div className="flex items-center gap-4">
                            <Avatar className="h-12 w-12">
                              <AvatarImage src={tutor.picture} />
                              <AvatarFallback className="bg-purple-100 text-purple-700">
                                {tutor.name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <h4 className="font-semibold text-slate-900">{tutor.name}</h4>
                              <p className="text-sm text-slate-500">{tutor.email}</p>
                              {tutor.profile && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {tutor.profile.subjects?.slice(0, 3).map(s => (
                                    <Badge key={s} variant="secondary" className="text-xs">
                                      {s}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              className="rounded-full bg-green-600 hover:bg-green-700 gap-1"
                              onClick={() => handleApproveTutor(tutor.user_id)}
                              data-testid={`approve-${tutor.user_id}`}
                            >
                              <CheckCircle className="h-4 w-4" />
                              Approve
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="chat">
                  <div className="flex h-[600px] border rounded-xl overflow-hidden bg-white" data-testid="chat-section">
                    {/* Conversations List */}
                    <div className="w-1/3 border-r bg-slate-50 flex flex-col">
                      {/* Header */}
                      <div className="p-4 border-b bg-white">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <MessageCircle className="h-5 w-5 text-indigo-700" />
                            <h3 className="font-semibold text-slate-900">
                              Conversations ({chatConversations.length})
                            </h3>
                          </div>
                          {unreadChatCount > 0 && (
                            <Badge className="bg-red-100 text-red-700">{unreadChatCount}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input
                              placeholder="Search..."
                              value={searchChat}
                              onChange={(e) => setSearchChat(e.target.value)}
                              className="pl-9 h-8 rounded-full text-sm"
                              data-testid="search-chat"
                            />
                          </div>
                          {unreadChatCount > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={handleMarkAllRead}
                              className="h-8 px-2"
                              title="Mark all as read"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Conversations List */}
                      <div className="flex-1 overflow-y-auto">
                        {chatConversations.filter(conv => 
                          !searchChat || 
                          conv.sender_name?.toLowerCase().includes(searchChat.toLowerCase()) ||
                          conv.sender_email?.toLowerCase().includes(searchChat.toLowerCase()) ||
                          conv.latest_message?.toLowerCase().includes(searchChat.toLowerCase())
                        ).length === 0 ? (
                          <div className="text-center py-8 px-4">
                            <MessageCircle className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500 text-sm">
                              {searchChat ? 'No matching conversations' : 'No conversations yet'}
                            </p>
                          </div>
                        ) : (
                          chatConversations.filter(conv => 
                            !searchChat || 
                            conv.sender_name?.toLowerCase().includes(searchChat.toLowerCase()) ||
                            conv.sender_email?.toLowerCase().includes(searchChat.toLowerCase()) ||
                            conv.latest_message?.toLowerCase().includes(searchChat.toLowerCase())
                          ).map((conv) => (
                            <div
                              key={conv.conversation_id}
                              onClick={() => handleSelectConversation(conv)}
                              className={`p-3 border-b cursor-pointer transition-colors ${
                                selectedConversation?.conversation_id === conv.conversation_id
                                  ? 'bg-indigo-50 border-l-4 border-l-indigo-600'
                                  : conv.unread_count > 0
                                    ? 'bg-amber-50 hover:bg-amber-100'
                                    : 'hover:bg-white'
                              }`}
                              data-testid={`conversation-${conv.conversation_id}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-slate-900 truncate">
                                      {conv.sender_name || 'Visitor'}
                                    </span>
                                    {conv.is_guest ? (
                                      <Badge className="bg-slate-100 text-slate-600 text-xs shrink-0">Guest</Badge>
                                    ) : (
                                      <Badge className="bg-blue-100 text-blue-700 text-xs shrink-0">User</Badge>
                                    )}
                                  </div>
                                  {conv.sender_email && (
                                    <p className="text-xs text-slate-500 truncate mb-1">{conv.sender_email}</p>
                                  )}
                                  <p className="text-sm text-slate-600 line-clamp-1">
                                    {conv.latest_message}
                                  </p>
                                </div>
                                <div className="flex flex-col items-end gap-1 shrink-0">
                                  {conv.unread_count > 0 && (
                                    <Badge className="bg-red-500 text-white text-xs">{conv.unread_count}</Badge>
                                  )}
                                  <span className="text-xs text-slate-400">
                                    {conv.latest_timestamp ? format(new Date(conv.latest_timestamp), 'MMM d') : ''}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    
                    {/* Chat Detail / Messages */}
                    <div className="flex-1 flex flex-col bg-white">
                      {selectedConversation ? (
                        <>
                          {/* Conversation Header */}
                          <div className="p-4 border-b bg-slate-50">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-slate-900">
                                    {selectedConversation.sender_name || 'Visitor'}
                                  </h3>
                                  {selectedConversation.is_guest ? (
                                    <Badge className="bg-slate-100 text-slate-600 text-xs">Guest</Badge>
                                  ) : (
                                    <Badge className="bg-blue-100 text-blue-700 text-xs">Registered User</Badge>
                                  )}
                                </div>
                                {selectedConversation.sender_email && (
                                  <p className="text-sm text-slate-500">{selectedConversation.sender_email}</p>
                                )}
                              </div>
                              <div className="text-sm text-slate-500">
                                {selectedConversation.message_count} messages
                              </div>
                            </div>
                          </div>
                          
                          {/* Messages */}
                          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="conversation-messages">
                            {selectedConversation.messages.map((msg) => (
                              <div
                                key={msg.message_id}
                                className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                              >
                                <div className={`max-w-[70%] p-3 rounded-2xl ${
                                  msg.sender_type === 'admin'
                                    ? 'bg-indigo-600 text-white rounded-br-md'
                                    : 'bg-slate-100 text-slate-900 rounded-bl-md'
                                }`}>
                                  <p className="text-sm">{msg.message}</p>
                                  <p className={`text-xs mt-1 ${
                                    msg.sender_type === 'admin' ? 'text-indigo-200' : 'text-slate-400'
                                  }`}>
                                    {msg.created_at ? format(new Date(msg.created_at), 'MMM d, h:mm a') : ''}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Reply Form */}
                          <form onSubmit={handleSendReply} className="p-4 border-t bg-slate-50">
                            <div className="flex items-end gap-2">
                              <Textarea
                                placeholder="Type your reply..."
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                rows={2}
                                className="flex-1 resize-none rounded-xl"
                                data-testid="chat-reply-input"
                              />
                              <Button 
                                type="submit" 
                                disabled={sendingReply || !replyMessage.trim()}
                                className="rounded-full bg-indigo-700 hover:bg-indigo-800 h-10 w-10 p-0"
                                data-testid="send-reply-btn"
                              >
                                {sendingReply ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Send className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </form>
                        </>
                      ) : (
                        <div className="flex-1 flex items-center justify-center">
                          <div className="text-center">
                            <MessageCircle className="h-16 w-16 text-slate-200 mx-auto mb-4" />
                            <h3 className="font-semibold text-slate-900 mb-1">Select a conversation</h3>
                            <p className="text-slate-500 text-sm">
                              Choose a conversation from the list to view messages
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="students">
                  <div className="space-y-4" data-testid="students-tab-content">
                    {/* Search and Archive Toggle */}
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Search students..."
                          value={searchStudents}
                          onChange={(e) => setSearchStudents(e.target.value)}
                          className="pl-9 rounded-full"
                          data-testid="search-students"
                        />
                      </div>
                      <Button
                        variant={showArchivedUsers ? "default" : "outline"}
                        size="sm"
                        onClick={() => setShowArchivedUsers(!showArchivedUsers)}
                        className="gap-2"
                        data-testid="toggle-archived-users"
                      >
                        <Archive className="h-4 w-4" />
                        {showArchivedUsers ? 'Hide Archived' : 'View Archived'}
                        {archivedUsers.filter(u => u.role === 'student').length > 0 && (
                          <Badge variant="secondary" className="ml-1">
                            {archivedUsers.filter(u => u.role === 'student').length}
                          </Badge>
                        )}
                      </Button>
                    </div>

                    {/* Archived Users Section */}
                    {showArchivedUsers && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                        <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
                          <Archive className="h-4 w-4" />
                          Archived Students
                        </h4>
                        {archivedUsers.filter(u => u.role === 'student').length === 0 ? (
                          <p className="text-amber-700 text-sm">No archived students</p>
                        ) : (
                          <div className="space-y-2">
                            {archivedUsers.filter(u => u.role === 'student').map(user => (
                              <div 
                                key={user.user_id}
                                className="bg-white rounded-lg p-3 flex items-center justify-between border border-amber-100"
                                data-testid={`archived-user-${user.user_id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-10 w-10">
                                    <AvatarImage src={user.picture} />
                                    <AvatarFallback className="bg-amber-100 text-amber-700">
                                      {user.name?.charAt(0) || 'U'}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="font-medium text-slate-900">{user.name || 'Unnamed User'}</p>
                                    <p className="text-sm text-slate-500">{user.email}</p>
                                    {user.archived_at && (
                                      <p className="text-xs text-amber-600">
                                        Archived: {new Date(user.archived_at).toLocaleDateString()}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUnarchiveUser(user)}
                                  disabled={restoringUser}
                                  className="gap-1 text-green-600 border-green-200 hover:bg-green-50"
                                  data-testid={`restore-user-${user.user_id}`}
                                >
                                  <ArchiveRestore className="h-4 w-4" />
                                  {restoringUser ? 'Restoring...' : 'Restore'}
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Active Students List */}
                    {users.filter(u => u.role === 'student').filter(s =>
                      !searchStudents ||
                      s.name?.toLowerCase().includes(searchStudents.toLowerCase()) ||
                      s.email?.toLowerCase().includes(searchStudents.toLowerCase())
                    ).length === 0 ? (
                      <div className="text-center py-12">
                        <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="font-heading text-lg text-slate-900">{searchStudents ? 'No matching students' : 'No students yet'}</h3>
                        <p className="text-slate-500">{searchStudents ? 'Try a different search term.' : 'Student accounts will appear here.'}</p>
                      </div>
                    ) : (
                      users.filter(u => u.role === 'student').filter(s =>
                        !searchStudents ||
                        s.name?.toLowerCase().includes(searchStudents.toLowerCase()) ||
                        s.email?.toLowerCase().includes(searchStudents.toLowerCase())
                      ).map((student) => {
                        const isExpanded = expandedStudents[student.user_id];
                        const upcomingSessions = getStudentUpcomingBookings(student.user_id);
                        
                        return (
                          <div
                            key={student.user_id}
                            className="bg-stone-50 rounded-xl overflow-hidden"
                            data-testid={`student-card-${student.user_id}`}
                          >
                            {/* Main Student Row */}
                            <div 
                              className="flex items-center justify-between p-4 cursor-pointer hover:bg-stone-100 transition-colors"
                              onClick={() => toggleStudentExpanded(student.user_id)}
                            >
                              <div className="flex items-center gap-4">
                                <Avatar className="h-12 w-12">
                                  <AvatarImage src={student.picture} />
                                  <AvatarFallback className="bg-blue-100 text-blue-700">
                                    {student.name?.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <h4 className="font-semibold text-slate-900">{student.name}</h4>
                                  <p className="text-sm text-slate-500">{student.email}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    {student.age && (
                                      <Badge variant="outline" className="text-xs">
                                        Age: {student.age}
                                      </Badge>
                                    )}
                                    {student.student_grade && (
                                      <Badge variant="secondary" className="text-xs">
                                        {getGradeLabel(student.student_grade)}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2 flex-wrap justify-end">
                                  {/* Email Verification Status */}
                                  {student.email_verified === false ? (
                                    <Badge className="bg-orange-100 text-orange-700">
                                      <Mail className="h-3 w-3 mr-1" />
                                      Email Not Verified
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-emerald-100 text-emerald-700">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Email Verified
                                    </Badge>
                                  )}
                                  {/* Consent Status */}
                                  {student.consent_status === 'not_required' && (
                                    <Badge className="bg-green-100 text-green-700">
                                      <UserCheck className="h-3 w-3 mr-1" />
                                      18+ (No consent required)
                                    </Badge>
                                  )}
                                  {student.consent_status === 'pending' && (
                                    <Badge className="bg-amber-100 text-amber-700">
                                      <Clock className="h-3 w-3 mr-1" />
                                      Consent Required - Pending
                                    </Badge>
                                  )}
                                  {student.consent_status === 'approved' && (
                                    <Badge className="bg-blue-100 text-blue-700">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Consent Required - Approved
                                    </Badge>
                                  )}
                                  {student.consent_status === 'denied' && (
                                    <Badge className="bg-red-100 text-red-700">
                                      <XCircle className="h-3 w-3 mr-1" />
                                      Consent Denied
                                    </Badge>
                                  )}
                                </div>
                                {isExpanded ? (
                                  <ChevronUp className="h-5 w-5 text-slate-400" />
                                ) : (
                                  <ChevronDown className="h-5 w-5 text-slate-400" />
                                )}
                              </div>
                            </div>
                            
                            {/* Expanded Details */}
                            {isExpanded && (
                              <div className="border-t border-stone-200 p-4 bg-white">
                                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                                  {/* Age & Consent Info */}
                                  <div className="space-y-3">
                                    <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                      <Users className="h-4 w-4" />
                                      Age & Consent
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">Age:</span>
                                        <span className="font-medium" data-testid={`student-age-${student.user_id}`}>
                                          {student.age || 'Not provided'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">Email Verified:</span>
                                        <span className={`font-medium ${student.email_verified === false ? 'text-orange-600' : 'text-green-600'}`} data-testid={`student-email-verified-${student.user_id}`}>
                                          {student.email_verified === false ? 'No' : 'Yes'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">Consent Required:</span>
                                        <span className="font-medium" data-testid={`student-consent-req-${student.user_id}`}>
                                          {student.consent_status === 'not_required' ? 'No' : 'Yes'}
                                        </span>
                                      </div>
                                      {student.parent_name && (
                                        <div className="flex justify-between">
                                          <span className="text-slate-500">Parent/Guardian:</span>
                                          <span className="font-medium" data-testid={`student-parent-${student.user_id}`}>
                                            {student.parent_name}
                                          </span>
                                        </div>
                                      )}
                                      {student.parent_email && (
                                        <div className="flex justify-between">
                                          <span className="text-slate-500">Parent Email:</span>
                                          <span className="font-medium text-indigo-600" data-testid={`student-parent-email-${student.user_id}`}>
                                            <a href={`mailto:${student.parent_email}`} className="hover:underline">
                                              {student.parent_email}
                                            </a>
                                          </span>
                                        </div>
                                      )}
                                      {student.parent_phone && (
                                        <div className="flex justify-between">
                                          <span className="text-slate-500">Parent Phone:</span>
                                          <span className="font-medium text-indigo-600" data-testid={`student-parent-phone-${student.user_id}`}>
                                            <a href={`tel:${student.parent_phone}`} className="hover:underline">
                                              {student.parent_phone}
                                            </a>
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Agreement Status */}
                                  <div className="space-y-3">
                                    <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                      <FileText className="h-4 w-4" />
                                      Agreement Status
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">Agreement Signed:</span>
                                        <span className={`font-medium ${student.agreement_signed ? 'text-green-600' : 'text-amber-600'}`}>
                                          {student.agreement_signed ? 'Yes' : 'Pending'}
                                        </span>
                                      </div>
                                      {student.agreement_signed && (
                                        <>
                                          <div className="flex justify-between">
                                            <span className="text-slate-500">Version:</span>
                                            <span className="font-medium">{student.agreement_version || '1.0'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-slate-500">Signed:</span>
                                            <span className="font-medium">
                                              {student.agreement_signed_at 
                                                ? new Date(student.agreement_signed_at).toLocaleDateString() 
                                                : 'N/A'}
                                            </span>
                                          </div>
                                        </>
                                      )}
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full mt-2"
                                        onClick={async () => {
                                          try {
                                            const resp = await api.get(`/admin/students/${student.user_id}/agreement`);
                                            if (resp.data.signed) {
                                              toast.success(
                                                <div>
                                                  <p><strong>Agreement Details</strong></p>
                                                  <p>Type: {resp.data.agreement_type === 'parent_guardian_consent' ? 'Parent Consent' : 'Adult Agreement'}</p>
                                                  <p>Signer: {resp.data.signer_full_name}</p>
                                                  <p>Email: {resp.data.signer_email}</p>
                                                  {resp.data.signer_phone && <p>Phone: {resp.data.signer_phone}</p>}
                                                  <p>Email Sent: {resp.data.email_sent ? 'Yes' : 'No'}</p>
                                                </div>,
                                                { duration: 10000 }
                                              );
                                            } else {
                                              toast.info('Agreement not yet signed');
                                            }
                                          } catch (error) {
                                            toast.error('Failed to load agreement details');
                                          }
                                        }}
                                      >
                                        <Eye className="h-3 w-3 mr-1" />
                                        View Details
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* School Info */}
                                  <div className="space-y-3">
                                    <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                      <School className="h-4 w-4" />
                                      Education
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">Grade:</span>
                                        <span className="font-medium" data-testid={`student-grade-${student.user_id}`}>
                                          {getGradeLabel(student.student_grade)}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">School:</span>
                                        <span className="font-medium truncate max-w-[150px]" data-testid={`student-school-${student.user_id}`}>
                                          {student.school_name || 'Not set'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Courses */}
                                  <div className="space-y-3">
                                    <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                      <BookOpen className="h-4 w-4" />
                                      Current Courses
                                    </h5>
                                    <div className="flex flex-wrap gap-1" data-testid={`student-courses-${student.user_id}`}>
                                      {student.current_courses?.length > 0 ? (
                                        student.current_courses.map((course, idx) => (
                                          <Badge key={idx} variant="secondary" className="text-xs">
                                            {course}
                                          </Badge>
                                        ))
                                      ) : (
                                        <span className="text-sm text-slate-400">No courses set</span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Upcoming Sessions */}
                                  <div className="space-y-3">
                                    <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                      <Calendar className="h-4 w-4" />
                                      Upcoming Sessions
                                    </h5>
                                    {upcomingSessions.length > 0 ? (
                                      <div className="space-y-2" data-testid={`student-sessions-${student.user_id}`}>
                                        {upcomingSessions.map((booking) => (
                                          <div 
                                            key={booking.booking_id}
                                            className="text-sm p-2 bg-indigo-50 rounded-lg"
                                          >
                                            <p className="font-medium text-indigo-900">{booking.subject}</p>
                                            <p className="text-indigo-700 text-xs">
                                              {format(new Date(booking.date), 'MMM d')} at {booking.time_slot}
                                            </p>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-sm text-slate-400">No upcoming sessions</span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Assigned Tutor Section */}
                                <div className="mt-6 pt-4 border-t border-stone-200">
                                  <div className="flex items-center justify-between mb-3">
                                    <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                      <Users className="h-4 w-4" />
                                      Assigned Tutor
                                    </h5>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenAssignTutorModal(student);
                                      }}
                                      className="rounded-full text-xs"
                                      data-testid={`assign-tutor-btn-${student.user_id}`}
                                    >
                                      {student.assigned_tutor_id ? (
                                        <>
                                          <RefreshCw className="h-3 w-3 mr-1" />
                                          Change Tutor
                                        </>
                                      ) : (
                                        <>
                                          <UserPlus className="h-3 w-3 mr-1" />
                                          Assign Tutor
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                  
                                  {student.assigned_tutor_id ? (
                                    <div className="bg-purple-50 rounded-lg p-3">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="font-medium text-purple-800">{student.assigned_tutor_name}</p>
                                          <p className="text-xs text-purple-600">
                                            Assigned {student.tutor_assigned_at ? format(new Date(student.tutor_assigned_at), 'MMM d, yyyy') : ''}
                                          </p>
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleUnassignTutor(student);
                                          }}
                                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                          data-testid={`unassign-tutor-btn-${student.user_id}`}
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="bg-slate-100 rounded-lg p-3 text-center">
                                      <p className="text-sm text-slate-500">No tutor assigned yet</p>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Tutor Hours Management Section */}
                                <div className="mt-6 pt-4 border-t border-stone-200">
                                  <div className="flex items-center justify-between mb-4">
                                    <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                      <Clock className="h-4 w-4" />
                                      Tutoring Hours Management
                                    </h5>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenHoursModal(student);
                                      }}
                                      className="rounded-full text-xs"
                                      data-testid={`manage-hours-btn-${student.user_id}`}
                                    >
                                      <Settings className="h-3 w-3 mr-1" />
                                      Manage Hours
                                    </Button>
                                  </div>
                                  
                                  <div className="grid grid-cols-3 gap-4 mb-4">
                                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                                      <p className="text-xs text-blue-600 mb-1">Purchased</p>
                                      <p className="text-xl font-bold text-blue-700">
                                        {student.total_hours_purchased || 0}
                                      </p>
                                    </div>
                                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                                      <p className="text-xs text-amber-600 mb-1">Used</p>
                                      <p className="text-xl font-bold text-amber-700">
                                        {student.total_hours_used || 0}
                                      </p>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-3 text-center">
                                      <p className="text-xs text-green-600 mb-1">Remaining</p>
                                      <p className="text-xl font-bold text-green-700">
                                        {(student.total_hours_purchased || 0) - (student.total_hours_used || 0)}
                                      </p>
                                    </div>
                                  </div>
                                  
                                  {/* Tutor Session Submission Link */}
                                  <div className="bg-slate-50 rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <p className="text-xs font-medium text-slate-600">Tutor Session Submission Link</p>
                                      {student.assignment_token && (
                                        <Badge className="bg-green-100 text-green-700">
                                          Active
                                        </Badge>
                                      )}
                                    </div>
                                    {student.assignment_token ? (
                                      <div className="flex items-center gap-2">
                                        <Input
                                          value={`${window.location.origin}/tutor-submit/${student.assignment_token}`}
                                          readOnly
                                          className="text-xs h-8 bg-white"
                                          data-testid={`tutor-link-input-${student.user_id}`}
                                        />
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigator.clipboard.writeText(`${window.location.origin}/tutor-submit/${student.assignment_token}`);
                                            toast.success('Link copied to clipboard!');
                                          }}
                                          className="h-8 px-2"
                                          data-testid={`copy-tutor-link-${student.user_id}`}
                                        >
                                          <Copy className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <p className="text-xs text-slate-500 italic">
                                        Assign a tutor to generate the submission link
                                      </p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Lesson History Section */}
                                <div className="mt-6 pt-4 border-t border-stone-200">
                                  <div 
                                    className="flex items-center justify-between cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleLessonHistory(student.user_id);
                                    }}
                                  >
                                    <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                      <History className="h-4 w-4" />
                                      Lesson History
                                    </h5>
                                    <div className="flex items-center gap-2">
                                      {lessonHistoryData[student.user_id]?.length > 0 && (
                                        <Badge variant="secondary" className="text-xs">
                                          {lessonHistoryData[student.user_id].length} lesson{lessonHistoryData[student.user_id].length !== 1 ? 's' : ''}
                                        </Badge>
                                      )}
                                      {lessonHistoryExpanded[student.user_id] ? (
                                        <ChevronUp className="h-4 w-4 text-slate-400" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4 text-slate-400" />
                                      )}
                                    </div>
                                  </div>
                                  
                                  {lessonHistoryExpanded[student.user_id] && (
                                    <div className="mt-4">
                                      {loadingLessonHistory[student.user_id] ? (
                                        <div className="flex items-center justify-center py-6">
                                          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                                          <span className="ml-2 text-sm text-slate-500">Loading lesson history...</span>
                                        </div>
                                      ) : lessonHistoryData[student.user_id]?.length > 0 ? (
                                        <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                                          {lessonHistoryData[student.user_id].map((lesson, idx) => {
                                            const notesStatus = getLessonNotesStatus(lesson);
                                            const notesUrl = getLessonNotesUrl(lesson.lesson_notes_url);
                                            
                                            return (
                                              <div 
                                                key={lesson.log_id || idx}
                                                className="bg-slate-50 rounded-lg p-4 border border-slate-100"
                                                data-testid={`lesson-entry-${lesson.log_id || idx}`}
                                              >
                                                {/* Header: Date, Time, Tutor */}
                                                <div className="flex items-start justify-between mb-2">
                                                  <div>
                                                    <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                                                      <Calendar className="h-3.5 w-3.5 text-slate-500" />
                                                      {lesson.session_date || 'No date'}
                                                      {lesson.session_time && (
                                                        <span className="text-slate-500">at {lesson.session_time}</span>
                                                      )}
                                                    </div>
                                                    <p className="text-xs text-slate-500 mt-0.5">
                                                      Tutor: <span className="font-medium text-slate-700">{lesson.tutor_name || 'Unknown'}</span>
                                                    </p>
                                                  </div>
                                                  <Badge className="bg-blue-100 text-blue-700 text-xs">
                                                    {lesson.hours_used || 0} hr{lesson.hours_used !== 1 ? 's' : ''}
                                                  </Badge>
                                                </div>
                                                
                                                {/* Lesson Topic */}
                                                {lesson.lesson_topic && (
                                                  <div className="mb-2">
                                                    <p className="text-xs text-slate-500">Topic:</p>
                                                    <p className="text-sm text-slate-700">{lesson.lesson_topic}</p>
                                                  </div>
                                                )}
                                                
                                                {/* Additional Notes */}
                                                {lesson.notes && (
                                                  <div className="mb-2">
                                                    <p className="text-xs text-slate-500">Notes:</p>
                                                    <p className="text-sm text-slate-600 italic">{lesson.notes}</p>
                                                  </div>
                                                )}
                                                
                                                {/* Lesson Notes File */}
                                                <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-200">
                                                  <div className="flex items-center gap-2 text-xs">
                                                    <FileText className="h-3.5 w-3.5 text-slate-400" />
                                                    <span className="text-slate-500">Lesson Notes:</span>
                                                    {notesStatus.available ? (
                                                      <Badge className="bg-green-100 text-green-700 text-xs">
                                                        {notesStatus.label}
                                                      </Badge>
                                                    ) : notesStatus.expired ? (
                                                      <Badge className="bg-amber-100 text-amber-700 text-xs">
                                                        {notesStatus.label}
                                                      </Badge>
                                                    ) : (
                                                      <Badge variant="outline" className="text-slate-500 text-xs">
                                                        {notesStatus.label}
                                                      </Badge>
                                                    )}
                                                  </div>
                                                  {notesStatus.available && notesUrl && (
                                                    <a
                                                      href={notesUrl}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      onClick={(e) => e.stopPropagation()}
                                                      className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                                                    >
                                                      <ExternalLink className="h-3 w-3" />
                                                      View/Download
                                                    </a>
                                                  )}
                                                </div>
                                                
                                                {/* Logged Timestamp */}
                                                {lesson.created_at && (
                                                  <p className="text-xs text-slate-400 mt-2">
                                                    Logged: {new Date(lesson.created_at).toLocaleString()}
                                                  </p>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      ) : (
                                        <div className="text-center py-6 text-sm text-slate-500">
                                          No lesson history recorded yet
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Created Date and Archive Action */}
                                <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between">
                                  <span className="text-xs text-slate-400">
                                    Registered: {student.created_at ? format(new Date(student.created_at), 'MMM d, yyyy') : 'N/A'}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleArchiveUser(student);
                                    }}
                                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 text-xs"
                                    data-testid={`archive-user-btn-${student.user_id}`}
                                  >
                                    <Archive className="h-3 w-3 mr-1" />
                                    Archive User
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="users">
                  <div className="space-y-4">
                    {/* Search */}
                    <div className="relative max-w-xs">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search users..."
                        value={searchUsers}
                        onChange={(e) => setSearchUsers(e.target.value)}
                        className="pl-9 rounded-full"
                        data-testid="search-users"
                      />
                    </div>

                    {users.filter(u =>
                      !searchUsers ||
                      u.name?.toLowerCase().includes(searchUsers.toLowerCase()) ||
                      u.email?.toLowerCase().includes(searchUsers.toLowerCase()) ||
                      u.role?.toLowerCase().includes(searchUsers.toLowerCase())
                    ).map((u) => (
                      <div
                        key={u.user_id}
                        className="flex items-center justify-between p-4 bg-stone-50 rounded-xl"
                      >
                        <div className="flex items-center gap-4">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={u.picture} />
                            <AvatarFallback className="bg-slate-200">
                              {u.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h4 className="font-semibold text-slate-900">{u.name}</h4>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={roleColors[u.role]}>
                            {u.role}
                          </Badge>
                          {u.role === 'tutor' && (
                            <Badge className={u.is_approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                              {u.is_approved ? 'Approved' : 'Pending'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                    {users.filter(u =>
                      !searchUsers ||
                      u.name?.toLowerCase().includes(searchUsers.toLowerCase()) ||
                      u.email?.toLowerCase().includes(searchUsers.toLowerCase()) ||
                      u.role?.toLowerCase().includes(searchUsers.toLowerCase())
                    ).length === 0 && (
                      <div className="text-center py-12">
                        <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="font-heading text-lg text-slate-900">No matching users</h3>
                        <p className="text-slate-500">Try a different search term.</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="bookings">
                  <div className="space-y-4">
                    {/* Search */}
                    <div className="relative max-w-xs">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search bookings..."
                        value={searchBookings}
                        onChange={(e) => setSearchBookings(e.target.value)}
                        className="pl-9 rounded-full"
                        data-testid="search-bookings"
                      />
                    </div>

                    {bookings.filter(b =>
                      !searchBookings ||
                      b.subject?.toLowerCase().includes(searchBookings.toLowerCase()) ||
                      b.student_name?.toLowerCase().includes(searchBookings.toLowerCase()) ||
                      b.tutor_name?.toLowerCase().includes(searchBookings.toLowerCase())
                    ).length === 0 ? (
                      <div className="text-center py-12">
                        <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="font-heading text-lg text-slate-900">{searchBookings ? 'No matching bookings' : 'No bookings yet'}</h3>
                        <p className="text-slate-500">{searchBookings ? 'Try a different search term.' : 'Bookings will appear here.'}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {bookings.filter(b =>
                          !searchBookings ||
                          b.subject?.toLowerCase().includes(searchBookings.toLowerCase()) ||
                          b.student_name?.toLowerCase().includes(searchBookings.toLowerCase()) ||
                          b.tutor_name?.toLowerCase().includes(searchBookings.toLowerCase())
                        ).map((booking) => (
                        <div
                          key={booking.booking_id}
                          className="flex items-center justify-between p-4 bg-stone-50 rounded-xl"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-900">{booking.subject}</h4>
                              <Badge className={statusColors[booking.status]}>
                                {booking.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500">
                              {booking.student_name} → {booking.tutor_name}
                            </p>
                            <p className="text-sm text-slate-400">
                              {format(new Date(booking.date), 'MMM d, yyyy')} at {booking.time_slot}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-slate-900">${booking.amount?.toFixed(2)}</p>
                            <Badge className={booking.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                              {booking.payment_status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                </TabsContent>

                {/* Payments Tab */}
                <TabsContent value="payments">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading text-lg font-semibold">E-Transfer Orders</h3>
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            placeholder="Search orders..."
                            value={searchOrders}
                            onChange={(e) => setSearchOrders(e.target.value)}
                            className="pl-9 w-[180px] rounded-full h-9"
                            data-testid="search-orders"
                          />
                        </div>
                        <Button variant="outline" size="sm" onClick={loadOrders}>
                          Refresh
                        </Button>
                      </div>
                    </div>

                    {/* Order Stats */}
                    <div className="grid grid-cols-4 gap-4 p-4 bg-stone-50 rounded-xl">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">${orderStats.total_revenue?.toLocaleString() || 0}</p>
                        <p className="text-xs text-slate-500">Total Revenue</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-slate-900">{orderStats.total_paid || 0}</p>
                        <p className="text-xs text-slate-500">Paid</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{orderStats.total_awaiting_verification || 0}</p>
                        <p className="text-xs text-slate-500">To Verify</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-amber-600">{orderStats.total_pending || 0}</p>
                        <p className="text-xs text-slate-500">Pending</p>
                      </div>
                    </div>

                    {/* Order Filters */}
                    <div className="flex gap-2 flex-wrap">
                      {['all', 'sent', 'pending', 'paid'].map((filter) => (
                        <Button
                          key={filter}
                          variant={orderFilter === filter ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            setOrderFilter(filter);
                            loadOrders();
                          }}
                          className={`rounded-full ${filter === 'sent' ? 'border-blue-300' : ''}`}
                        >
                          {filter === 'sent' ? 'To Verify' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                          {filter === 'sent' && orderStats.total_awaiting_verification > 0 && (
                            <span className="ml-1 px-1.5 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                              {orderStats.total_awaiting_verification}
                            </span>
                          )}
                        </Button>
                      ))}
                    </div>

                    {/* Order List */}
                    {loadingOrders ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-700" />
                      </div>
                    ) : orders.filter(o =>
                        !searchOrders ||
                        o.student_name?.toLowerCase().includes(searchOrders.toLowerCase()) ||
                        o.student_email?.toLowerCase().includes(searchOrders.toLowerCase()) ||
                        o.order_id?.toLowerCase().includes(searchOrders.toLowerCase()) ||
                        o.subjects?.some(s => s.toLowerCase().includes(searchOrders.toLowerCase()))
                      ).length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <DollarSign className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                        <p>{searchOrders ? 'No matching orders' : 'No orders yet'}</p>
                        <p className="text-sm mt-1">{searchOrders ? 'Try a different search term.' : 'Orders will appear here when students make purchases'}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {orders.filter(o =>
                          !searchOrders ||
                          o.student_name?.toLowerCase().includes(searchOrders.toLowerCase()) ||
                          o.student_email?.toLowerCase().includes(searchOrders.toLowerCase()) ||
                          o.order_id?.toLowerCase().includes(searchOrders.toLowerCase()) ||
                          o.subjects?.some(s => s.toLowerCase().includes(searchOrders.toLowerCase()))
                        ).map((order) => (
                          <div
                            key={order.order_id}
                            className={`p-4 bg-white border rounded-xl hover:shadow-md transition-shadow ${
                              order.payment_status === 'sent' ? 'border-blue-300 bg-blue-50/30' : 'border-stone-200'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  order.payment_status === 'paid' ? 'bg-green-100' : 
                                  order.payment_status === 'sent' ? 'bg-blue-100' : 'bg-amber-100'
                                }`}>
                                  <DollarSign className={`h-5 w-5 ${
                                    order.payment_status === 'paid' ? 'text-green-600' : 
                                    order.payment_status === 'sent' ? 'text-blue-600' : 'text-amber-600'
                                  }`} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-slate-900">{order.student_name}</p>
                                    {order.is_under_18 && (
                                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                                        Under 18
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-500">{order.student_email}</p>
                                  <p className="text-sm text-slate-600 mt-1">
                                    {order.package_name} • {order.subjects?.join(', ')}
                                  </p>
                                  {order.preferred_tutor_name && (
                                    <p className="text-sm text-indigo-600 mt-1">
                                      <UserPlus className="h-3 w-3 inline mr-1" />
                                      Preferred: {order.preferred_tutor_name}
                                    </p>
                                  )}
                                  <p className="text-xs text-slate-400 mt-1">
                                    Order ID: <span className="font-mono font-medium">{order.order_id}</span> • {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                                  </p>
                                  {order.etransfer_confirmed_at && (
                                    <p className="text-xs text-blue-600 mt-1">
                                      Customer confirmed sent: {new Date(order.etransfer_confirmed_at).toLocaleString()}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <p className="font-semibold text-slate-900 text-lg">${order.amount?.toFixed(2)}</p>
                                  <Badge className={
                                    order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 
                                    order.payment_status === 'sent' ? 'bg-blue-100 text-blue-700' : 
                                    'bg-amber-100 text-amber-700'
                                  }>
                                    {order.payment_status === 'paid' ? 'Paid' : 
                                     order.payment_status === 'sent' ? 'Verify E-Transfer' : 
                                     'Awaiting E-Transfer'}
                                  </Badge>
                                </div>
                                {(order.payment_status === 'pending' || order.payment_status === 'sent') && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleMarkOrderPaid(order.order_id)}
                                    className={order.payment_status === 'sent' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}
                                    data-testid={`mark-paid-${order.order_id}`}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    {order.payment_status === 'sent' ? 'Verify & Confirm' : 'Mark Paid'}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Tutoring Requests Tab */}
                <TabsContent value="requests">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="font-heading text-lg font-semibold">Tutoring Requests</h3>
                      <div className="flex gap-2 items-center">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            placeholder="Search requests..."
                            value={searchRequests}
                            onChange={(e) => setSearchRequests(e.target.value)}
                            className="pl-9 w-[180px] rounded-full h-9"
                            data-testid="search-requests"
                          />
                        </div>
                        <select
                          value={requestFilter}
                          onChange={(e) => {
                            setRequestFilter(e.target.value);
                            loadTutoringRequests();
                          }}
                          className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                        >
                          <option value="all">All Requests</option>
                          <option value="pending">Pending</option>
                          <option value="matched">Matched</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                        <Button variant="outline" size="sm" onClick={loadTutoringRequests}>
                          Refresh
                        </Button>
                      </div>
                    </div>

                    {/* Request Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="py-3">
                          <div className="text-2xl font-bold text-blue-700">{requestStats.pending}</div>
                          <div className="text-xs text-blue-600">Pending</div>
                        </CardContent>
                      </Card>
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="py-3">
                          <div className="text-2xl font-bold text-purple-700">{requestStats.matched}</div>
                          <div className="text-xs text-purple-600">Matched</div>
                        </CardContent>
                      </Card>
                      <Card className="bg-green-50 border-green-200">
                        <CardContent className="py-3">
                          <div className="text-2xl font-bold text-green-700">{requestStats.in_progress}</div>
                          <div className="text-xs text-green-600">In Progress</div>
                        </CardContent>
                      </Card>
                    </div>

                    {loadingRequests ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                      </div>
                    ) : tutoringRequests.filter(r =>
                        !searchRequests ||
                        r.student_name?.toLowerCase().includes(searchRequests.toLowerCase()) ||
                        r.student_email?.toLowerCase().includes(searchRequests.toLowerCase()) ||
                        r.subjects?.some(s => s.toLowerCase().includes(searchRequests.toLowerCase()))
                      ).length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <FileText className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                        <p>{searchRequests ? 'No matching requests' : 'No tutoring requests yet'}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {tutoringRequests.filter(r =>
                          !searchRequests ||
                          r.student_name?.toLowerCase().includes(searchRequests.toLowerCase()) ||
                          r.student_email?.toLowerCase().includes(searchRequests.toLowerCase()) ||
                          r.subjects?.some(s => s.toLowerCase().includes(searchRequests.toLowerCase()))
                        ).map((request) => (
                          <div
                            key={request.request_id}
                            className="p-4 border rounded-lg hover:bg-slate-50"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <User className="h-4 w-4 text-slate-500" />
                                  <span className="font-medium">{request.student_name}</span>
                                  <Badge variant={
                                    request.status === 'pending' ? 'default' :
                                    request.status === 'matched' ? 'secondary' :
                                    request.status === 'in_progress' ? 'outline' : 'default'
                                  }>
                                    {request.status}
                                  </Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 mb-2">
                                  <div><span className="text-slate-400">Email:</span> {request.student_email}</div>
                                  <div><span className="text-slate-400">Grade:</span> {request.grade_level}</div>
                                  <div><span className="text-slate-400">Subjects:</span> {request.subjects?.join(', ')}</div>
                                  <div><span className="text-slate-400">Schedule:</span> {request.preferred_schedule}</div>
                                </div>
                                {request.specific_goals && (
                                  <div className="text-sm mb-2">
                                    <span className="text-slate-400">Goals:</span> {request.specific_goals}
                                  </div>
                                )}
                                <div className="text-xs text-slate-400">
                                  Submitted: {request.created_at ? format(new Date(request.created_at), 'MMM d, yyyy h:mm a') : 'N/A'}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <select
                                  value={request.status}
                                  onChange={(e) => handleUpdateRequest(request.request_id, { status: e.target.value })}
                                  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                                >
                                  <option value="pending">Pending</option>
                                  <option value="matched">Matched</option>
                                  <option value="in_progress">In Progress</option>
                                  <option value="completed">Completed</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Notifications Tab */}
                <TabsContent value="notifications">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="font-heading text-lg font-semibold">Notifications</h3>
                      <div className="flex gap-2">
                        {unreadNotificationCount > 0 && (
                          <Button variant="outline" size="sm" onClick={handleMarkAllNotificationsRead}>
                            Mark All Read
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={loadNotifications}>
                          Refresh
                        </Button>
                      </div>
                    </div>

                    {notifications.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Bell className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                        <p>No notifications</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {notifications.map((notif) => (
                          <div
                            key={notif.notification_id}
                            className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                              notif.is_read ? 'bg-white' : 'bg-blue-50 border-blue-200'
                            }`}
                            onClick={() => !notif.is_read && handleMarkNotificationRead(notif.notification_id)}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`mt-1 p-2 rounded-full ${
                                notif.type === 'new_purchase' ? 'bg-green-100' :
                                notif.type === 'new_tutoring_request' ? 'bg-blue-100' : 'bg-slate-100'
                              }`}>
                                {notif.type === 'new_purchase' ? (
                                  <CreditCard className="h-4 w-4 text-green-600" />
                                ) : notif.type === 'new_tutoring_request' ? (
                                  <FileText className="h-4 w-4 text-blue-600" />
                                ) : (
                                  <Bell className="h-4 w-4 text-slate-600" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{notif.title}</span>
                                  {!notif.is_read && (
                                    <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">New</span>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600">{notif.message}</p>
                                <p className="text-xs text-slate-400 mt-1">
                                  {notif.created_at ? format(new Date(notif.created_at), 'MMM d, yyyy h:mm a') : 'N/A'}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Team Management Tab */}
                <TabsContent value="team">
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-indigo-50 rounded-xl">
                      <div>
                        <h3 className="font-semibold text-slate-900">About Page Team Members</h3>
                        <p className="text-sm text-slate-600">Manage the "Meet Our Team" section on the About page</p>
                      </div>
                      <Button
                        onClick={() => handleOpenTeamDialog()}
                        className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                        data-testid="add-team-member-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Team Member
                      </Button>
                    </div>

                    {loadingTeam ? (
                      <div className="flex justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                      </div>
                    ) : teamMembers.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <UsersRound className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No team members yet. Add your first team member!</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {teamMembers.map((member, index) => (
                          <Card key={member.member_id} className={`border ${member.is_visible ? 'border-stone-200' : 'border-orange-200 bg-orange-50'}`}>
                            <CardContent className="p-4">
                              <div className="flex items-center gap-4">
                                <div className="flex flex-col gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => moveTeamMember(index, 'up')}
                                    disabled={index === 0}
                                    className="h-6 w-6 p-0"
                                  >
                                    <ChevronUp className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => moveTeamMember(index, 'down')}
                                    disabled={index === teamMembers.length - 1}
                                    className="h-6 w-6 p-0"
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                  </Button>
                                </div>
                                
                                {member.profile_image ? (
                                  <img 
                                    src={getTeamImageUrl(member.profile_image)} 
                                    alt={member.full_name}
                                    className="w-16 h-16 rounded-full object-cover border-2 border-indigo-100"
                                  />
                                ) : (
                                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
                                    <User className="w-8 h-8 text-white" />
                                  </div>
                                )}
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-slate-900">{member.full_name}</h4>
                                    {!member.is_visible && (
                                      <Badge variant="outline" className="text-orange-600 border-orange-300">
                                        Hidden
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-indigo-600 font-medium">{member.title}</p>
                                  <p className="text-sm text-slate-500 line-clamp-2">{member.bio}</p>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleTeamVisibility(member)}
                                    title={member.is_visible ? 'Hide from website' : 'Show on website'}
                                  >
                                    {member.is_visible ? (
                                      <Eye className="h-4 w-4 text-green-600" />
                                    ) : (
                                      <EyeOff className="h-4 w-4 text-orange-600" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenTeamDialog(member)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteTeamMember(member.member_id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Tutor Hours Overview Tab */}
                <TabsContent value="tutor-hours">
                  <div className="space-y-6" data-testid="tutor-hours-overview">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">Tutor Hours Overview</h3>
                        <p className="text-sm text-slate-500">View each tutor's reported hours and student sessions</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={loadTutorHoursOverview}
                        disabled={loadingTutorHours}
                      >
                        <RefreshCw className={`h-4 w-4 mr-1 ${loadingTutorHours ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>

                    {/* Load on first view */}
                    {tutorHoursOverview.length === 0 && !loadingTutorHours && (
                      <Card className="p-8 text-center">
                        <p className="text-slate-500 mb-4">Click refresh to load tutor hours data</p>
                        <Button onClick={loadTutorHoursOverview}>
                          <Clock className="h-4 w-4 mr-2" />
                          Load Tutor Hours
                        </Button>
                      </Card>
                    )}

                    {/* Loading state */}
                    {loadingTutorHours && (
                      <Card className="p-8 text-center">
                        <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-500 mb-2" />
                        <p className="text-slate-500">Loading tutor hours...</p>
                      </Card>
                    )}

                    {/* Tutor List */}
                    {!loadingTutorHours && tutorHoursOverview.length > 0 && (
                      <div className="space-y-4">
                        {tutorHoursOverview.map((tutor) => (
                          <Card key={tutor.tutor_name || tutor.tutor_id} className="overflow-hidden" data-testid={`tutor-card-${tutor.tutor_name}`}>
                            {/* Tutor Summary Header */}
                            <div 
                              className="p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors flex items-center justify-between"
                              onClick={() => toggleTutorExpand(tutor.tutor_name)}
                              data-testid={`tutor-header-${tutor.tutor_name}`}
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                  <UserRound className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-slate-800">{tutor.tutor_name || 'Unknown Tutor'}</h4>
                                  <p className="text-sm text-slate-500">{tutor.student_count} student(s)</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-6">
                                <div className="text-right">
                                  <p className="text-sm text-slate-500">Total Hours</p>
                                  <p className="font-bold text-lg text-slate-800">{tutor.total_hours}h</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm text-slate-500">Approved</p>
                                  <p className="font-bold text-lg text-green-600">{tutor.approved_hours}h</p>
                                </div>
                                {tutor.pending_hours > 0 && (
                                  <div className="text-right">
                                    <p className="text-sm text-slate-500">Pending</p>
                                    <p className="font-bold text-lg text-amber-600">{tutor.pending_hours}h</p>
                                  </div>
                                )}
                                <div className="text-right">
                                  <p className="text-sm text-slate-500">Sessions</p>
                                  <p className="font-bold text-lg text-slate-800">{tutor.total_sessions}</p>
                                </div>
                                <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${expandedTutors[tutor.tutor_name] ? 'rotate-180' : ''}`} />
                              </div>
                            </div>

                            {/* Expanded Student Details */}
                            {expandedTutors[tutor.tutor_name] && (
                              <div className="border-t border-slate-200">
                                {tutor.students_detail?.map((student) => (
                                  <div key={student.student_name} className="border-b border-slate-100 last:border-b-0">
                                    {/* Student Summary */}
                                    <div 
                                      className="p-4 pl-8 cursor-pointer hover:bg-slate-50 flex items-center justify-between"
                                      onClick={() => toggleStudentExpand(tutor.tutor_name, student.student_name)}
                                      data-testid={`student-row-${student.student_name}`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <GraduationCap className="h-5 w-5 text-slate-400" />
                                        <div>
                                          <p className="font-medium text-slate-700">{student.student_name}</p>
                                          <p className="text-sm text-slate-500">{student.session_count} session(s)</p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-4">
                                        <span className="font-semibold text-slate-700">{student.total_hours}h</span>
                                        <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${expandedTutorStudents[`${tutor.tutor_name}-${student.student_name}`] ? 'rotate-90' : ''}`} />
                                      </div>
                                    </div>

                                    {/* Session Details */}
                                    {expandedTutorStudents[`${tutor.tutor_name}-${student.student_name}`] && (
                                      <div className="bg-slate-50 p-4 pl-12">
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="text-left text-slate-500">
                                              <th className="pb-2">Date</th>
                                              <th className="pb-2">Time</th>
                                              <th className="pb-2">Hours</th>
                                              <th className="pb-2">Topic</th>
                                              <th className="pb-2">Status</th>
                                              <th className="pb-2">Notes</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {student.sessions?.map((session, idx) => (
                                              <tr key={session.log_id || idx} className="border-t border-slate-200">
                                                <td className="py-2">{session.session_date || '-'}</td>
                                                <td className="py-2">{session.session_time || '-'}</td>
                                                <td className="py-2 font-medium">{session.hours_used}h</td>
                                                <td className="py-2">{session.lesson_topic || '-'}</td>
                                                <td className="py-2">
                                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                    session.status === 'approved' 
                                                      ? 'bg-green-100 text-green-700' 
                                                      : 'bg-amber-100 text-amber-700'
                                                  }`}>
                                                    {session.status || 'pending'}
                                                  </span>
                                                </td>
                                                <td className="py-2">
                                                  {session.lesson_notes_url ? (
                                                    <a 
                                                      href={session.lesson_notes_url} 
                                                      target="_blank" 
                                                      rel="noopener noreferrer"
                                                      className="text-blue-600 hover:underline flex items-center gap-1"
                                                    >
                                                      <FileText className="h-3 w-3" />
                                                      View
                                                    </a>
                                                  ) : session.notes ? (
                                                    <span className="text-slate-500 truncate block max-w-[150px]" title={session.notes}>
                                                      {session.notes.substring(0, 30)}{session.notes.length > 30 ? '...' : ''}
                                                    </span>
                                                  ) : (
                                                    <span className="text-slate-400">-</span>
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Tutor Payroll Tab */}
                <TabsContent value="tutor-payroll">
                  <div className="space-y-6" data-testid="tutor-payroll-tab">
                    {/* Header with filters */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">Tutor Payroll</h3>
                        <p className="text-sm text-slate-500">Track and manage tutor payments</p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm"
                          value={payrollPeriod}
                          onChange={(e) => setPayrollPeriod(e.target.value)}
                          data-testid="payroll-period-select"
                        >
                          <option value="all">All Time</option>
                          <option value="this_week">This Week</option>
                          <option value="this_month">This Month</option>
                          <option value="this_year">This Year</option>
                          <option value="custom">Custom Range</option>
                        </select>
                        {payrollPeriod === 'custom' && (
                          <>
                            <Input
                              type="date"
                              value={payrollCustomStart}
                              onChange={(e) => setPayrollCustomStart(e.target.value)}
                              className="h-9 w-36"
                              data-testid="payroll-start-date"
                            />
                            <span className="text-slate-400">to</span>
                            <Input
                              type="date"
                              value={payrollCustomEnd}
                              onChange={(e) => setPayrollCustomEnd(e.target.value)}
                              className="h-9 w-36"
                              data-testid="payroll-end-date"
                            />
                          </>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={loadTutorPayroll}
                          disabled={loadingPayroll}
                          data-testid="payroll-refresh-btn"
                        >
                          <RefreshCw className={`h-4 w-4 mr-1 ${loadingPayroll ? 'animate-spin' : ''}`} />
                          Load
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => loadPaymentHistory()}
                          data-testid="view-payment-history-btn"
                        >
                          <History className="h-4 w-4 mr-1" />
                          History
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={runPayrollBackfill}
                          disabled={runningBackfill}
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          data-testid="run-backfill-btn"
                        >
                          {runningBackfill ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Database className="h-4 w-4 mr-1" />
                          )}
                          Backfill
                        </Button>
                      </div>
                    </div>

                    {/* Summary Cards */}
                    {tutorPayroll.summary && Object.keys(tutorPayroll.summary).length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        <Card className="p-4 bg-gradient-to-br from-blue-50 to-white">
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Total Hours</p>
                          <p className="text-2xl font-bold text-slate-800">{tutorPayroll.summary.total_approved_hours}h</p>
                        </Card>
                        <Card className="p-4 bg-gradient-to-br from-amber-50 to-white">
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Unpaid Hours</p>
                          <p className="text-2xl font-bold text-amber-600">{tutorPayroll.summary.total_unpaid_hours}h</p>
                        </Card>
                        <Card className="p-4 bg-gradient-to-br from-green-50 to-white">
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Paid Hours</p>
                          <p className="text-2xl font-bold text-green-600">{tutorPayroll.summary.total_paid_hours}h</p>
                        </Card>
                        <Card className="p-4 bg-gradient-to-br from-red-50 to-white">
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Tutor Cost Owed</p>
                          <p className="text-2xl font-bold text-red-600">${tutorPayroll.summary.total_tutor_cost_owed?.toFixed(2) || '0.00'}</p>
                        </Card>
                        <Card className="p-4 bg-gradient-to-br from-emerald-50 to-white">
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Tutor Cost Paid</p>
                          <p className="text-2xl font-bold text-emerald-600">${tutorPayroll.summary.total_tutor_cost_paid?.toFixed(2) || '0.00'}</p>
                        </Card>
                        <Card className="p-4 bg-gradient-to-br from-indigo-50 to-white">
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Gross Revenue</p>
                          <p className="text-2xl font-bold text-indigo-600">${tutorPayroll.summary.gross_revenue?.toFixed(2) || '0.00'}</p>
                        </Card>
                      </div>
                    )}

                    {/* Platform Income Summary */}
                    {tutorPayroll.summary && Object.keys(tutorPayroll.summary).length > 0 && (
                      <Card className="p-4 bg-gradient-to-r from-slate-800 to-slate-700">
                        <div className="flex items-center justify-between text-white">
                          <div>
                            <p className="text-xs text-slate-300 uppercase tracking-wide">Realized Platform Income</p>
                            <p className="text-2xl font-bold">${tutorPayroll.summary.realized_platform_income?.toFixed(2) || '0.00'}</p>
                            <p className="text-xs text-slate-400">Revenue minus paid tutor costs</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-300 uppercase tracking-wide">Projected Net Income</p>
                            <p className="text-2xl font-bold">${tutorPayroll.summary.projected_platform_income?.toFixed(2) || '0.00'}</p>
                            <p className="text-xs text-slate-400">Revenue minus all tutor costs</p>
                          </div>
                        </div>
                      </Card>
                    )}

                    {/* Empty state */}
                    {tutorPayroll.tutors?.length === 0 && !loadingPayroll && (
                      <Card className="p-8 text-center">
                        <DollarSign className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500 mb-4">Click "Load" to fetch tutor payroll data</p>
                        <Button onClick={loadTutorPayroll} data-testid="load-payroll-btn">
                          Load Payroll Data
                        </Button>
                      </Card>
                    )}

                    {/* Loading state */}
                    {loadingPayroll && (
                      <Card className="p-8 text-center">
                        <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-500 mb-2" />
                        <p className="text-slate-500">Loading payroll data...</p>
                      </Card>
                    )}

                    {/* Tutor Payroll List */}
                    {!loadingPayroll && tutorPayroll.tutors?.length > 0 && (
                      <div className="space-y-4">
                        {tutorPayroll.tutors.map((tutor) => (
                          <Card key={tutor.tutor_name || tutor.tutor_id} className="overflow-hidden" data-testid={`payroll-tutor-${tutor.tutor_name}`}>
                            {/* Tutor Summary Header */}
                            <div 
                              className="p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                              onClick={() => togglePayrollTutorExpand(tutor.tutor_name)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <UserRound className="h-5 w-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                                      {tutor.tutor_name || 'Unknown Tutor'}
                                      {tutor.has_rate_missing && (
                                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                                          Rate Missing
                                        </span>
                                      )}
                                    </h4>
                                    <p className="text-sm text-slate-500">
                                      {tutor.total_sessions} session(s) • Last paid: {tutor.last_payment_date 
                                        ? new Date(tutor.last_payment_date).toLocaleDateString() 
                                        : 'Never'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-6">
                                  <div className="text-right">
                                    <p className="text-xs text-slate-500">Unpaid Hours</p>
                                    <p className="font-bold text-lg text-amber-600">{tutor.unpaid_hours}h</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-slate-500">Paid Hours</p>
                                    <p className="font-bold text-lg text-green-600">{tutor.paid_hours}h</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-slate-500">Amount Owed</p>
                                    <p className={`font-bold text-lg ${tutor.has_rate_missing ? 'text-slate-400' : 'text-red-600'}`}>
                                      {tutor.has_rate_missing ? 'Rate Missing' : `$${tutor.amount_owed?.toFixed(2) || '0.00'}`}
                                    </p>
                                  </div>
                                  {tutor.unpaid_hours > 0 && !tutor.has_rate_missing && (
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarkAsPaid(tutor);
                                      }}
                                      disabled={processingPayment === tutor.tutor_name}
                                      data-testid={`mark-paid-${tutor.tutor_name}`}
                                    >
                                      {processingPayment === tutor.tutor_name ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <>
                                          <CheckCircle className="h-4 w-4 mr-1" />
                                          Mark as Paid
                                        </>
                                      )}
                                    </Button>
                                  )}
                                  <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${expandedPayrollTutors[tutor.tutor_name] ? 'rotate-180' : ''}`} />
                                </div>
                              </div>
                            </div>

                            {/* Expanded Session Details */}
                            {expandedPayrollTutors[tutor.tutor_name] && tutor.unpaid_session_ids?.length > 0 && (
                              <div className="border-t border-slate-200 p-4">
                                <p className="text-sm font-medium text-slate-600 mb-3">Unpaid Sessions ({tutor.unpaid_sessions})</p>
                                <div className="bg-slate-50 rounded-lg overflow-hidden">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="text-left text-slate-500 bg-slate-100">
                                        <th className="px-3 py-2">Date</th>
                                        <th className="px-3 py-2">Student</th>
                                        <th className="px-3 py-2">Hours</th>
                                        <th className="px-3 py-2">Rate</th>
                                        <th className="px-3 py-2">Amount</th>
                                        <th className="px-3 py-2">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {tutor.unpaid_session_ids.map((session, idx) => (
                                        <tr key={session.log_id || idx} className="border-t border-slate-200">
                                          <td className="px-3 py-2">{session.date || '-'}</td>
                                          <td className="px-3 py-2">{session.student || '-'}</td>
                                          <td className="px-3 py-2 font-medium">{session.hours}h</td>
                                          <td className="px-3 py-2">
                                            {session.tutor_rate_used 
                                              ? `$${session.tutor_rate_used}/h` 
                                              : <span className="text-amber-600">Missing</span>
                                            }
                                          </td>
                                          <td className="px-3 py-2 font-medium">
                                            {session.tutor_rate_used 
                                              ? `$${(session.hours * session.tutor_rate_used).toFixed(2)}` 
                                              : '-'
                                            }
                                          </td>
                                          <td className="px-3 py-2">
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openSessionEditModal(session);
                                              }}
                                              data-testid={`edit-session-${session.log_id}`}
                                            >
                                              <Pencil className="h-3 w-3 mr-1" />
                                              Edit
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* Payment History Modal */}
                    {showPaymentHistory && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPaymentHistory(false)}>
                        <Card className="w-full max-w-3xl max-h-[80vh] overflow-hidden m-4" onClick={(e) => e.stopPropagation()}>
                          <CardHeader className="border-b">
                            <div className="flex items-center justify-between">
                              <CardTitle className="flex items-center gap-2">
                                <History className="h-5 w-5 text-indigo-500" />
                                Payment History
                              </CardTitle>
                              <Button variant="ghost" size="sm" onClick={() => setShowPaymentHistory(false)}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="p-0 overflow-auto max-h-[60vh]">
                            {paymentHistory.length === 0 ? (
                              <div className="p-8 text-center text-slate-500">
                                No payment history found
                              </div>
                            ) : (
                              <table className="w-full text-sm">
                                <thead className="bg-slate-50 sticky top-0">
                                  <tr className="text-left text-slate-600">
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Tutor</th>
                                    <th className="px-4 py-3">Hours</th>
                                    <th className="px-4 py-3">Amount</th>
                                    <th className="px-4 py-3">Sessions</th>
                                    <th className="px-4 py-3">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paymentHistory.map((payment) => (
                                    <tr key={payment.payment_id} className="border-t hover:bg-slate-50">
                                      <td className="px-4 py-3">
                                        {new Date(payment.payment_date).toLocaleDateString()}
                                      </td>
                                      <td className="px-4 py-3 font-medium">{payment.tutor_name}</td>
                                      <td className="px-4 py-3">{payment.paid_hours}h</td>
                                      <td className="px-4 py-3 font-medium text-green-600">
                                        ${payment.amount_paid?.toFixed(2)}
                                      </td>
                                      <td className="px-4 py-3">{payment.session_count}</td>
                                      <td className="px-4 py-3">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                          onClick={() => handleReversePayment(payment.payment_id, payment.tutor_name)}
                                          data-testid={`reverse-payment-${payment.payment_id}`}
                                        >
                                          <Undo2 className="h-4 w-4 mr-1" />
                                          Reverse
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    )}

                    {/* Session Edit Modal */}
                    {showSessionEditModal && editingSession && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSessionEditModal(false)}>
                        <Card className="w-full max-w-md m-4" onClick={(e) => e.stopPropagation()}>
                          <CardHeader className="border-b">
                            <div className="flex items-center justify-between">
                              <CardTitle className="flex items-center gap-2">
                                <Pencil className="h-5 w-5 text-blue-500" />
                                Edit Session Payroll
                              </CardTitle>
                              <Button variant="ghost" size="sm" onClick={() => setShowSessionEditModal(false)}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="p-4 space-y-4">
                            {/* Session Info */}
                            <div className="bg-slate-50 rounded-lg p-3 text-sm">
                              <p><strong>Session ID:</strong> {editingSession.session.log_id}</p>
                              <p><strong>Date:</strong> {editingSession.session.session_date}</p>
                              <p><strong>Student:</strong> {editingSession.session.student_name}</p>
                              <p><strong>Tutor:</strong> {editingSession.session.tutor_name}</p>
                              <p><strong>Hours:</strong> {editingSession.session.hours_used}h</p>
                              <p><strong>Status:</strong> {editingSession.session.status}</p>
                              <p><strong>Payment Status:</strong> {editingSession.payroll_fields.payment_status || 'unpaid'}</p>
                            </div>

                            {/* Tutor Profile Rates Reference */}
                            {editingSession.tutor_profile_rates && (
                              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                                <p className="font-medium text-blue-700 mb-1">Tutor Profile Rates (Reference):</p>
                                <p>High School: ${editingSession.tutor_profile_rates.high_school || 0}/h</p>
                                <p>University: ${editingSession.tutor_profile_rates.university || 0}/h</p>
                              </div>
                            )}

                            {/* Tutor Rate Input */}
                            <div className="space-y-2">
                              <Label htmlFor="tutor_rate_used" className="text-sm font-medium">
                                Tutor Rate Used ($/hour) *
                              </Label>
                              <Input
                                id="tutor_rate_used"
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Enter tutor hourly rate"
                                value={sessionEditForm.tutor_rate_used}
                                onChange={(e) => setSessionEditForm(prev => ({ ...prev, tutor_rate_used: e.target.value }))}
                                data-testid="session-rate-input"
                              />
                              <p className="text-xs text-slate-500">
                                This is the tutor's pay rate, NOT the student's billing rate.
                              </p>
                            </div>

                            {/* Payment Notes */}
                            <div className="space-y-2">
                              <Label htmlFor="payment_notes" className="text-sm font-medium">
                                Payment Notes (optional)
                              </Label>
                              <Textarea
                                id="payment_notes"
                                placeholder="Add any notes about this session's payment..."
                                value={sessionEditForm.payment_notes}
                                onChange={(e) => setSessionEditForm(prev => ({ ...prev, payment_notes: e.target.value }))}
                                rows={2}
                                data-testid="session-notes-input"
                              />
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-2 pt-2">
                              <Button
                                variant="outline"
                                onClick={() => setShowSessionEditModal(false)}
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={saveSessionPayroll}
                                disabled={savingSessionPayroll || !sessionEditForm.tutor_rate_used}
                                data-testid="save-session-payroll-btn"
                              >
                                {savingSessionPayroll ? (
                                  <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                                ) : (
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                )}
                                Save
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Email Center Tab */}
                <TabsContent value="email-center">
                  <div className="space-y-6">
                    {/* Email Center Header */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">Email Center</h3>
                        <p className="text-sm text-slate-500">Send targeted emails to tutors, parents, and students</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={loadEmailHistory}
                        disabled={loadingEmailHistory}
                      >
                        <RefreshCw className={`h-4 w-4 mr-1 ${loadingEmailHistory ? 'animate-spin' : ''}`} />
                        Refresh History
                      </Button>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-6">
                      {/* Compose Email Section */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Mail className="h-4 w-4 text-indigo-500" />
                            Compose Email
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Audience Selection */}
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Audience</Label>
                            <select
                              className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                              value={emailCenterForm.audience}
                              onChange={(e) => handleAudienceChange(e.target.value)}
                              data-testid="email-audience-select"
                            >
                              <option value="">Select audience...</option>
                              <option value="tutors">Tutors</option>
                              <option value="parents">Parents</option>
                              <option value="students">Students</option>
                            </select>
                          </div>

                          {/* Email Type Selection */}
                          {emailCenterForm.audience && (
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">Email Type</Label>
                              <select
                                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                                value={emailCenterForm.emailType}
                                onChange={(e) => handleEmailTypeChange(e.target.value)}
                                data-testid="email-type-select"
                              >
                                <option value="">Select email type...</option>
                                {emailTypeOptions[emailCenterForm.audience]?.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Recipient Scope Selection */}
                          {emailCenterForm.audience && (
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">Recipients</Label>
                              <select
                                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                                value={emailCenterForm.recipientScope}
                                onChange={(e) => handleRecipientScopeChange(e.target.value)}
                                data-testid="email-scope-select"
                              >
                                <option value="all_active">All Active ({emailCenterForm.recipientScope === 'all_active' ? emailRecipients.length : '...'})</option>
                                <option value="all_inactive">All Inactive</option>
                                <option value="selected">Selected Recipients Only</option>
                              </select>
                              
                              {loadingRecipients && (
                                <p className="text-xs text-slate-500">Loading recipients...</p>
                              )}
                            </div>
                          )}

                          {/* Selected Recipients Picker */}
                          {emailCenterForm.recipientScope === 'selected' && emailRecipients.length > 0 && (
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">
                                Select Recipients ({emailCenterForm.selectedRecipients.length} selected)
                              </Label>
                              <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-1">
                                {emailRecipients.map(r => (
                                  <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                                    <input
                                      type="checkbox"
                                      checked={emailCenterForm.selectedRecipients.includes(r.id)}
                                      onChange={(e) => {
                                        setEmailCenterForm(prev => ({
                                          ...prev,
                                          selectedRecipients: e.target.checked
                                            ? [...prev.selectedRecipients, r.id]
                                            : prev.selectedRecipients.filter(id => id !== r.id)
                                        }));
                                      }}
                                      className="rounded border-slate-300"
                                    />
                                    <span>{r.name}</span>
                                    <span className="text-slate-400 text-xs">({r.email})</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Subject */}
                          {emailCenterForm.emailType && (
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">Subject</Label>
                              <Input
                                value={emailCenterForm.subject}
                                onChange={(e) => setEmailCenterForm(prev => ({ ...prev, subject: e.target.value }))}
                                placeholder="Email subject..."
                                data-testid="email-subject-input"
                              />
                            </div>
                          )}

                          {/* Message Body */}
                          {emailCenterForm.emailType && (
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">
                                Message Body
                                <span className="text-xs text-slate-400 ml-2">Use {"{{name}}"} for personalization</span>
                              </Label>
                              <Textarea
                                value={emailCenterForm.body}
                                onChange={(e) => setEmailCenterForm(prev => ({ ...prev, body: e.target.value }))}
                                placeholder="Write your message..."
                                rows={8}
                                className="font-mono text-sm"
                                data-testid="email-body-textarea"
                              />
                            </div>
                          )}

                          {/* Action Buttons */}
                          {emailCenterForm.emailType && (
                            <div className="flex flex-wrap gap-2 pt-2 border-t">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowEmailPreview(true)}
                                disabled={!emailCenterForm.subject || !emailCenterForm.body}
                                data-testid="email-preview-btn"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Preview
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleSendTestEmail}
                                disabled={sendingTestEmail || !emailCenterForm.subject || !emailCenterForm.body}
                                data-testid="email-test-btn"
                              >
                                {sendingTestEmail ? (
                                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                  <Mail className="h-4 w-4 mr-1" />
                                )}
                                Send Test to Admin
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleSendEmails()}
                                disabled={sendingEmail || !emailCenterForm.subject || !emailCenterForm.body || getRecipientCount() === 0}
                                className="bg-indigo-600 hover:bg-indigo-700"
                                data-testid="email-send-now-btn"
                              >
                                {sendingEmail ? (
                                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                  <Send className="h-4 w-4 mr-1" />
                                )}
                                Send Now ({getRecipientCount()})
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Email Preview Card */}
                      {showEmailPreview && (
                        <Card>
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-base flex items-center gap-2">
                                <Eye className="h-4 w-4 text-indigo-500" />
                                Email Preview
                              </CardTitle>
                              <Button variant="ghost" size="sm" onClick={() => setShowEmailPreview(false)}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="border rounded-lg overflow-hidden">
                              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4">
                                <h3 className="text-white font-semibold">Gifted Academy Tutors</h3>
                              </div>
                              <div className="p-4 space-y-3">
                                <div>
                                  <p className="text-xs text-slate-500">Subject:</p>
                                  <p className="font-medium">{emailCenterForm.subject}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500 mb-1">Message:</p>
                                  <div className="whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 p-3 rounded">
                                    {emailCenterForm.body.replace(/\{\{name\}\}/g, '[Recipient Name]')}
                                  </div>
                                </div>
                              </div>
                              <div className="bg-slate-50 p-3 text-center text-xs text-slate-500">
                                Gifted Academy Tutors | Toronto, Canada
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Send History Section - shows when preview is not open */}
                      {!showEmailPreview && (
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <History className="h-4 w-4 text-indigo-500" />
                              Send History
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {loadingEmailHistory ? (
                              <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                              </div>
                            ) : emailHistory.length === 0 ? (
                              <div className="text-center py-8 text-slate-500">
                                <Mail className="h-10 w-10 mx-auto mb-2 opacity-30" />
                                <p className="text-sm">No emails sent yet</p>
                              </div>
                            ) : (
                              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                                {emailHistory.map((log) => (
                                  <div key={log.log_id} className="border rounded-lg p-3 text-sm">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate">{log.subject}</p>
                                        <p className="text-xs text-slate-500 mt-1">
                                          To: <span className="capitalize">{log.audience}</span> • {log.email_type?.replace(/_/g, ' ')}
                                        </p>
                                      </div>
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        log.status === 'completed' ? 'bg-green-100 text-green-700' :
                                        log.status === 'sending' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-red-100 text-red-700'
                                      }`}>
                                        {log.status}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                      <span>{log.recipient_count} recipients</span>
                                      {log.sent_count > 0 && <span className="text-green-600">{log.sent_count} sent</span>}
                                      {log.failed_count > 0 && <span className="text-red-600">{log.failed_count} failed</span>}
                                      <span>{new Date(log.created_at).toLocaleDateString()}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* Invoices Tab - Full Invoice Manager */}
                <TabsContent value="invoices">
                  <div className="space-y-6" data-testid="tutor-invoices-tab">
                    {/* Header with actions */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">Tutor Invoice Manager</h3>
                        <p className="text-sm text-slate-500">Generate, send, and manage tutor payment invoices</p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm"
                          value={invoiceStatusFilter}
                          onChange={(e) => setInvoiceStatusFilter(e.target.value)}
                          data-testid="invoice-status-filter"
                        >
                          <option value="all">All Status</option>
                          <option value="draft">Draft</option>
                          <option value="ready">Ready</option>
                          <option value="sent">Sent</option>
                          <option value="failed">Failed</option>
                        </select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            loadInvoices();
                            loadInvoicePeriods();
                          }}
                          disabled={loadingInvoices}
                          data-testid="refresh-invoices-btn"
                        >
                          <RefreshCw className={`h-4 w-4 mr-1 ${loadingInvoices ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            loadSchedulerRuns();
                            setShowSchedulerHistory(true);
                          }}
                          data-testid="scheduler-history-btn"
                        >
                          <History className="h-4 w-4 mr-1" />
                          Batch History
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            loadInvoicePeriods();
                            setShowGenerateInvoiceModal(true);
                          }}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="generate-invoice-btn"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Generate Invoice
                        </Button>
                      </div>
                    </div>

                    {/* Invoice Schedule Info */}
                    <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <p className="text-sm font-medium text-blue-800">Automatic Invoice Schedule</p>
                          <p className="text-xs text-blue-600 mt-1">
                            Invoices are generated on the 15th (period 1st-15th) and month-end (period 16th-end).
                          </p>
                          {currentPeriod && (
                            <p className="text-xs text-slate-600 mt-2">
                              <strong>Current period:</strong> {currentPeriod.period_start} to {currentPeriod.period_end}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Month/Year Selector */}
                          <select
                            className="h-9 px-2 rounded-md border border-slate-200 bg-white text-sm"
                            value={batchMonth}
                            onChange={(e) => setBatchMonth(parseInt(e.target.value))}
                            data-testid="batch-month-select"
                          >
                            <option value={1}>January</option>
                            <option value={2}>February</option>
                            <option value={3}>March</option>
                            <option value={4}>April</option>
                            <option value={5}>May</option>
                            <option value={6}>June</option>
                            <option value={7}>July</option>
                            <option value={8}>August</option>
                            <option value={9}>September</option>
                            <option value={10}>October</option>
                            <option value={11}>November</option>
                            <option value={12}>December</option>
                          </select>
                          <select
                            className="h-9 px-2 rounded-md border border-slate-200 bg-white text-sm"
                            value={batchYear}
                            onChange={(e) => setBatchYear(parseInt(e.target.value))}
                            data-testid="batch-year-select"
                          >
                            {[2024, 2025, 2026, 2027].map(y => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRunInvoiceBatch('15th')}
                            disabled={runningInvoiceBatch}
                            className="text-blue-700 border-blue-300 hover:bg-blue-100"
                            data-testid="run-batch-15th-btn"
                          >
                            {runningInvoiceBatch ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                            1st-15th
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRunInvoiceBatch('month-end')}
                            disabled={runningInvoiceBatch}
                            className="text-indigo-700 border-indigo-300 hover:bg-indigo-100"
                            data-testid="run-batch-month-end-btn"
                          >
                            {runningInvoiceBatch ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                            16th-End
                          </Button>
                        </div>
                      </div>
                    </Card>

                    {/* Loading state */}
                    {loadingInvoices && (
                      <Card className="p-8 text-center">
                        <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-500 mb-2" />
                        <p className="text-slate-500">Loading invoices...</p>
                      </Card>
                    )}

                    {/* Empty state */}
                    {!loadingInvoices && invoices.length === 0 && (
                      <Card className="p-8 text-center">
                        <Receipt className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500 mb-4">No invoices found. Click "Refresh" to load invoices or "Generate Invoice" to create one.</p>
                        <Button onClick={() => { loadInvoices(); loadInvoicePeriods(); }} data-testid="load-invoices-btn">
                          Load Invoices
                        </Button>
                      </Card>
                    )}

                    {/* Invoice List */}
                    {!loadingInvoices && invoices.length > 0 && (
                      <div className="bg-white rounded-lg border overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr className="text-left text-slate-600">
                              <th className="px-4 py-3">Invoice #</th>
                              <th className="px-4 py-3">Tutor</th>
                              <th className="px-4 py-3">Period</th>
                              <th className="px-4 py-3">Hours</th>
                              <th className="px-4 py-3">Amount</th>
                              <th className="px-4 py-3">Status</th>
                              <th className="px-4 py-3">Generated</th>
                              <th className="px-4 py-3">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoices.map((invoice) => (
                              <tr key={invoice.invoice_id} className="border-t hover:bg-slate-50" data-testid={`invoice-row-${invoice.invoice_id}`}>
                                <td className="px-4 py-3">
                                  <span className="font-mono text-xs">{invoice.invoice_number}</span>
                                </td>
                                <td className="px-4 py-3">
                                  <p className="font-medium">{invoice.tutor_name_snapshot}</p>
                                  <p className="text-xs text-slate-500">{invoice.tutor_email_snapshot}</p>
                                </td>
                                <td className="px-4 py-3 text-xs">
                                  {invoice.invoice_period_start} to {invoice.invoice_period_end}
                                </td>
                                <td className="px-4 py-3 font-medium">
                                  {invoice.total_hours?.toFixed(1)}h
                                </td>
                                <td className="px-4 py-3 font-bold text-green-600">
                                  ${invoice.total_amount?.toFixed(2)}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    invoice.status === 'sent' ? 'bg-green-100 text-green-700' :
                                    invoice.status === 'ready' ? 'bg-blue-100 text-blue-700' :
                                    invoice.status === 'draft' ? 'bg-slate-100 text-slate-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {invoice.status}
                                  </span>
                                  {invoice.tutor_email_sent && (
                                    <span className="ml-1 text-xs text-green-600">✓ Emailed</span>
                                  )}
                                  {invoice.tutor_email_error && !invoice.tutor_email_sent && (
                                    <span className="ml-1 text-xs text-red-600" title={invoice.tutor_email_error}>
                                      ✗ Failed
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-xs text-slate-500">
                                  {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : '-'}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                      onClick={() => handleDownloadInvoicePDF(invoice.invoice_id, invoice.invoice_number)}
                                      data-testid={`download-pdf-${invoice.invoice_id}`}
                                    >
                                      <FileText className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                                      onClick={() => handleSendInvoiceEmail(invoice.invoice_id, 'both')}
                                      disabled={sendingInvoiceEmail === invoice.invoice_id}
                                      data-testid={`send-email-${invoice.invoice_id}`}
                                    >
                                      {sendingInvoiceEmail === invoice.invoice_id ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Send className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Generate Invoice Modal */}
                    {showGenerateInvoiceModal && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowGenerateInvoiceModal(false)}>
                        <Card className="w-full max-w-md m-4" onClick={(e) => e.stopPropagation()}>
                          <CardHeader className="border-b">
                            <div className="flex items-center justify-between">
                              <CardTitle className="flex items-center gap-2">
                                <Receipt className="h-5 w-5 text-blue-500" />
                                Generate Invoice
                              </CardTitle>
                              <Button variant="ghost" size="sm" onClick={() => setShowGenerateInvoiceModal(false)}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="p-4 space-y-4">
                            {/* Period suggestions */}
                            {(currentPeriod || previousPeriod) && (
                              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                                <p className="font-medium text-blue-700 mb-2">Quick Period Select:</p>
                                {previousPeriod && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="mr-2 mb-2"
                                    onClick={() => {
                                      setGenerateInvoiceForm(prev => ({
                                        ...prev,
                                        period_start: previousPeriod.period_start,
                                        period_end: previousPeriod.period_end,
                                        tutor_id: ''
                                      }));
                                      loadEligibleTutors(previousPeriod.period_start, previousPeriod.period_end);
                                    }}
                                  >
                                    Previous: {previousPeriod.period_start} to {previousPeriod.period_end}
                                  </Button>
                                )}
                                {currentPeriod && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="mb-2"
                                    onClick={() => {
                                      setGenerateInvoiceForm(prev => ({
                                        ...prev,
                                        period_start: currentPeriod.period_start,
                                        period_end: currentPeriod.period_end,
                                        tutor_id: ''
                                      }));
                                      loadEligibleTutors(currentPeriod.period_start, currentPeriod.period_end);
                                    }}
                                  >
                                    Current: {currentPeriod.period_start} to {currentPeriod.period_end}
                                  </Button>
                                )}
                              </div>
                            )}

                            {/* Period inputs */}
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor="period_start">Period Start *</Label>
                                <Input
                                  id="period_start"
                                  type="date"
                                  value={generateInvoiceForm.period_start}
                                  onChange={(e) => {
                                    const newStart = e.target.value;
                                    setGenerateInvoiceForm(prev => ({ ...prev, period_start: newStart, tutor_id: '' }));
                                    if (newStart && generateInvoiceForm.period_end) {
                                      loadEligibleTutors(newStart, generateInvoiceForm.period_end);
                                    }
                                  }}
                                  data-testid="invoice-period-start"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="period_end">Period End *</Label>
                                <Input
                                  id="period_end"
                                  type="date"
                                  value={generateInvoiceForm.period_end}
                                  onChange={(e) => {
                                    const newEnd = e.target.value;
                                    setGenerateInvoiceForm(prev => ({ ...prev, period_end: newEnd, tutor_id: '' }));
                                    if (generateInvoiceForm.period_start && newEnd) {
                                      loadEligibleTutors(generateInvoiceForm.period_start, newEnd);
                                    }
                                  }}
                                  data-testid="invoice-period-end"
                                />
                              </div>
                            </div>

                            {/* Tutor selection */}
                            <div className="space-y-2">
                              <Label htmlFor="tutor_id">Eligible Tutor *</Label>
                              {loadingEligibleTutors ? (
                                <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                  Loading eligible tutors...
                                </div>
                              ) : eligibleTutors.length === 0 && generateInvoiceForm.period_start && generateInvoiceForm.period_end ? (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                                  <p className="font-medium text-amber-700 mb-2">No invoice-ready tutors found for this period.</p>
                                  {ineligibleReasons.length > 0 && (
                                    <ul className="text-amber-600 space-y-1">
                                      {ineligibleReasons.map((r, idx) => (
                                        <li key={idx}>• {r.tutor_name}: {r.reason}</li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              ) : (
                                <select
                                  id="tutor_id"
                                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white"
                                  value={generateInvoiceForm.tutor_id}
                                  onChange={(e) => setGenerateInvoiceForm(prev => ({ ...prev, tutor_id: e.target.value }))}
                                  data-testid="invoice-tutor-select"
                                >
                                  <option value="">Select tutor...</option>
                                  {eligibleTutors.map(tutor => (
                                    <option key={tutor.tutor_id} value={tutor.tutor_id}>
                                      {tutor.label}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>

                            <p className="text-xs text-slate-500">
                              Only approved sessions with tutor_rate_used will be included. Sessions already invoiced are excluded.
                            </p>

                            {/* Actions */}
                            <div className="flex justify-end gap-2 pt-2">
                              <Button variant="outline" onClick={() => setShowGenerateInvoiceModal(false)}>
                                Cancel
                              </Button>
                              <Button
                                onClick={handleGenerateInvoice}
                                disabled={generatingInvoice || !generateInvoiceForm.tutor_id || eligibleTutors.length === 0}
                                data-testid="confirm-generate-invoice-btn"
                              >
                                {generatingInvoice ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Receipt className="h-4 w-4 mr-1" />}
                                Generate
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}

                    {/* Scheduler History Modal */}
                    {showSchedulerHistory && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSchedulerHistory(false)}>
                        <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden m-4" onClick={(e) => e.stopPropagation()}>
                          <CardHeader className="border-b">
                            <div className="flex items-center justify-between">
                              <CardTitle className="flex items-center gap-2">
                                <History className="h-5 w-5 text-indigo-500" />
                                Invoice Batch History
                              </CardTitle>
                              <Button variant="ghost" size="sm" onClick={() => setShowSchedulerHistory(false)}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="p-0 overflow-auto max-h-[60vh]">
                            {schedulerRuns.length === 0 ? (
                              <div className="p-8 text-center text-slate-500">
                                No batch runs recorded yet
                              </div>
                            ) : (
                              <table className="w-full text-sm">
                                <thead className="bg-slate-50 sticky top-0">
                                  <tr className="text-left text-slate-600">
                                    <th className="px-4 py-3">Run ID</th>
                                    <th className="px-4 py-3">Period</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Invoices</th>
                                    <th className="px-4 py-3">Emails</th>
                                    <th className="px-4 py-3">Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {schedulerRuns.map((run) => (
                                    <tr key={run.run_id} className="border-t hover:bg-slate-50">
                                      <td className="px-4 py-3 font-mono text-xs">{run.run_id}</td>
                                      <td className="px-4 py-3 text-xs">
                                        {run.period_start} to {run.period_end}
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                          run.status === 'completed' ? 'bg-green-100 text-green-700' :
                                          run.status === 'partial_failure' ? 'bg-yellow-100 text-yellow-700' :
                                          run.status === 'running' ? 'bg-blue-100 text-blue-700' :
                                          'bg-red-100 text-red-700'
                                        }`}>
                                          {run.status}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3">{run.invoices_created || 0}</td>
                                      <td className="px-4 py-3">{run.emails_sent || 0}</td>
                                      <td className="px-4 py-3 text-xs text-slate-500">
                                        {run.started_at ? new Date(run.started_at).toLocaleString() : '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Maintenance / Data Reset Tab */}
                <TabsContent value="maintenance">
                  <div className="space-y-6">
                    {/* Warning Banner */}
                    <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <ShieldAlert className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="font-semibold text-amber-800">⚠️ ADMIN MAINTENANCE - USE WITH CAUTION</h3>
                          <p className="text-sm text-amber-700 mt-1">
                            These tools permanently delete or modify data. Always run a DRY RUN first to preview what will be affected.
                            Student user accounts themselves are NOT deleted.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Sub-navigation for maintenance tools */}
                    <div className="flex gap-2 border-b border-slate-200 pb-2">
                      <Button
                        variant={maintenanceTab === 'operational' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setMaintenanceTab('operational')}
                        className={maintenanceTab === 'operational' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                      >
                        <Database className="h-4 w-4 mr-1" />
                        Operational Reset
                      </Button>
                      <Button
                        variant={maintenanceTab === 'financial' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setMaintenanceTab('financial')}
                        className={maintenanceTab === 'financial' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                      >
                        <DollarSign className="h-4 w-4 mr-1" />
                        Financial Reconciliation
                      </Button>
                    </div>

                    {/* Operational Reset Section */}
                    {maintenanceTab === 'operational' && (
                      <div className="space-y-6">
                        {/* Header with Load/Clear buttons */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">Student Operational Data Reset</h3>
                        <p className="text-sm text-slate-500">Select TEST/DEMO student accounts to reset their operational history</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={loadMaintenanceStudents}
                          disabled={loadingMaintenanceStudents}
                          data-testid="load-maintenance-students-btn"
                        >
                          <RefreshCw className={`h-4 w-4 mr-1 ${loadingMaintenanceStudents ? 'animate-spin' : ''}`} />
                          Load Students
                        </Button>
                        {selectedResetIds.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleClearSelection}
                            data-testid="clear-selection-btn"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Clear Selection ({selectedResetIds.length})
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Search and Selection Controls */}
                    {maintenanceStudents.length > 0 && (
                      <div className="flex items-center gap-4">
                        <div className="flex-1 relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            placeholder="Search by name, email, or user_id..."
                            value={searchMaintenance}
                            onChange={(e) => setSearchMaintenance(e.target.value)}
                            className="pl-10"
                            data-testid="maintenance-search-input"
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSelectAllFiltered}
                          data-testid="select-all-filtered-btn"
                        >
                          {getFilteredMaintenanceStudents().every(s => selectedResetIds.includes(s.user_id)) 
                            ? 'Deselect All Filtered' 
                            : 'Select All Filtered'}
                        </Button>
                      </div>
                    )}

                    {/* Student List */}
                    {loadingMaintenanceStudents ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                        <span className="ml-2 text-slate-600">Loading student accounts...</span>
                      </div>
                    ) : maintenanceStudents.length === 0 ? (
                      <Card className="border-dashed">
                        <CardContent className="py-12 text-center">
                          <Database className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                          <p className="text-slate-500">Click "Load Students" to fetch student accounts</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium text-slate-600">
                            Student Accounts ({getFilteredMaintenanceStudents().length} shown, {selectedResetIds.length} selected)
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                          <div className="max-h-[400px] overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-slate-50 sticky top-0">
                                <tr>
                                  <th className="px-4 py-2 text-left font-medium text-slate-600 w-12">Select</th>
                                  <th className="px-4 py-2 text-left font-medium text-slate-600">Name</th>
                                  <th className="px-4 py-2 text-left font-medium text-slate-600">Email</th>
                                  <th className="px-4 py-2 text-left font-medium text-slate-600">User ID</th>
                                  <th className="px-4 py-2 text-left font-medium text-slate-600">Created</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {getFilteredMaintenanceStudents().map((student) => (
                                  <tr 
                                    key={student.user_id} 
                                    className={`hover:bg-slate-50 cursor-pointer ${selectedResetIds.includes(student.user_id) ? 'bg-amber-50' : ''}`}
                                    onClick={() => handleResetSelectionToggle(student.user_id)}
                                  >
                                    <td className="px-4 py-2">
                                      <input
                                        type="checkbox"
                                        checked={selectedResetIds.includes(student.user_id)}
                                        onChange={() => handleResetSelectionToggle(student.user_id)}
                                        className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                        data-testid={`select-student-${student.user_id}`}
                                      />
                                    </td>
                                    <td className="px-4 py-2 font-medium text-slate-800">{student.name || '-'}</td>
                                    <td className="px-4 py-2 text-slate-600">{student.email}</td>
                                    <td className="px-4 py-2 text-slate-500 font-mono text-xs">{student.user_id}</td>
                                    <td className="px-4 py-2 text-slate-500 text-xs">
                                      {student.created_at ? format(new Date(student.created_at), 'MMM d, yyyy') : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Action Buttons */}
                    {selectedResetIds.length > 0 && (
                      <div className="flex items-center gap-4 p-4 bg-slate-100 rounded-xl">
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">
                            {selectedResetIds.length} student account(s) selected
                          </p>
                          <p className="text-sm text-slate-500">
                            Run a dry run first to see what will be deleted
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          onClick={handleDryRunReset}
                          disabled={executingReset}
                          className="border-amber-400 text-amber-700 hover:bg-amber-50"
                          data-testid="dry-run-btn"
                        >
                          {executingReset ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Eye className="h-4 w-4 mr-1" />
                          )}
                          Dry Run (Preview)
                        </Button>
                        {dryRunResult?.status === 'dry_run_complete' && (
                          <Button
                            variant="destructive"
                            onClick={handleExecuteReset}
                            disabled={executingReset}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="execute-reset-btn"
                          >
                            {executingReset ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 mr-1" />
                            )}
                            Execute Reset
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Dry Run Results */}
                    {dryRunResult && (
                      <Card className={`border-2 ${dryRunResult.status === 'error' ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            {dryRunResult.status === 'error' ? (
                              <>
                                <XCircle className="h-5 w-5 text-red-500" />
                                <span className="text-red-700">Dry Run Failed</span>
                              </>
                            ) : (
                              <>
                                <Eye className="h-5 w-5 text-amber-600" />
                                <span className="text-amber-800">Dry Run Results (Preview Only)</span>
                              </>
                            )}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {dryRunResult.status === 'error' ? (
                            <p className="text-red-700">{dryRunResult.message}</p>
                          ) : (
                            <>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-white p-3 rounded-lg border">
                                  <p className="text-xs text-slate-500">Valid Students</p>
                                  <p className="text-xl font-bold text-slate-800">{dryRunResult.valid_student_ids?.length || 0}</p>
                                </div>
                                <div className="bg-white p-3 rounded-lg border">
                                  <p className="text-xs text-slate-500">Total Records to Delete</p>
                                  <p className="text-xl font-bold text-red-600">{dryRunResult.total_records_to_delete}</p>
                                </div>
                              </div>
                              
                              <div>
                                <p className="text-sm font-medium text-amber-800 mb-2">Records by Collection:</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                  {Object.entries(dryRunResult.before_counts || {}).map(([collection, count]) => (
                                    <div key={collection} className="bg-white px-3 py-2 rounded border text-sm">
                                      <span className="text-slate-600">{collection}:</span>
                                      <span className="ml-1 font-medium text-slate-800">{count}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className="pt-2 border-t border-amber-200">
                                <p className="text-sm text-amber-700">
                                  <strong>Note:</strong> Student user accounts will NOT be deleted. Only operational data (orders, sessions, bookings, etc.) will be removed.
                                </p>
                              </div>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Execution Results */}
                    {resetResult && (
                      <Card className={`border-2 ${resetResult.status === 'execution_complete' ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            {resetResult.status === 'execution_complete' ? (
                              <>
                                <CheckCircle className="h-5 w-5 text-green-500" />
                                <span className="text-green-700">Reset Completed Successfully</span>
                              </>
                            ) : (
                              <>
                                <XCircle className="h-5 w-5 text-red-500" />
                                <span className="text-red-700">Reset Failed</span>
                              </>
                            )}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {resetResult.status === 'execution_complete' ? (
                            <>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div className="bg-white p-3 rounded-lg border">
                                  <p className="text-xs text-slate-500">Total Records Deleted</p>
                                  <p className="text-xl font-bold text-green-600">{resetResult.total_records_deleted}</p>
                                </div>
                                <div className="bg-white p-3 rounded-lg border">
                                  <p className="text-xs text-slate-500">Student Accounts Preserved</p>
                                  <p className="text-xl font-bold text-slate-800">{resetResult.student_accounts_preserved}</p>
                                </div>
                              </div>

                              <div>
                                <p className="text-sm font-medium text-green-800 mb-2">Deleted by Collection:</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                  {Object.entries(resetResult.deleted_counts || {}).map(([collection, count]) => (
                                    <div key={collection} className="bg-white px-3 py-2 rounded border text-sm">
                                      <span className="text-slate-600">{collection}:</span>
                                      <span className="ml-1 font-medium text-green-600">{count} deleted</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className="pt-2 border-t border-green-200">
                                <p className="text-sm text-green-700">
                                  ✓ Dashboard metrics have been automatically refreshed.
                                </p>
                              </div>
                            </>
                          ) : (
                            <p className="text-red-700">{resetResult.message}</p>
                          )}
                        </CardContent>
                      </Card>
                    )}
                      </div>
                    )}

                    {/* Financial Reconciliation Section */}
                    {maintenanceTab === 'financial' && (
                      <div className="space-y-6">
                        {/* Financial Audit Header */}
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-slate-800">Financial Reconciliation</h3>
                            <p className="text-sm text-slate-500">Audit and reset financial metrics with exact before/after tracking</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={loadFinancialAudit}
                            disabled={loadingFinancialAudit}
                            data-testid="load-financial-audit-btn"
                          >
                            <RefreshCw className={`h-4 w-4 mr-1 ${loadingFinancialAudit ? 'animate-spin' : ''}`} />
                            Load Financial Audit
                          </Button>
                        </div>

                        {/* Current Metrics Card */}
                        {loadingFinancialAudit ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
                            <span className="ml-2 text-slate-600">Loading financial audit...</span>
                          </div>
                        ) : financialAudit ? (
                          <div className="space-y-6">
                            {/* Current Metrics Summary */}
                            <Card className="border-emerald-200 bg-emerald-50">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-base flex items-center gap-2">
                                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                                  <span className="text-emerald-800">Current Financial Metrics (Live)</span>
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                  <div className="bg-white p-3 rounded-lg border">
                                    <p className="text-xs text-slate-500">Revenue</p>
                                    <p className="text-xl font-bold text-slate-800">${financialAudit.current_metrics?.revenue?.toLocaleString()}</p>
                                  </div>
                                  <div className="bg-white p-3 rounded-lg border">
                                    <p className="text-xs text-slate-500">Gross Revenue</p>
                                    <p className="text-xl font-bold text-emerald-600">${financialAudit.current_metrics?.gross_revenue?.toLocaleString()}</p>
                                  </div>
                                  <div className="bg-white p-3 rounded-lg border">
                                    <p className="text-xs text-slate-500">Tutor Cost</p>
                                    <p className="text-xl font-bold text-orange-600">${financialAudit.current_metrics?.tutor_cost?.toLocaleString()}</p>
                                  </div>
                                  <div className="bg-white p-3 rounded-lg border">
                                    <p className="text-xs text-slate-500">Platform Income</p>
                                    <p className="text-xl font-bold text-blue-600">${financialAudit.current_metrics?.platform_income?.toLocaleString()}</p>
                                  </div>
                                  <div className="bg-white p-3 rounded-lg border">
                                    <p className="text-xs text-slate-500">Hours Owed</p>
                                    <p className="text-xl font-bold text-purple-600">{financialAudit.current_metrics?.hours_owed}</p>
                                  </div>
                                </div>
                                <div className="mt-3 text-xs text-slate-500">
                                  Based on {financialAudit.summary?.total_paid_orders} paid orders, {financialAudit.summary?.total_approved_sessions} approved sessions, {financialAudit.summary?.students_with_hour_balances} students with hour balances
                                </div>
                              </CardContent>
                            </Card>

                            {/* Unified Contributors - Card Layout */}
                            <Card>
                              <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                  <CardTitle className="text-base font-semibold text-slate-800">
                                    All Contributors to Financial Metrics
                                  </CardTitle>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-slate-500">
                                      {financialAudit.contributors?.length || 0} found, {selectedFinancialResetIds.length} selected
                                    </span>
                                    {selectedFinancialResetIds.length > 0 && (
                                      <Button variant="ghost" size="sm" onClick={handleClearFinancialSelection}>
                                        <X className="h-4 w-4 mr-1" /> Clear
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent className="p-4 pt-0">
                                {financialAudit.verification && !financialAudit.verification.gross_revenue_match && (
                                  <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                    ⚠️ Warning: Contributor totals don't match dashboard metrics. Some data may be from non-student sources.
                                  </div>
                                )}
                                
                                {/* Contributors List */}
                                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                                  {(financialAudit.contributors || []).map((contributor) => (
                                    <div 
                                      key={contributor.user_id} 
                                      className={`border rounded-xl p-4 cursor-pointer transition-all ${
                                        selectedFinancialResetIds.includes(contributor.user_id) 
                                          ? 'bg-emerald-50 border-emerald-300 shadow-sm' 
                                          : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                                      }`}
                                      onClick={() => handleFinancialResetSelectionToggle(contributor.user_id)}
                                    >
                                      {/* Header Row */}
                                      <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                          <input
                                            type="checkbox"
                                            checked={selectedFinancialResetIds.includes(contributor.user_id)}
                                            onChange={() => handleFinancialResetSelectionToggle(contributor.user_id)}
                                            className="h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                          />
                                          <div>
                                            <div className="font-semibold text-slate-800">{contributor.name}</div>
                                            <div className="text-sm text-slate-500">{contributor.email}</div>
                                            <div className="text-xs text-slate-400 font-mono mt-0.5">{contributor.user_id}</div>
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <div className="text-xs text-slate-500 uppercase tracking-wide">Data Sources</div>
                                          <div className="text-sm text-slate-700">
                                            {contributor.data_counts.paid_orders} orders · {contributor.data_counts.approved_sessions} sessions
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Metrics Grid */}
                                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                        <div className="bg-slate-50 rounded-lg p-3">
                                          <div className="text-xs text-slate-500 mb-1">Revenue</div>
                                          <div className={`text-lg font-bold ${contributor.metrics_contribution.revenue > 0 ? 'text-slate-800' : 'text-slate-300'}`}>
                                            ${contributor.metrics_contribution.revenue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                          </div>
                                          <div className="text-xs text-slate-400">{contributor.data_counts.paid_orders} paid orders</div>
                                        </div>
                                        
                                        <div className="bg-emerald-50 rounded-lg p-3">
                                          <div className="text-xs text-emerald-600 mb-1">Gross Revenue</div>
                                          <div className={`text-lg font-bold ${contributor.metrics_contribution.gross_revenue > 0 ? 'text-emerald-700' : 'text-slate-300'}`}>
                                            ${contributor.metrics_contribution.gross_revenue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                          </div>
                                          <div className="text-xs text-emerald-500">{contributor.data_counts.approved_sessions} sessions</div>
                                        </div>
                                        
                                        <div className="bg-orange-50 rounded-lg p-3">
                                          <div className="text-xs text-orange-600 mb-1">Tutor Cost</div>
                                          <div className={`text-lg font-bold ${contributor.metrics_contribution.tutor_cost > 0 ? 'text-orange-700' : 'text-slate-300'}`}>
                                            ${contributor.metrics_contribution.tutor_cost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                          </div>
                                          <div className="text-xs text-orange-500">{contributor.data_counts.session_hours} hrs logged</div>
                                        </div>
                                        
                                        <div className="bg-blue-50 rounded-lg p-3">
                                          <div className="text-xs text-blue-600 mb-1">Platform Income</div>
                                          <div className={`text-lg font-bold ${contributor.metrics_contribution.platform_income > 0 ? 'text-blue-700' : 'text-slate-300'}`}>
                                            ${contributor.metrics_contribution.platform_income.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                          </div>
                                          <div className="text-xs text-blue-500">gross - cost</div>
                                        </div>
                                        
                                        <div className="bg-purple-50 rounded-lg p-3">
                                          <div className="text-xs text-purple-600 mb-1">Hours Owed</div>
                                          <div className={`text-lg font-bold ${contributor.metrics_contribution.hours_owed > 0 ? 'text-purple-700' : 'text-slate-300'}`}>
                                            {contributor.metrics_contribution.hours_owed.toLocaleString(undefined, {minimumFractionDigits: 1, maximumFractionDigits: 1})}
                                          </div>
                                          <div className="text-xs text-purple-500">
                                            {contributor.hour_tracking.hours_purchased} bought / {contributor.hour_tracking.hours_used} used
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                
                                {/* Totals Summary - Fixed at bottom */}
                                <div className="mt-4 p-4 bg-slate-800 text-white rounded-xl">
                                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Dashboard Totals (Verification)</div>
                                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                    <div>
                                      <div className="text-xs text-slate-400">Revenue</div>
                                      <div className="text-xl font-bold">${financialAudit.verification?.sum_revenue?.toLocaleString()}</div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-emerald-400">Gross Revenue</div>
                                      <div className="text-xl font-bold text-emerald-400">${financialAudit.verification?.sum_gross_revenue?.toLocaleString()}</div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-orange-400">Tutor Cost</div>
                                      <div className="text-xl font-bold text-orange-400">${financialAudit.verification?.sum_tutor_cost?.toLocaleString()}</div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-blue-400">Platform Income</div>
                                      <div className="text-xl font-bold text-blue-400">${financialAudit.verification?.sum_platform_income?.toLocaleString()}</div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-purple-400">Hours Owed</div>
                                      <div className="text-xl font-bold text-purple-400">{financialAudit.verification?.sum_hours_owed}</div>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            {/* Action Buttons */}
                            {selectedFinancialResetIds.length > 0 && (
                              <div className="flex items-center gap-4 p-4 bg-slate-100 rounded-xl">
                                <div className="flex-1">
                                  <p className="font-medium text-slate-800">
                                    {selectedFinancialResetIds.length} student(s) selected for financial reset
                                  </p>
                                  <p className="text-sm text-slate-500">
                                    This will delete their orders, sessions, and reset hour tracking
                                  </p>
                                </div>
                                <Button
                                  variant="outline"
                                  onClick={handleFinancialDryRun}
                                  disabled={executingFinancialReset}
                                  className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                                  data-testid="financial-dry-run-btn"
                                >
                                  {executingFinancialReset ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  ) : (
                                    <Eye className="h-4 w-4 mr-1" />
                                  )}
                                  Dry Run (Preview Impact)
                                </Button>
                                {financialDryRun?.status === 'dry_run_complete' && (
                                  <Button
                                    variant="destructive"
                                    onClick={handleFinancialResetExecute}
                                    disabled={executingFinancialReset}
                                    className="bg-red-600 hover:bg-red-700"
                                    data-testid="financial-execute-btn"
                                  >
                                    {executingFinancialReset ? (
                                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4 mr-1" />
                                    )}
                                    Execute Financial Reset
                                  </Button>
                                )}
                              </div>
                            )}

                            {/* Dry Run Results */}
                            {financialDryRun && (
                              <Card className={`border-2 ${financialDryRun.status === 'error' ? 'border-red-300 bg-red-50' : 'border-emerald-300 bg-emerald-50'}`}>
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-base flex items-center gap-2">
                                    {financialDryRun.status === 'error' ? (
                                      <>
                                        <XCircle className="h-5 w-5 text-red-500" />
                                        <span className="text-red-700">Dry Run Failed</span>
                                      </>
                                    ) : (
                                      <>
                                        <Eye className="h-5 w-5 text-emerald-600" />
                                        <span className="text-emerald-800">Financial Impact Preview</span>
                                      </>
                                    )}
                                  </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                  {financialDryRun.status === 'error' ? (
                                    <p className="text-red-700">{financialDryRun.message}</p>
                                  ) : (
                                    <>
                                      {/* Before/After Comparison */}
                                      <div className="grid grid-cols-2 gap-6">
                                        <div>
                                          <p className="text-sm font-medium text-slate-600 mb-2">BEFORE Reset</p>
                                          <div className="space-y-1 text-sm">
                                            <div className="flex justify-between"><span>Revenue:</span><span className="font-medium">${financialDryRun.before_metrics?.revenue}</span></div>
                                            <div className="flex justify-between"><span>Gross Revenue:</span><span className="font-medium">${financialDryRun.before_metrics?.gross_revenue}</span></div>
                                            <div className="flex justify-between"><span>Tutor Cost:</span><span className="font-medium">${financialDryRun.before_metrics?.tutor_cost}</span></div>
                                            <div className="flex justify-between"><span>Platform Income:</span><span className="font-medium">${financialDryRun.before_metrics?.platform_income}</span></div>
                                            <div className="flex justify-between"><span>Hours Owed:</span><span className="font-medium">{financialDryRun.before_metrics?.hours_owed}</span></div>
                                          </div>
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium text-emerald-700 mb-2">AFTER Reset (Expected)</p>
                                          <div className="space-y-1 text-sm">
                                            <div className="flex justify-between"><span>Revenue:</span><span className="font-bold text-emerald-700">${financialDryRun.expected_after_metrics?.revenue}</span></div>
                                            <div className="flex justify-between"><span>Gross Revenue:</span><span className="font-bold text-emerald-700">${financialDryRun.expected_after_metrics?.gross_revenue}</span></div>
                                            <div className="flex justify-between"><span>Tutor Cost:</span><span className="font-bold text-emerald-700">${financialDryRun.expected_after_metrics?.tutor_cost}</span></div>
                                            <div className="flex justify-between"><span>Platform Income:</span><span className="font-bold text-emerald-700">${financialDryRun.expected_after_metrics?.platform_income}</span></div>
                                            <div className="flex justify-between"><span>Hours Owed:</span><span className="font-bold text-emerald-700">{financialDryRun.expected_after_metrics?.hours_owed}</span></div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Impact Summary */}
                                      <div className="pt-3 border-t border-emerald-200">
                                        <p className="text-sm font-medium text-amber-700 mb-2">Financial Impact (Will Be Removed)</p>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                          <div className="bg-white px-3 py-2 rounded border text-sm">
                                            <span className="text-slate-600">Revenue:</span>
                                            <span className="ml-1 font-medium text-red-600">-${financialDryRun.financial_impact?.revenue_to_remove}</span>
                                          </div>
                                          <div className="bg-white px-3 py-2 rounded border text-sm">
                                            <span className="text-slate-600">Gross:</span>
                                            <span className="ml-1 font-medium text-red-600">-${financialDryRun.financial_impact?.gross_revenue_to_remove}</span>
                                          </div>
                                          <div className="bg-white px-3 py-2 rounded border text-sm">
                                            <span className="text-slate-600">Tutor Cost:</span>
                                            <span className="ml-1 font-medium text-red-600">-${financialDryRun.financial_impact?.tutor_cost_to_remove}</span>
                                          </div>
                                          <div className="bg-white px-3 py-2 rounded border text-sm">
                                            <span className="text-slate-600">Hours:</span>
                                            <span className="ml-1 font-medium text-red-600">-{financialDryRun.financial_impact?.hours_owed_to_remove}</span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="text-xs text-emerald-700">
                                        Records to delete: {financialDryRun.total_records_to_delete} | Hour tracking will be reset to 0 for {financialDryRun.valid_student_ids?.length} students
                                      </div>
                                    </>
                                  )}
                                </CardContent>
                              </Card>
                            )}

                            {/* Execution Results */}
                            {financialResetResult && (
                              <Card className={`border-2 ${financialResetResult.status === 'execution_complete' ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-base flex items-center gap-2">
                                    {financialResetResult.status === 'execution_complete' ? (
                                      <>
                                        <CheckCircle className="h-5 w-5 text-green-500" />
                                        <span className="text-green-700">Financial Reset Complete</span>
                                      </>
                                    ) : (
                                      <>
                                        <XCircle className="h-5 w-5 text-red-500" />
                                        <span className="text-red-700">Financial Reset Failed</span>
                                      </>
                                    )}
                                  </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                  {financialResetResult.status === 'execution_complete' ? (
                                    <>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-sm text-slate-600">Audit ID: <span className="font-mono font-medium">{financialResetResult.audit_id}</span></p>
                                      </div>
                                      
                                      {/* Final Metrics */}
                                      <div className="grid grid-cols-2 gap-6">
                                        <div>
                                          <p className="text-sm font-medium text-slate-600 mb-2">BEFORE</p>
                                          <div className="space-y-1 text-sm">
                                            <div className="flex justify-between"><span>Revenue:</span><span>${financialResetResult.before_metrics?.revenue}</span></div>
                                            <div className="flex justify-between"><span>Gross Revenue:</span><span>${financialResetResult.before_metrics?.gross_revenue}</span></div>
                                            <div className="flex justify-between"><span>Tutor Cost:</span><span>${financialResetResult.before_metrics?.tutor_cost}</span></div>
                                            <div className="flex justify-between"><span>Platform Income:</span><span>${financialResetResult.before_metrics?.platform_income}</span></div>
                                            <div className="flex justify-between"><span>Hours Owed:</span><span>{financialResetResult.before_metrics?.hours_owed}</span></div>
                                          </div>
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium text-green-700 mb-2">AFTER (Verified)</p>
                                          <div className="space-y-1 text-sm">
                                            <div className="flex justify-between"><span>Revenue:</span><span className="font-bold text-green-700">${financialResetResult.after_metrics?.revenue}</span></div>
                                            <div className="flex justify-between"><span>Gross Revenue:</span><span className="font-bold text-green-700">${financialResetResult.after_metrics?.gross_revenue}</span></div>
                                            <div className="flex justify-between"><span>Tutor Cost:</span><span className="font-bold text-green-700">${financialResetResult.after_metrics?.tutor_cost}</span></div>
                                            <div className="flex justify-between"><span>Platform Income:</span><span className="font-bold text-green-700">${financialResetResult.after_metrics?.platform_income}</span></div>
                                            <div className="flex justify-between"><span>Hours Owed:</span><span className="font-bold text-green-700">{financialResetResult.after_metrics?.hours_owed}</span></div>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="pt-2 border-t border-green-200 text-sm text-green-700">
                                        ✓ {financialResetResult.total_records_deleted} records deleted | ✓ Hour tracking reset for {financialResetResult.hour_tracking_reset?.modified || 0} students | ✓ Dashboard refreshed
                                      </div>
                                    </>
                                  ) : (
                                    <p className="text-red-700">{financialResetResult.message}</p>
                                  )}
                                </CardContent>
                              </Card>
                            )}
                          </div>
                        ) : (
                          <Card className="border-dashed">
                            <CardContent className="py-12 text-center">
                              <DollarSign className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                              <p className="text-slate-500">Click "Load Financial Audit" to view current metrics and select accounts for reset</p>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Website Content CMS Tab */}
                <TabsContent value="content-cms">
                  <CMSContentManager />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Tutor Add/Edit Dialog */}
      <Dialog open={showTutorDialog} onOpenChange={setShowTutorDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTutor ? 'Edit Tutor Profile' : 'Add New Tutor'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={tutorForm.name}
                  onChange={(e) => setTutorForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={tutorForm.email}
                  onChange={(e) => setTutorForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Professional Headline</Label>
              <Input
                value={tutorForm.headline}
                onChange={(e) => setTutorForm(prev => ({ ...prev, headline: e.target.value }))}
                placeholder="e.g., Certified Math Teacher with 10+ Years Experience"
              />
            </div>

            <div className="space-y-2">
              <Label>Profile Photo</Label>
              <div className="flex items-center gap-4">
                <div 
                  className={`relative w-20 h-20 rounded-lg border-2 border-dashed ${
                    tutorPhotoPreview || tutorForm.profile_photo_url ? 'border-indigo-300' : 'border-slate-300'
                  } flex items-center justify-center cursor-pointer hover:border-indigo-400 overflow-hidden`}
                  onClick={() => document.getElementById('admin-tutor-photo').click()}
                >
                  {uploadingTutorPhoto ? (
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                  ) : tutorPhotoPreview || tutorForm.profile_photo_url ? (
                    <img 
                      src={tutorPhotoPreview || tutorForm.profile_photo_url} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="h-8 w-8 text-slate-400" />
                  )}
                </div>
                <input
                  id="admin-tutor-photo"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleTutorPhotoUpload}
                  className="hidden"
                />
                <div className="space-y-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('admin-tutor-photo').click()}
                    disabled={uploadingTutorPhoto}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {tutorForm.profile_photo_url ? 'Change' : 'Upload'}
                  </Button>
                  <p className="text-xs text-slate-500">JPEG, PNG, WebP (max 5MB)</p>
                </div>
              </div>
            </div>

            {/* Subjects */}
            <div className="space-y-2">
              <Label>Subjects *</Label>
              <div className="flex gap-2">
                <Input
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  placeholder="Add subject"
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSubjectToForm())}
                />
                <Button type="button" onClick={addSubjectToForm} disabled={!newSubject.trim()}>
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {tutorForm.subjects.map(subject => (
                  <Badge key={subject} variant="secondary" className="gap-1">
                    {subject}
                    <button
                      type="button"
                      onClick={() => setTutorForm(prev => ({ 
                        ...prev, 
                        subjects: prev.subjects.filter(s => s !== subject) 
                      }))}
                      className="ml-1 hover:text-red-600"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Bio & Qualifications */}
            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea
                value={tutorForm.bio}
                onChange={(e) => setTutorForm(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Short biography..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Qualifications</Label>
              <Textarea
                value={tutorForm.qualifications}
                onChange={(e) => setTutorForm(prev => ({ ...prev, qualifications: e.target.value }))}
                placeholder="Degrees, certifications..."
                rows={2}
              />
            </div>

            {/* Location & Mode */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Tutoring Mode</Label>
                <Select 
                  value={tutorForm.tutoring_mode} 
                  onValueChange={(v) => setTutorForm(prev => ({ ...prev, tutoring_mode: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="in-person">In-Person</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={tutorForm.city}
                  onChange={(e) => setTutorForm(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Toronto, ON"
                />
              </div>
              <div className="space-y-2">
                <Label>Experience (years)</Label>
                <Input
                  type="number"
                  min="0"
                  value={tutorForm.experience_years}
                  onChange={(e) => setTutorForm(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Availability Summary</Label>
              <Input
                value={tutorForm.availability_summary}
                onChange={(e) => setTutorForm(prev => ({ ...prev, availability_summary: e.target.value }))}
                placeholder="e.g., Weekday evenings, Saturday mornings"
              />
            </div>

            {/* Structured Availability */}
            <div className="pt-4 border-t border-stone-200">
              <AvailabilitySelector
                availability={tutorForm.availability || {}}
                onChange={(availability) => setTutorForm(prev => ({ ...prev, availability }))}
              />
            </div>

            {/* Status */}
            <div className="flex items-center gap-6 pt-4 border-t">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={tutorForm.is_approved}
                  onChange={(e) => setTutorForm(prev => ({ ...prev, is_approved: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm">Approved</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={tutorForm.is_visible}
                  onChange={(e) => setTutorForm(prev => ({ ...prev, is_visible: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm">Visible on public page</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTutorDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTutor} disabled={savingTutor}>
              {savingTutor ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                editingTutor ? 'Update Tutor' : 'Create Tutor'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Tutor Profile Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tutor Profile</DialogTitle>
          </DialogHeader>
          
          {viewingTutor && (
            <div className="space-y-6 py-4">
              {/* Header with Photo and Basic Info */}
              <div className="flex items-start gap-6">
                <Avatar className="h-24 w-24 border-2 border-indigo-100">
                  <AvatarImage src={viewingTutor.profile_photo_url} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-2xl">
                    {viewingTutor.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-xl font-semibold text-slate-900">{viewingTutor.name}</h2>
                    {viewingTutor.is_approved ? (
                      viewingTutor.is_visible ? (
                        <Badge className="bg-green-100 text-green-700">Live</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600">Hidden</Badge>
                      )
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700">Pending Approval</Badge>
                    )}
                  </div>
                  {viewingTutor.headline && (
                    <p className="text-slate-600 mb-2">{viewingTutor.headline}</p>
                  )}
                  {viewingTutor.email && (
                    <p className="text-sm text-slate-500">{viewingTutor.email}</p>
                  )}
                </div>
              </div>

              {/* Subjects */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Subjects Taught</h3>
                <div className="flex flex-wrap gap-2">
                  {viewingTutor.subjects?.length > 0 ? (
                    viewingTutor.subjects.map(s => (
                      <Badge key={s} variant="secondary">{s}</Badge>
                    ))
                  ) : (
                    <span className="text-slate-400 text-sm">No subjects listed</span>
                  )}
                </div>
              </div>

              {/* Grade Levels & University Courses */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Grade Levels</h3>
                  <div className="flex flex-wrap gap-1">
                    {viewingTutor.grade_levels?.length > 0 ? (
                      viewingTutor.grade_levels.map(g => (
                        <Badge key={g} variant="outline" className="text-xs">{g}</Badge>
                      ))
                    ) : (
                      <span className="text-slate-400 text-sm">Not specified</span>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">University Courses</h3>
                  <div className="flex flex-wrap gap-1">
                    {viewingTutor.university_courses?.length > 0 ? (
                      viewingTutor.university_courses.map(c => (
                        <Badge key={c} variant="outline" className="text-xs bg-blue-50">{c}</Badge>
                      ))
                    ) : (
                      <span className="text-slate-400 text-sm">Not specified</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Bio</h3>
                <p className="text-slate-600 text-sm whitespace-pre-line">
                  {viewingTutor.bio || 'No bio provided'}
                </p>
              </div>

              {/* Qualifications */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Qualifications</h3>
                <p className="text-slate-600 text-sm whitespace-pre-line">
                  {viewingTutor.qualifications || viewingTutor.educational_background || 'Not specified'}
                </p>
              </div>

              {/* Experience, Mode, Location */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h3 className="text-xs font-semibold text-slate-500 mb-1">Experience</h3>
                  <p className="text-slate-900">{viewingTutor.experience_years || 0} years</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h3 className="text-xs font-semibold text-slate-500 mb-1">Tutoring Mode</h3>
                  <p className="text-slate-900 capitalize">{viewingTutor.tutoring_mode || 'Online'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h3 className="text-xs font-semibold text-slate-500 mb-1">Location</h3>
                  <p className="text-slate-900">{viewingTutor.city || viewingTutor.location || 'Not specified'}</p>
                </div>
              </div>

              {/* Availability */}
              {(viewingTutor.availability_summary || (viewingTutor.availability && Object.keys(viewingTutor.availability).length > 0)) && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Availability</h3>
                  {viewingTutor.availability_summary && (
                    <p className="text-slate-600 text-sm mb-3">{viewingTutor.availability_summary}</p>
                  )}
                  {viewingTutor.availability && Object.keys(viewingTutor.availability).length > 0 && (
                    <div className="bg-stone-50 p-3 rounded-lg">
                      <AvailabilitySelector 
                        availability={viewingTutor.availability} 
                        compact={true}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Languages */}
              {viewingTutor.languages?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Languages</h3>
                  <div className="flex flex-wrap gap-2">
                    {viewingTutor.languages.map(lang => (
                      <Badge key={lang} variant="secondary" className="text-xs">{lang}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Tutor Approved Pay Rates - Admin Only */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-indigo-600" />
                    Approved Pay Rates
                  </h3>
                  {!editingRates && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => setEditingRates(true)}
                      className="text-xs"
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit Rates
                    </Button>
                  )}
                </div>

                {editingRates ? (
                  <div className="space-y-4 bg-slate-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">High School Rate ($/hr)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={rateFormData.approved_rate_high_school}
                          onChange={(e) => setRateFormData(prev => ({ ...prev, approved_rate_high_school: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="e.g., 25.00"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">University Rate ($/hr)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={rateFormData.approved_rate_university}
                          onChange={(e) => setRateFormData(prev => ({ ...prev, approved_rate_university: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="e.g., 30.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Effective Date</label>
                      <input
                        type="date"
                        value={rateFormData.effective_date}
                        onChange={(e) => setRateFormData(prev => ({ ...prev, effective_date: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="text-xs text-slate-500 mt-1">New rates apply to sessions completed on or after this date</p>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => setEditingRates(false)}
                        disabled={savingRates}
                      >
                        Cancel
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={handleSaveTutorRates}
                        disabled={savingRates}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        {savingRates ? 'Saving...' : 'Save Rates'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-600 font-medium">High School</p>
                      <p className="text-lg font-bold text-blue-900">
                        ${tutorRates?.approved_rate_high_school?.toFixed(2) || '0.00'}/hr
                      </p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <p className="text-xs text-purple-600 font-medium">University</p>
                      <p className="text-lg font-bold text-purple-900">
                        ${tutorRates?.approved_rate_university?.toFixed(2) || '0.00'}/hr
                      </p>
                    </div>
                  </div>
                )}
                
                {tutorRates?.rate_effective_date && !editingRates && (
                  <p className="text-xs text-slate-500 mt-2">
                    Current rates effective from: {tutorRates.rate_effective_date}
                  </p>
                )}

                {/* Rate History Summary */}
                {tutorRates?.rate_history?.length > 0 && !editingRates && (
                  <details className="mt-3">
                    <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                      Rate change history ({tutorRates.rate_history.length} changes)
                    </summary>
                    <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                      {tutorRates.rate_history.slice().reverse().map((entry, idx) => (
                        <div key={idx} className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
                          <span className="font-medium">{entry.effective_date}</span>: 
                          {entry.rate_high_school !== undefined && <span className="ml-2">HS: ${entry.rate_high_school}</span>}
                          {entry.rate_university !== undefined && <span className="ml-2">Uni: ${entry.rate_university}</span>}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>

              {/* Status Info */}
              <div className="pt-4 border-t border-slate-200">
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Submission Type: </span>
                    <span className="font-medium">{viewingTutor.submission_type === 'self-submitted' ? 'Self-submitted' : 'Admin created'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Created: </span>
                    <span className="font-medium">
                      {viewingTutor.created_at ? format(new Date(viewingTutor.created_at), 'MMM d, yyyy') : 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-4">
                {!viewingTutor.is_approved && (
                  <Button
                    onClick={() => {
                      handleApproveTutorProfile(viewingTutor.profile_id);
                      setShowViewDialog(false);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowViewDialog(false);
                    handleOpenTutorDialog(viewingTutor);
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Details Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
          </DialogHeader>
          
          {viewingPayment && (
            <div className="space-y-6 py-4">
              {/* Payment Status Header */}
              <div className={`p-4 rounded-xl ${
                viewingPayment.payment?.payment_status === 'paid' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-amber-50 border border-amber-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DollarSign className={`h-8 w-8 ${
                      viewingPayment.payment?.payment_status === 'paid' ? 'text-green-600' : 'text-amber-600'
                    }`} />
                    <div>
                      <p className="text-2xl font-bold text-slate-900">
                        ${viewingPayment.payment?.amount?.toFixed(2)}
                      </p>
                      <p className={`text-sm font-medium ${
                        viewingPayment.payment?.payment_status === 'paid' ? 'text-green-700' : 'text-amber-700'
                      }`}>
                        {viewingPayment.payment?.payment_status?.toUpperCase()}
                      </p>
                    </div>
                  </div>
                  {viewingPayment.payment?.is_under_18 && (
                    <Badge className="bg-amber-100 text-amber-700">
                      Under 18 Purchase
                    </Badge>
                  )}
                </div>
              </div>

              {/* Student Information */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Student Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4 p-4 bg-stone-50 rounded-xl">
                  <div>
                    <p className="text-xs text-slate-500">Full Name</p>
                    <p className="font-medium text-slate-900">{viewingPayment.user?.name || viewingPayment.payment?.user_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium text-slate-900">{viewingPayment.payment?.user_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Age</p>
                    <p className="font-medium text-slate-900">
                      {viewingPayment.payment?.user_age || 'Unknown'}
                      {viewingPayment.payment?.is_under_18 && (
                        <span className="ml-2 text-amber-600 text-sm">(Minor)</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Student Grade</p>
                    <p className="font-medium text-slate-900">{viewingPayment.user?.student_grade || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Parent/Guardian Consent (if under 18) */}
              {viewingPayment.payment?.is_under_18 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Parent/Guardian Consent
                  </h3>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Consent Required</p>
                        <p className="font-medium text-slate-900">
                          {viewingPayment.payment?.parent_consent_required ? 'Yes' : 'No'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Consent Given</p>
                        <p className="font-medium text-slate-900">
                          {viewingPayment.payment?.parent_consent_given ? (
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle className="h-4 w-4" /> Yes
                            </span>
                          ) : 'No'}
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-xs text-slate-500">Parent/Guardian Name</p>
                        <p className="font-medium text-slate-900">
                          {viewingPayment.payment?.parent_guardian_name || viewingPayment.user?.parent_name || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Package Details */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Package Details
                </h3>
                <div className="grid md:grid-cols-2 gap-4 p-4 bg-stone-50 rounded-xl">
                  <div>
                    <p className="text-xs text-slate-500">Package Name</p>
                    <p className="font-medium text-slate-900">{viewingPayment.payment?.package_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Package Type</p>
                    <p className="font-medium text-slate-900">
                      {viewingPayment.payment?.package_type === 'highSchool' ? 'High School' : 'University'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Hours Purchased</p>
                    <p className="font-medium text-slate-900">{viewingPayment.payment?.hours} hours</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Hourly Rate</p>
                    <p className="font-medium text-slate-900">${viewingPayment.payment?.hourly_rate}/hr</p>
                  </div>
                  {viewingPayment.payment?.subjects?.length > 0 && (
                    <div className="md:col-span-2">
                      <p className="text-xs text-slate-500 mb-2">Selected Subjects</p>
                      <div className="flex flex-wrap gap-1">
                        {viewingPayment.payment.subjects.map(s => (
                          <Badge key={s} variant="secondary">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Stripe Payment Details */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Stripe Payment Information
                </h3>
                <div className="p-4 bg-stone-50 rounded-xl space-y-3">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Payment ID</p>
                      <p className="font-mono text-sm text-slate-900">{viewingPayment.payment?.payment_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Stripe Session ID</p>
                      <p className="font-mono text-xs text-slate-600 truncate">{viewingPayment.payment?.stripe_session_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Currency</p>
                      <p className="font-medium text-slate-900">{viewingPayment.payment?.currency?.toUpperCase()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Payment Date</p>
                      <p className="font-medium text-slate-900">
                        {new Date(viewingPayment.payment?.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {viewingPayment.payment?.stripe_receipt_url && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Receipt URL</p>
                      <a 
                        href={viewingPayment.payment.stripe_receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
                      >
                        View Stripe Receipt
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Close Button */}
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Member Add/Edit Dialog */}
      <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTeamMember ? 'Edit Team Member' : 'Add Team Member'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Full Name *</Label>
              <Input
                id="team-name"
                value={teamForm.full_name}
                onChange={(e) => setTeamForm(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="e.g., John Smith"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="team-title">Title / Role *</Label>
              <Input
                id="team-title"
                value={teamForm.title}
                onChange={(e) => setTeamForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Founder & CEO"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="team-bio">Bio / Description</Label>
              <Textarea
                id="team-bio"
                value={teamForm.bio}
                onChange={(e) => setTeamForm(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Brief description about this team member..."
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Profile Image</Label>
              <div className="flex items-center gap-4">
                {teamForm.profile_image ? (
                  <img 
                    src={getTeamImageUrl(teamForm.profile_image)} 
                    alt="Preview"
                    className="w-20 h-20 rounded-full object-cover border-2 border-indigo-100"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
                    <User className="w-10 h-10 text-white" />
                  </div>
                )}
                <div className="flex-1">
                  {editingTeamMember ? (
                    <div>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleTeamPhotoUpload}
                        disabled={uploadingTeamPhoto}
                        className="text-sm"
                      />
                      {uploadingTeamPhoto && (
                        <p className="text-xs text-slate-500 mt-1">
                          <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
                          Uploading...
                        </p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <Input
                        value={teamForm.profile_image}
                        onChange={(e) => setTeamForm(prev => ({ ...prev, profile_image: e.target.value }))}
                        placeholder="Image URL (or upload after saving)"
                        className="text-sm"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Save first to upload an image
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="team-visible" className="font-medium">Visible on Website</Label>
                <p className="text-xs text-slate-500">Show this team member on the About page</p>
              </div>
              <Switch
                id="team-visible"
                checked={teamForm.is_visible}
                onCheckedChange={(checked) => setTeamForm(prev => ({ ...prev, is_visible: checked }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTeamDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTeamMember} 
              disabled={savingTeamMember}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {savingTeamMember ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingTeamMember ? 'Update' : 'Add Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Student Hours Management Modal */}
      <Dialog open={hoursModalOpen} onOpenChange={setHoursModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-indigo-600" />
              Hours Management - {selectedStudentForHours?.name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingHoursData ? (
            <div className="py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
              <p className="text-slate-500 mt-2">Loading hours data...</p>
            </div>
          ) : studentHoursData ? (
            <div className="space-y-6 py-4">
              {/* Hours Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
                  <p className="text-sm text-blue-600 mb-1">Purchased</p>
                  {editingPurchasedHours ? (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        step="0.5"
                        value={newPurchasedHours}
                        onChange={(e) => setNewPurchasedHours(e.target.value)}
                        className="h-8 text-center"
                      />
                      <Button size="sm" onClick={handleUpdatePurchasedHours} className="h-8 px-2">
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingPurchasedHours(false)} className="h-8 px-2">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <p className="text-2xl font-bold text-blue-700">{studentHoursData.total_hours_purchased || 0}</p>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setEditingPurchasedHours(true)}
                        className="h-6 w-6 p-0"
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center border border-amber-100">
                  <p className="text-sm text-amber-600 mb-1">Used</p>
                  <p className="text-2xl font-bold text-amber-700">{studentHoursData.total_hours_used || 0}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center border border-green-100">
                  <p className="text-sm text-green-600 mb-1">Remaining</p>
                  <p className="text-2xl font-bold text-green-700">{studentHoursData.total_hours_remaining || 0}</p>
                </div>
              </div>

              {/* Tutor Link Management */}
              <div className="bg-slate-50 rounded-xl p-4 border">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <LinkIcon className="h-4 w-4" />
                    Tutor Session Submission Link
                  </h4>
                  {studentHoursData.assignment_token && (
                    <Badge className="bg-green-100 text-green-700">
                      Active
                    </Badge>
                  )}
                </div>
                
                {studentHoursData.assignment_token ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={`${window.location.origin}/tutor-submit/${studentHoursData.assignment_token}`}
                      readOnly
                      className="text-sm bg-white"
                    />
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/tutor-submit/${studentHoursData.assignment_token}`);
                        toast.success('Link copied!');
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">
                    Assign a tutor to this student to generate the submission link
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  This is the same link sent to the assigned tutor via email. Share it to log session hours.
                </p>
              </div>

              {/* Session Logs */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Session History ({studentHoursData.session_logs?.length || 0} sessions)
                </h4>
                
                {studentHoursData.session_logs?.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {studentHoursData.session_logs.map((log) => (
                      <div 
                        key={log.log_id}
                        className="bg-white rounded-lg p-3 border"
                        data-testid={`session-log-${log.log_id}`}
                      >
                        {editingSessionLog?.log_id === log.log_id ? (
                          <div className="space-y-3">
                            {/* Edit Form - Tutor Name */}
                            <div>
                              <Label className="text-xs text-slate-500">Tutor Name</Label>
                              <Input
                                value={sessionLogForm.tutor_name}
                                onChange={(e) => setSessionLogForm(prev => ({ ...prev, tutor_name: e.target.value }))}
                                placeholder="Tutor Name"
                                data-testid="edit-tutor-name"
                              />
                            </div>
                            {/* Edit Form - Date & Time */}
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs text-slate-500">Session Date</Label>
                                <Input
                                  type="date"
                                  value={sessionLogForm.session_date}
                                  onChange={(e) => setSessionLogForm(prev => ({ ...prev, session_date: e.target.value }))}
                                  data-testid="edit-session-date"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Session Time</Label>
                                <Input
                                  type="time"
                                  value={sessionLogForm.session_time}
                                  onChange={(e) => setSessionLogForm(prev => ({ ...prev, session_time: e.target.value }))}
                                  data-testid="edit-session-time"
                                />
                              </div>
                            </div>
                            {/* Edit Form - Hours */}
                            <div>
                              <Label className="text-xs text-slate-500">Hours Used</Label>
                              <Input
                                type="number"
                                step="0.25"
                                min="0.25"
                                value={sessionLogForm.hours_used}
                                onChange={(e) => setSessionLogForm(prev => ({ ...prev, hours_used: e.target.value }))}
                                placeholder="Hours"
                                data-testid="edit-hours-used"
                              />
                            </div>
                            {/* Edit Form - Notes */}
                            <div>
                              <Label className="text-xs text-slate-500">Notes</Label>
                              <Input
                                value={sessionLogForm.notes}
                                onChange={(e) => setSessionLogForm(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Session notes (optional)"
                                data-testid="edit-notes"
                              />
                            </div>
                            <div className="flex gap-2 pt-1">
                              <Button size="sm" onClick={handleSaveSessionLog} data-testid="save-session-btn">
                                Save Changes
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingSessionLog(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {/* Session Header Row */}
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-semibold text-slate-900">{log.session_date}</span>
                                {log.session_time && (
                                  <span className="text-slate-600 text-sm">at {log.session_time}</span>
                                )}
                                <Badge className="bg-indigo-100 text-indigo-700">
                                  {log.hours_used} hrs
                                </Badge>
                                {log.source === 'tutor_link' && (
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">
                                    Via Tutor Link
                                  </Badge>
                                )}
                                {log.edited_by_admin && (
                                  <Badge className="bg-amber-100 text-amber-700 text-xs">
                                    Edited
                                  </Badge>
                                )}
                              </div>
                              {/* Tutor Name */}
                              {log.tutor_name && (
                                <p className="text-sm text-slate-700">
                                  <span className="text-slate-500">Tutor:</span> {log.tutor_name}
                                </p>
                              )}
                              {/* Notes */}
                              {log.notes && (
                                <p className="text-sm text-slate-600 mt-1">{log.notes}</p>
                              )}
                              {/* Created timestamp */}
                              <p className="text-xs text-slate-400 mt-1">
                                Logged: {format(new Date(log.created_at), 'MMM d, yyyy h:mm a')}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditSessionLog(log)}
                                className="h-8 w-8 p-0"
                                title="Edit session"
                                data-testid={`edit-session-${log.log_id}`}
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSessionLog(log.log_id)}
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                title="Delete session"
                                data-testid={`delete-session-${log.log_id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                    <Clock className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No session logs yet</p>
                  </div>
                )}
              </div>
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoursModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tutor Review Back Office Modal */}
      <Dialog open={showTutorReviewModal} onOpenChange={setShowTutorReviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-indigo-600" />
              Tutor Review - {selectedTutorForReview?.name || selectedTutorForReview?.full_name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingTutorReview ? (
            <div className="py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
              <p className="text-slate-500 mt-2">Loading tutor data...</p>
            </div>
          ) : tutorReviewData ? (
            <div className="space-y-6 py-4">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg flex items-center justify-between ${
                tutorReviewData.is_approved ? 'bg-green-50 border border-green-200' :
                tutorReviewData.current_status === 'rejected' ? 'bg-red-50 border border-red-200' :
                'bg-amber-50 border border-amber-200'
              }`}>
                <div className="flex items-center gap-3">
                  <Badge className={
                    tutorReviewData.is_approved ? 'bg-green-100 text-green-700' :
                    tutorReviewData.current_status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }>
                    {tutorReviewData.current_status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                  </Badge>
                  <span className="text-sm text-slate-600">
                    Current onboarding status
                  </span>
                </div>
                <div className="flex gap-2">
                  {!tutorReviewData.is_approved && (
                    <Button 
                      size="sm" 
                      onClick={() => handleUpdateTutorStatus('approved')}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                  )}
                  {tutorReviewData.current_status !== 'rejected' && (
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleUpdateTutorStatus('rejected')}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  )}
                </div>
              </div>

              {/* Profile Section */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Profile Details
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Full Name:</span>
                    <p className="font-medium">{tutorReviewData.profile?.name || tutorReviewData.user?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Email:</span>
                    <p className="font-medium">{tutorReviewData.profile?.email || tutorReviewData.user?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Headline:</span>
                    <p className="font-medium">{tutorReviewData.profile?.headline || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Location:</span>
                    <p className="font-medium">{tutorReviewData.profile?.location || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Subjects:</span>
                    <p className="font-medium">{tutorReviewData.profile?.subjects?.join(', ') || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Grade Levels:</span>
                    <p className="font-medium">{tutorReviewData.profile?.grade_levels?.join(', ') || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Bio:</span>
                    <p className="font-medium">{tutorReviewData.profile?.bio || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Qualifications:</span>
                    <p className="font-medium">{tutorReviewData.profile?.qualifications || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Experience:</span>
                    <p className="font-medium">{tutorReviewData.profile?.experience_years || 0} years</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Submitted:</span>
                    <p className="font-medium">
                      {tutorReviewData.profile?.created_at ? 
                        new Date(tutorReviewData.profile.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Agreement Section */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Agreement Status
                </h3>
                {tutorReviewData.agreement?.signed ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Signed
                      </Badge>
                      <span className="text-sm text-slate-600">
                        Version {tutorReviewData.agreement.data?.agreement_version}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Signed Date:</span>
                        <p className="font-medium">
                          {tutorReviewData.agreement.data?.signed_at ? 
                            new Date(tutorReviewData.agreement.data.signed_at).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Digital Signature:</span>
                        <p className="font-medium italic font-cursive">
                          {tutorReviewData.agreement.data?.typed_signature || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm text-indigo-600 hover:text-indigo-800">
                        View Full Agreement Text
                      </summary>
                      <div className="mt-2 p-3 bg-white rounded border text-xs max-h-[200px] overflow-y-auto">
                        {tutorReviewData.agreement.data?.agreement_text_snapshot && 
                          Object.entries(tutorReviewData.agreement.data.agreement_text_snapshot).map(([key, section]) => (
                            <div key={key} className="mb-3">
                              <h4 className="font-semibold text-slate-800">{section.title}</h4>
                              <p className="text-slate-600 whitespace-pre-wrap">{section.content}</p>
                            </div>
                          ))
                        }
                      </div>
                    </details>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-orange-100 text-orange-700">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Not Signed
                    </Badge>
                    <span className="text-sm text-slate-600">Tutor has not signed the agreement yet</span>
                  </div>
                )}
              </div>

              {/* Documents Section */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Verification Documents
                  <Badge className={tutorReviewData.documents?.all_required_approved ? 
                    'bg-green-100 text-green-700' : 
                    tutorReviewData.documents?.all_required_uploaded ? 
                    'bg-blue-100 text-blue-700' : 
                    'bg-orange-100 text-orange-700'
                  }>
                    {tutorReviewData.documents?.all_required_approved ? 'All Verified' :
                     tutorReviewData.documents?.all_required_uploaded ? 'Pending Review' :
                     `${tutorReviewData.documents?.uploaded?.length || 0}/3 Uploaded`}
                  </Badge>
                </h3>
                
                <div className="space-y-3">
                  {['government_id', 'proof_of_education', 'police_check', 'certification', 'resume'].map(docType => {
                    const doc = tutorReviewData.documents?.list?.find(d => d.document_type === docType);
                    const isRequired = ['government_id', 'proof_of_education', 'police_check'].includes(docType);
                    const docLabels = {
                      government_id: 'Government ID',
                      proof_of_education: 'Proof of Education',
                      police_check: 'Police Background Check',
                      certification: 'Certifications',
                      resume: 'Resume/CV'
                    };
                    
                    return (
                      <div key={docType} className="flex items-center justify-between p-3 bg-white rounded border">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded flex items-center justify-center ${
                            doc?.status === 'approved' ? 'bg-green-100' :
                            doc?.status === 'rejected' ? 'bg-red-100' :
                            doc ? 'bg-blue-100' : 'bg-slate-100'
                          }`}>
                            {doc?.status === 'approved' ? <CheckCircle className="h-5 w-5 text-green-600" /> :
                             doc?.status === 'rejected' ? <XCircle className="h-5 w-5 text-red-600" /> :
                             doc ? <Clock className="h-5 w-5 text-blue-600" /> :
                             <AlertCircle className="h-5 w-5 text-slate-400" />}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 flex items-center gap-2">
                              {docLabels[docType]}
                              {isRequired && <Badge variant="outline" className="text-xs">Required</Badge>}
                            </p>
                            {doc ? (
                              <p className="text-xs text-slate-500">
                                {doc.file_name} • Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                              </p>
                            ) : (
                              <p className="text-xs text-slate-500">Not uploaded</p>
                            )}
                            {doc?.admin_notes && (
                              <p className="text-xs text-amber-600 mt-1">Note: {doc.admin_notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc && (
                            <>
                              <a 
                                href={doc.file_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-800 text-sm"
                              >
                                View
                              </a>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleReviewDocument(doc)}
                              >
                                Review
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Document Review Inline Form */}
                {reviewingDocument && (
                  <div className="mt-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                    <h4 className="font-medium text-indigo-900 mb-3">
                      Reviewing: {reviewingDocument.file_name}
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm">Status</Label>
                        <Select value={documentReviewStatus} onValueChange={setDocumentReviewStatus}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm">Admin Notes</Label>
                        <Textarea 
                          value={documentReviewNotes}
                          onChange={(e) => setDocumentReviewNotes(e.target.value)}
                          placeholder="Add notes about this document..."
                          rows={2}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSubmitDocumentReview}>
                          Save Review
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setReviewingDocument(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Admin Notes Section */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Internal Notes ({tutorReviewData.admin_notes?.length || 0})
                </h3>
                
                {/* Add Note Form */}
                <div className="mb-4 p-3 bg-white rounded border">
                  <div className="flex gap-2 mb-2">
                    <Select value={adminNoteType} onValueChange={setAdminNoteType}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="important">Important</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="investigation">Investigation</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input 
                      value={newAdminNote}
                      onChange={(e) => setNewAdminNote(e.target.value)}
                      placeholder="Add an internal note..."
                      className="flex-1"
                    />
                    <Button onClick={handleAddAdminNote} disabled={!newAdminNote.trim()}>
                      Add
                    </Button>
                  </div>
                </div>

                {/* Notes List */}
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {tutorReviewData.admin_notes?.length > 0 ? (
                    tutorReviewData.admin_notes.map(note => (
                      <div key={note.note_id} className={`p-3 rounded border ${
                        note.type === 'warning' ? 'bg-amber-50 border-amber-200' :
                        note.type === 'important' ? 'bg-red-50 border-red-200' :
                        note.type === 'investigation' ? 'bg-purple-50 border-purple-200' :
                        'bg-white'
                      }`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs capitalize">{note.type}</Badge>
                              <span className="text-xs text-slate-500">
                                {note.created_by_name} • {new Date(note.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-slate-800">{note.note}</p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteAdminNote(note.note_id)}
                            className="h-6 w-6 p-0 text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">No internal notes yet</p>
                  )}
                </div>
              </div>

              {/* Status History */}
              {tutorReviewData.status_history?.length > 0 && (
                <details className="bg-slate-50 rounded-lg p-4">
                  <summary className="cursor-pointer font-semibold text-slate-900 flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Status History ({tutorReviewData.status_history.length})
                  </summary>
                  <div className="mt-3 space-y-2">
                    {tutorReviewData.status_history.map((item, idx) => (
                      <div key={idx} className="text-sm flex items-center gap-2">
                        <span className="text-slate-500">{new Date(item.changed_at).toLocaleString()}</span>
                        <Badge variant="outline">{item.new_status}</Badge>
                        {item.notes && <span className="text-slate-600">- {item.notes}</span>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTutorReviewModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tutor Assignment Modal */}
      <Dialog open={showAssignTutorModal} onOpenChange={setShowAssignTutorModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-purple-600" />
              {studentForTutorAssignment?.assigned_tutor_id ? 'Change' : 'Assign'} Tutor
            </DialogTitle>
          </DialogHeader>
          
          {studentForTutorAssignment && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">Assigning tutor to:</p>
                <p className="font-semibold text-slate-900">{studentForTutorAssignment.name}</p>
                <p className="text-xs text-slate-500">{studentForTutorAssignment.email}</p>
                {studentForTutorAssignment.age < 18 && (
                  <Badge className="mt-2 bg-amber-100 text-amber-700">Under 18 - Parent will be notified</Badge>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Tutor
                </label>
                <select
                  value={selectedTutorForAssignment}
                  onChange={(e) => setSelectedTutorForAssignment(e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="tutor-select-dropdown"
                >
                  <option value="">-- Select a tutor --</option>
                  {approvedTutors.map(tutor => (
                    <option key={tutor.profile_id} value={tutor.profile_id}>
                      {tutor.name || tutor.full_name} - {tutor.headline || 'Tutor'}
                    </option>
                  ))}
                </select>
                {approvedTutors.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No approved tutors available</p>
                )}
              </div>
              
              {selectedTutorForAssignment && (
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-purple-800">Confirmation</p>
                  <p className="text-xs text-purple-600 mt-1">
                    After assignment, notification emails will be sent to:
                  </p>
                  <ul className="text-xs text-purple-700 mt-2 space-y-1">
                    <li>• Student ({studentForTutorAssignment.email})</li>
                    <li>• Tutor ({approvedTutors.find(t => t.profile_id === selectedTutorForAssignment)?.email})</li>
                    {studentForTutorAssignment.age < 18 && (
                      <li>• Parent/Guardian (from agreement)</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowAssignTutorModal(false);
                setStudentForTutorAssignment(null);
                setSelectedTutorForAssignment('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignTutor}
              disabled={!selectedTutorForAssignment || assigningTutor}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-assign-tutor-btn"
            >
              {assigningTutor ? 'Assigning...' : 'Assign Tutor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Tutor Unassignment Confirmation Modal */}
      <AlertDialog open={showUnassignConfirmModal} onOpenChange={setShowUnassignConfirmModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Confirm Tutor Unassignment
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              {studentForUnassignment && (
                <div className="space-y-3 text-left">
                  <p>
                    Are you sure you want to remove <strong>{studentForUnassignment.assigned_tutor_name}</strong> as the tutor for <strong>{studentForUnassignment.name}</strong>?
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                    <p className="font-medium text-amber-800 text-sm">Notification emails will be sent to:</p>
                    <ul className="text-sm text-amber-700 mt-2 space-y-1">
                      <li>• Student ({studentForUnassignment.email})</li>
                      <li>• Tutor ({studentForUnassignment.assigned_tutor_name})</li>
                      {studentForUnassignment.age < 18 && (
                        <li>• Parent/Guardian (from agreement)</li>
                      )}
                    </ul>
                  </div>
                  <p className="text-xs text-slate-500">
                    This action will end the tutoring assignment immediately.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowUnassignConfirmModal(false);
                setStudentForUnassignment(null);
              }}
              disabled={unassigningTutor}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmUnassignTutor}
              disabled={unassigningTutor}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="confirm-unassign-tutor-btn"
            >
              {unassigningTutor ? 'Unassigning...' : 'Yes, Unassign Tutor'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive User Confirmation Modal */}
      <AlertDialog open={showArchiveConfirmModal} onOpenChange={setShowArchiveConfirmModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <Archive className="h-5 w-5" />
              Archive User
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              {userToArchive && (
                <div className="space-y-3 text-left">
                  <p>
                    Are you sure you want to archive <strong>{userToArchive.name || userToArchive.email}</strong>?
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                    <p className="font-medium text-blue-800 text-sm">What happens when you archive:</p>
                    <ul className="text-sm text-blue-700 mt-2 space-y-1">
                      <li>• User will not be able to log in</li>
                      <li>• User will be hidden from active lists</li>
                      <li>• All records and history are preserved</li>
                      <li>• You can restore this user at any time</li>
                    </ul>
                  </div>
                  <p className="text-xs text-green-600 font-medium">
                    This action does NOT delete any data. It's fully reversible.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowArchiveConfirmModal(false);
                setUserToArchive(null);
              }}
              disabled={archivingUser}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmArchiveUser}
              disabled={archivingUser}
              className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
              data-testid="confirm-archive-user-btn"
            >
              {archivingUser ? 'Archiving...' : 'Yes, Archive User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
